import { UseCaseData } from "@/types/global";

// ============================================================================
// STARTUP ICP - Early stage startups
// ============================================================================
const startupUseCases: UseCaseData[] = [
    {
        slug: "saas-mvp",
        title: "SaaS MVP",
        description: "Ship a lean core with auth, billing, and analytics so you can test real demand and keep the same foundation as you scale.",
        hero: {
            label: "USE CASE",
            title: "SaaS MVP",
            description: "Ship a lean core with auth, billing, and analytics so you can test real demand and keep the same foundation as you scale.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the MVP Checklist",
                    modalTitle: "Get the MVP Checklist"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Startups building creator-focused products that need signal quickly and a path to scale without a rebuild."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "The essential building blocks that get real users on a working product fast.",
            items: [
                {
                    id: 1,
                    title: "Auth and accounts",
                    description: "email, social sign-in, SSO options with secure sessions",
                },
                {
                    id: 2,
                    title: "Billing and entitlements",
                    description: "plans, trials, coupons, usage meters, and permissions",
                },
                {
                    id: 3,
                    title: "Core feature loop",
                    description: "the smallest slice that delivers first value fast",
                },
                {
                    id: 4,
                    title: "Onboarding and activation",
                    description: "guided setup, checklists, and nudges to reduce time to first value",
                },
                {
                    id: 5,
                    title: "Event map and analytics",
                    description: "funnels, cohorts, and dashboards for decisions, not guesses",
                },
                {
                    id: 6,
                    title: "Integration rails",
                    description: "payments, email, CRM, and webhooks that grow with you",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The strategic wins that turn an MVP into a fundable, scalable path.",
            items: [
                {
                    id: 1,
                    title: "Speed with confidence",
                    description: "learn from real users in weeks, not quarters",
                },
                {
                    id: 2,
                    title: "No throwaway code",
                    description: "the MVP hardens into the product you scale",
                },
                {
                    id: 3,
                    title: "Clear proof",
                    description: "activation, conversion, and retention visible by cohort",
                },
                {
                    id: 4,
                    title: "Fundable story",
                    description: "a crisp path from idea to revenue on a modular stack",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "The focused steps from scope to launch, designed to avoid rework.",
            steps: [
                {
                    title: "Discovery sprint",
                    description: "align ICP, promise, success metrics; scope to one value loop"
                },
                {
                    title: "Prototype and quick tests",
                    description: "clickable flows with target users; fix friction early"
                },
                {
                    title: "Build the core",
                    description: "auth, billing, design system, and the core features"
                },
                {
                    title: "Wire the signals",
                    description: "event taxonomy, funnels, cohort dashboards before launch"
                },
                {
                    title: "Launch and iterate",
                    description: "weekly improvements, pricing tests, and integrations"
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "The signals that prove demand and guide the next release.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Time to first value down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Activation and paid conversion up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Retention visible by cohort"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "CAC payback your leadership can defend"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "How we architect, code, and instrument so the MVP hardens as you grow.",
            items: [
                {
                    title: "Frontend",
                    description: "modern framework with shared components and accessibility checks",
                },
                {
                    title: "Backend",
                    description: "services with clear contracts, background queues, and retries",
                },
                {
                    title: "Payments",
                    description: "subscriptions, proration, usage, coupons with clean entitlements",
                },
                {
                    title: "Data",
                    description: "event map to analytics; simple cohort and funnel dashboards",
                },
                {
                    title: "Ops",
                    description: "CI and CD, environments, logs, and alerts for calm releases",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Optional modules that accelerate learning and revenue without bloating v1.",
            items: [
                {
                    title: "Pricing lab",
                    description: "plans, trials, meters, and experiments",
                },
                {
                    title: "AI onboarding copilot",
                    description: "contextual help that reduces support load",
                },
                {
                    title: "Referral engine",
                    description: "programs that bring qualified users for less",
                },
                {
                    title: "Community starter",
                    description: "owned space that turns users into advocates",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls that keep data safe and reviews smooth from day one.",
            tags: [
                "Role-based access",
                "audit trails",
                "environment separation",
                "SSO and SCIM options",
                "Documented data handling aligned to common reviews"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Will this scale or be rebuilt later?",
                    answer: "It is built on a modular stack so it hardens into the product you scale."
                },
                {
                    id: 2,
                    question: "Do we include AI in version one?",
                    answer: "Only where it moves KPIs; a common starter is an onboarding assistant."
                },
                {
                    id: 3,
                    question: "Who owns the code and assets?",
                    answer: "You do. Repositories, design files, and documentation are handed over."
                },
                {
                    id: 4,
                    question: "How long to first launch?",
                    answer: "A short discovery followed by a few weeks to first revenue, depending on scope."
                },
                {
                    id: 5,
                    question: "Can we bring our own tools?",
                    answer: "Yes. We integrate with common providers for payments, email, CRM, and analytics."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the MVP Checklist",
                    modalTitle: "Get the MVP Checklist"
                }
            }
        },
        seo: {
            title: "SaaS MVP Development | Build & Launch Your MVP Fast | Desilo",
            description: "Ship a lean SaaS MVP with auth, billing, and analytics. Get to market fast with a scalable foundation. Book a strategy call today.",
            keywords: "SaaS MVP, startup development, minimum viable product, SaaS launch, startup MVP",
            ogTitle: "SaaS MVP Development | Build & Launch Your MVP Fast | Desilo",
            ogDescription: "Ship a lean SaaS MVP with auth, billing, and analytics. Get to market fast with a scalable foundation. Book a strategy call today.",
            twitterTitle: "SaaS MVP Development | Build & Launch Your MVP Fast | Desilo",
            twitterDescription: "Ship a lean SaaS MVP with auth, billing, and analytics. Get to market fast with a scalable foundation. Book a strategy call today."
        }
    },
    {
        slug: "creator-crm",
        title: "Creator CRM",
        description: "Track creators, campaigns, and revenue in one place so outreach, follow ups, and forecasting become repeatable.",
        hero: {
            label: "USE CASE",
            title: "Creator CRM",
            description: "Track creators, campaigns, and revenue in one place so outreach, follow ups, and forecasting become repeatable.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the 5 Minute Growth Audit",
                    modalTitle: "Get the 5 Minute Growth Audit"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Agencies and creator managers who need clean pipelines and reliable delivery. Startups building creator platforms that require deal workflows, payouts, and reporting brands trust."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "The practical pieces we ship so new users reach first value fast.",
            items: [
                {
                    id: 1,
                    title: "Pipelines and stages",
                    description: "outreach, brief review, negotiation, booked, live, delivered, paid",
                },
                {
                    id: 2,
                    title: "Creator and brand profiles",
                    description: "contacts, rates, niches, performance, contracts, notes",
                },
                {
                    id: 3,
                    title: "Briefs and rate cards",
                    description: "structured deliverables, usage terms, rate intelligence",
                },
                {
                    id: 4,
                    title: "Contracts and approvals",
                    description: "templates, e-sign, audit trails, change logs",
                },
                {
                    id: 5,
                    title: "Calendars and deliverables",
                    description: "slots, deadlines, assets, status, reminders",
                },
                {
                    id: 6,
                    title: "Payouts and invoices",
                    description: "splits, statuses, reconciliations, invoice aging",
                },
                {
                    id: 7,
                    title: "Attribution and reporting",
                    description: "campaign results, revenue by creator, ROAS views",
                },
                {
                    id: 8,
                    title: "Client portals",
                    description: "live dashboards so clients stop chasing updates",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when onboarding works.",
            items: [
                {
                    id: 1,
                    title: "Less chaos",
                    description: "one source of truth beats spreadsheets and DMs",
                },
                {
                    id: 2,
                    title: "Higher win rate",
                    description: "faster response, clearer pricing, fewer errors",
                },
                {
                    id: 3,
                    title: "More capacity",
                    description: "one manager handles more creators without burnout",
                },
                {
                    id: 4,
                    title: "Trusted results",
                    description: "branded reports that make renewals easier",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "The simple, repeatable process we follow from definition to launch.",
            steps: [
                {
                    title: "Discovery sprint",
                    description: "Map your stages, SLAs, and data model for creators, brands, and campaigns."
                },
                {
                    title: "System design",
                    description: "Define objects, permissions, automations, and integrations you already use."
                },
                {
                    title: "Build and connect",
                    description: "Pipelines, contracts, calendars, payouts, attribution, and client portals."
                },
                {
                    title: "Migrate and train",
                    description: "Import key data, set roles, and run a pilot on a real account."
                },
                {
                    title: "Operate and improve",
                    description: "Weekly tweaks, reporting views, and automation that removes manual steps."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "The metrics that show progress week to week and cohort to cohort.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Win rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Cycle time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Creators per manager up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Invoice aging down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Client renewals and margin up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "How we design, build, and instrument the system so it scales cleanly.",
            items: [
                {
                    title: "Frontend",
                    description: "manager-first UI with fast tables, filters, and bulk actions",
                },
                {
                    title: "Backend",
                    description: "services for deals, contracts, payouts, assets, and audit logs",
                },
                {
                    title: "Integrations",
                    description: "email and calendar, e-sign, storage, payments, CRM, accounting",
                },
                {
                    title: "Data",
                    description: "event map for pipeline health, revenue, and delivery status",
                },
                {
                    title: "Ops",
                    description: "role-based access, environments, logs, and alerts",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Features that raise win rate, speed delivery, and deepen client trust.",
            items: [
                {
                    title: "AI outreach assistant",
                    description: "draft emails, follow ups, and summaries with your tone",
                },
                {
                    title: "AI chatter and support",
                    description: "triage comments and FAQs to reduce back and forth",
                },
                {
                    title: "Rate card intelligence",
                    description: "price guidance from past wins and performance",
                },
                {
                    title: "Rights manager",
                    description: "usage terms, expiries, and reminders in one place",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Permissions, logs, and data handling that satisfy brand and enterprise standards.",
            tags: ["Role based access",
                "approval logs",
                "encrypted storage",
                "PII controls",
                "clear audit trails",
                "SSO and SCIM options for larger teams"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Will this replace our current CRM or sit alongside it?",
                    answer: "Either. We can integrate with your CRM or run a focused Creator CRM for ops and reporting."
                },
                {
                    id: 2,
                    question: "Can we standardize briefs and contracts for brands?",
                    answer: "Yes. Templates, required fields, and e-sign make compliance simple."
                },
                {
                    id: 3,
                    question: "How do payouts and splits work?",
                    answer: "Track splits per campaign, generate invoices, and reconcile payments with clear statuses."
                },
                {
                    id: 4,
                    question: "Can clients see progress without emails?",
                    answer: "Client portals show timelines, deliverables, performance, and exports."
                },
                {
                    id: 5,
                    question: "How fast is rollout?",
                    answer: "A short discovery, then a few weeks to a live pilot with one team. Scale from there."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the 5 Minute Growth Audit",
                    modalTitle: "Get the 5 Minute Growth Audit"
                }
            }
        },
        seo: {
            title: "Creator CRM for deals, delivery, and revenue | Desilo",
            description: "Centralize creator pipelines, briefs, contracts, calendars, payouts, and reporting. Win more, deliver faster, and forecast with confidence.",
            keywords: "Creator CRM, creator management, campaign tracking, creator platforms, creator tools, influencer CRM",
            ogTitle: "Creator CRM for deals, delivery, and revenue | Desilo",
            ogDescription: "Centralize creator pipelines, briefs, contracts, calendars, payouts, and reporting. Win more, deliver faster, and forecast with confidence.",
            twitterTitle: "Creator CRM for deals, delivery, and revenue | Desilo",
            twitterDescription: "Centralize creator pipelines, briefs, contracts, calendars, payouts, and reporting. Win more, deliver faster, and forecast with confidence."
        }
    },
    {
        slug: "community-os",
        title: "Community OS",
        description: "Host gated spaces, cohorts, events, and courses in one hub so fans stick, churn drops, and LTV climbs.",
        hero: {
            label: "USE CASE",
            title: "Community OS",
            description: "Host gated spaces, cohorts, events, and courses in one hub so fans stick, churn drops, and LTV climbs.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Community Playbook",
                    modalTitle: "Get the Community Playbook"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "For teams moving from rented audiences to an owned, durable community."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "The building blocks for a sticky, tiered community that pays for itself.",
            items: [
                {
                    id: 1,
                    title: "Gated spaces",
                    description: "Channels, topics, and roles with tiered access so members see the right things at the right time.",
                },
                {
                    id: 2,
                    title: "Cohorts and programs",
                    description: "Limited-run groups with start and end dates, progress tracking, and outcomes that create momentum.",
                },
                {
                    id: 3,
                    title: "Events and live sessions",
                    description: "Calendars, registrations, reminders, recordings, and replays so live value is easy to deliver and revisit.",
                },
                {
                    id: 4,
                    title: "Courses and challenges",
                    description: "Modules, lessons, drip schedules, quizzes, and completions that turn knowledge into habits.",
                },
                {
                    id: 5,
                    title: "Perks and rewards",
                    description: "Badges, streaks, and member-only drops by tier so loyalty feels recognized and renewed.",
                },
                {
                    id: 6,
                    title: "Moderation and safety",
                    description: "Reports, mutes, role permissions, and content guidelines that keep spaces helpful and safe.",
                },
                {
                    id: 7,
                    title: "Member profiles",
                    description: "Interests, activity, achievements, and recommendations that power relevant connections.",
                },
                {
                    id: 8,
                    title: "Analytics",
                    description: "Engagement, retention, cohort health, and revenue by tier so you know what to double down on.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "Belonging beats feeds; clear value beats churn.",
            items: [
                {
                    id: 1,
                    title: "Belonging beats feeds",
                    description: "owned spaces build habit and trust",
                },
                {
                    id: 2,
                    title: "Lower churn",
                    description: "members renew when value is obvious and nearby",
                },
                {
                    id: 3,
                    title: "More value per member",
                    description: "programs and perks lift ARPU and LTV",
                },
                {
                    id: 4,
                    title: "Resilience",
                    description: "less dependence on platform algorithms",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A simple playbook from blueprint to weekly improvements.",
            steps: [
                {
                    title: "Community blueprint",
                    description: "Define purpose, tiers, spaces, and a first 90-day program that sets expectations and rhythm."
                },
                {
                    title: "Access and structure",
                    description: "Map roles, gates, and content types to tiers so value is obvious from day one."
                },
                {
                    title: "Programming and cadence",
                    description: "Plan events, cohorts, and challenges that anchor weekly engagement and highlight wins."
                },
                {
                    title: "Perks and delivery",
                    description: "Automate drops, rewards, and member communications so perks arrive on time without manual effort."
                },
                {
                    title: "Measure and improve",
                    description: "Track participation, renewals, and sentiment and ship weekly tweaks that remove friction."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Engagement, retention, and LTV you can actually see.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Member activation up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Weekly active members up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Renewal rate up and churn down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "ARPU and LTV up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Access-related tickets down"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Fast UI, reliable permissions, clean data, and calm ops.",
            items: [
                {
                    title: "Frontend",
                    description: "Fast channels, course players, and event views with accessible UI patterns",
                },
                {
                    title: "Backend",
                    description: "Roles, permissions, entitlements, and audit logs that stand up to reviews",
                },
                {
                    title: "Content",
                    description: "Uploads, streaming, transcripts, and safe storage with sensible limits",
                },
                {
                    title: "Payments",
                    description: "Tiered subscriptions with upgrades, downgrades, and proration that feel fair",
                },
                {
                    title: "Data",
                    description: "Event map for engagement, completions, renewals, and cohort health",
                },
                {
                    title: "Integrations",
                    description: "Email or CRM for lifecycle, calendar for reminders, and chat sync where useful",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Programs and tools that keep members active and increase lifetime value.",
            items: [
                {
                    title: "Community gates for Discord or Slack",
                    description: "Sync roles to tiers so access follows membership automatically.",
                },
                {
                    title: "Member directory",
                    description: "Search by skills or interests so collaboration and discovery come naturally.",
                },
                {
                    title: "Mentor or office hours booking",
                    description: "Time slots tied to plans so higher tiers get real access.",
                },
                {
                    title: "Referral credits",
                    description: "Give and get rewards that turn happy members into sustainable growth.",
                },
                {
                    title: "AI community assistant",
                    description: "Summaries, highlights, and gentle nudges that keep conversations useful.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Enterprise-ready controls without community friction.",
            tags: [
                "Role-based access",
                "clear moderation tools",
                "audit trails",
                "consent and privacy controls",
                "safe handling of member data"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Can we migrate from a free group or hosted platform?",
                    answer: "Yes. We import members, map tiers, and redirect common paths so people land in the right place."
                },
                {
                    id: 2,
                    question: "How do we prevent spam and low-quality posts?",
                    answer: "Clear rules, role-gated posting, rate limits, and helpful prompts keep quality high."
                },
                {
                    id: 3,
                    question: "Will courses and events work on mobile?",
                    answer: "Yes. Responsive layouts and offline-friendly patterns where possible."
                },
                {
                    id: 4,
                    question: "How do perks get delivered?",
                    answer: "Perks trigger on payment or milestone events and appear in the member hub."
                },
                {
                    id: 5,
                    question: "Can we keep our existing chat or forum?",
                    answer: "Yes. We can sync access with roles and surface the best threads in the hub."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Community Playbook",
                    modalTitle: "Get the Community Playbook"
                }
            }
        },
        seo: {
            title: "Community OS for retention and LTV | Desilo",
            description: "Host gated spaces, cohorts, events, and courses in one hub. Increase engagement, reduce churn, and grow LTV with clear structure and clean UX.",
            keywords: "Community OS, community platform, gated community, community management, member retention, community software",
            ogTitle: "Community OS for retention and LTV | Desilo",
            ogDescription: "Host gated spaces, cohorts, events, and courses in one hub. Increase engagement, reduce churn, and grow LTV with clear structure and clean UX.",
            twitterTitle: "Community OS for retention and LTV | Desilo",
            twitterDescription: "Host gated spaces, cohorts, events, and courses in one hub. Increase engagement, reduce churn, and grow LTV with clear structure and clean UX."
        }
    },
    {
        slug: "niche-social-platform",
        title: "Niche social platform",
        description: "Ship profiles, follows, DMs, and a smart feed with safety tools so your vertical gets a real home and network effects.",
        hero: {
            label: "USE CASE",
            title: "Niche social platform",
            description: "Ship profiles, follows, DMs, and a smart feed with safety tools so your vertical gets a real home and network effects.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Community Playbook",
                    modalTitle: "Get the Community Playbook"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Teams building an owned social space for a specific vertical."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "The core social stack plus the guardrails your community needs.",
            items: [
                {
                    id: 1,
                    title: "Profiles and identity",
                    description: "Custom profiles with bios, links, badges, and optional verification so members feel real and discoverable.",
                },
                {
                    id: 2,
                    title: "Follow graph and discovery",
                    description: "Follow, mute, and block with topic and people suggestions that surface relevant connections.",
                },
                {
                    id: 3,
                    title: "Smart feed",
                    description: "Chrono or relevance based feed with recency, quality, and trust signals so good posts rise.",
                },
                {
                    id: 4,
                    title: "Posts and media",
                    description: "Text, images, video, polls, and drafts with scheduled posting and editing windows.",
                },
                {
                    id: 5,
                    title: "Comments and reactions",
                    description: "Threaded replies and lightweight reactions to keep conversations clear and positive.",
                },
                {
                    id: 6,
                    title: "DMs and group chats",
                    description: "One to one and small group messaging with media, link previews, and safety controls.",
                },
                {
                    id: 7,
                    title: "Search and hashtags",
                    description: "Fast search with filters, saved queries, and topic tags to organize discovery.",
                },
                {
                    id: 8,
                    title: "Safety and moderation",
                    description: "Reporting, rate limits, word filters, and role tools so communities stay healthy.",
                },
                {
                    id: 9,
                    title: "Monetization hooks",
                    description: "Paywalls, tips, and member only areas that map to subscriptions and perks.",
                },
                {
                    id: 10,
                    title: "Analytics",
                    description: "Feed health, creators and posts distribution, and network growth to guide programming.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "Relevance builds habit, and habit builds network effects.",
            items: [
                {
                    id: 1,
                    title: "Stronger signal",
                    description: "relevance rises, feed quality improves, and posting becomes a habit",
                },
                {
                    id: 2,
                    title: "Faster growth",
                    description: "follow graph density increases and network effects kick in",
                },
                {
                    id: 3,
                    title: "Higher conversion",
                    description: "trusted context lifts opt-ins, purchases, and membership upgrades",
                },
                {
                    id: 4,
                    title: "Lower risk",
                    description: "safety tools reduce spam, abuse, and moderation overhead",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "From blueprint to live network with measurable feedback loops.",
            steps: [
                {
                    title: "Audience and purpose",
                    description: "Define who this is for, why it matters, and what good looks like in ninety days."
                },
                {
                    title: "Content and safety model",
                    description: "Set formats, rules, and moderation workflows that protect members and reduce noise."
                },
                {
                    title: "Feed and ranking design",
                    description: "Choose ranking signals and transparency UI so members understand why they see posts."
                },
                {
                    title: "Profiles and social actions",
                    description: "Design profiles, follows, and DMs that encourage healthy connections."
                },
                {
                    title: "Launch programming",
                    description: "Plan prompts, challenges, and events that kick-start posting in the first weeks."
                },
                {
                    title: "Measurement",
                    description: "Instrument creation, consumption, and connection metrics to tune the loops weekly."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Clear indicators of a healthy niche network.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "First post rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Weekly active posters up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Comment quality up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Report rate stable or down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Follow graph density up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Retention and LTV up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Fast UI, safe data, and scalable feeds.",
            items: [
                {
                    title: "Frontend",
                    description: "Accessible, responsive UI with fast lists, optimistic updates, and offline friendly patterns",
                },
                {
                    title: "Backend",
                    description: "Services for users, posts, relationships, and moderation with clear contracts",
                },
                {
                    title: "Feed and ranking",
                    description: "Jobs that compute relevance from recency, engagement, relationships, and trust signals",
                },
                {
                    title: "Storage and media",
                    description: "Image and video handling with thumbnails, transcodes, and safe links",
                },
                {
                    title: "Safety and governance",
                    description: "Rate limits, abuse detection signals, audit trails, and configurable policies",
                },
                {
                    title: "Data",
                    description: "Event map for feed interactions, creator health, and network growth",
                },
                {
                    title: "Integrations",
                    description: "Payments for tips and subs, email and push for lifecycle, analytics for dashboards",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that boost relevance and safety.",
            items: [
                {
                    title: "Verified profiles",
                    description: "Light or strong verification flows to increase trust",
                },
                {
                    title: "Topic onboarding",
                    description: "Pick interests to seed the follow graph and personalize the first feed",
                },
                {
                    title: "Creator tiers and perks",
                    description: "Member only content and badges that reward contribution",
                },
                {
                    title: "AI helpers",
                    description: "Toxicity checks, summary highlights, and suggested replies that stay on brand",
                },
                {
                    title: "Cross posting",
                    description: "Optional share-out to external platforms without fragmenting the community",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls that protect people and the brand.",
            tags: [
                "Role based access",
                "audit logs",
                "PII protection",
                "consent handling",
                "SSO and SCIM for team access",
                "Clear pathways for moderation requests and appeals"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "How do we avoid spam and low quality content?",
                    answer: "Use rate limits, word filters, role throttles, and clear reporting with human review."
                },
                {
                    id: 2,
                    question: "Can members understand why posts rank where they do?",
                    answer: "Yes. We add simple \"why you are seeing this\" cues and tune signals with data."
                },
                {
                    id: 3,
                    question: "Will this work on mobile first?",
                    answer: "Yes. We design for small screens and low bandwidth from the start."
                },
                {
                    id: 4,
                    question: "How does monetization fit a social feed?",
                    answer: "Tips, paywalled posts, and member areas map to subscriptions and tiers without breaking flow."
                },
                {
                    id: 5,
                    question: "Can we migrate an existing audience?",
                    answer: "We support invite waves, imports, and redirect paths so early adopters arrive together."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Community Playbook",
                    modalTitle: "Get the Community Playbook"
                }
            }
        },
        seo: {
            title: "Niche social platform with smart feeds and safety | Desilo",
            description: "Ship profiles, follows, DMs, and a relevance based feed with safety tools. Give your vertical a real home and unlock network effects.",
            keywords: "Niche social platform, social network, community platform, social media, niche community, social platform development",
            ogTitle: "Niche social platform with smart feeds and safety | Desilo",
            ogDescription: "Ship profiles, follows, DMs, and a relevance based feed with safety tools. Give your vertical a real home and unlock network effects.",
            twitterTitle: "Niche social platform with smart feeds and safety | Desilo",
            twitterDescription: "Ship profiles, follows, DMs, and a relevance based feed with safety tools. Give your vertical a real home and unlock network effects."
        }
    },
    {
        slug: "decentralized-identity-portability",
        title: "Decentralized identity and portability",
        description: "Use ActivityPub and DIDs with optional wallets and on chain receipts so creators own audience, content, and payouts with no platform lock in.",
        hero: {
            label: "USE CASE",
            title: "Decentralized identity and portability",
            description: "Use ActivityPub and DIDs with optional wallets and on chain receipts so creators own audience, content, and payouts with no platform lock in.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Portability Playbook",
                    modalTitle: "Get the Portability Playbook"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Teams that want ownership and freedom to move. Creators, startups, and platforms that need portable identity, audience lists, and earnings that are not trapped inside a single app."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Portable identity, interoperable feeds, and verifiable earnings.",
            items: [
                {
                    id: 1,
                    title: "Decentralized identity (DIDs)",
                    description: "Issue or link DIDs so creators prove who they are across apps without new logins each time.",
                },
                {
                    id: 2,
                    title: "ActivityPub compatibility",
                    description: "Publish and follow using ActivityPub so posts and relationships travel across federated networks.",
                },
                {
                    id: 3,
                    title: "Audience portability",
                    description: "Export and import followers and subscribers so mailing lists and social graphs move with the creator.",
                },
                {
                    id: 4,
                    title: "Content provenance",
                    description: "Attach signatures and hashes to posts and media so authorship and integrity are verifiable.",
                },
                {
                    id: 5,
                    title: "On chain receipts",
                    description: "Record payouts or revenue shares as signed receipts on chain for transparent settlements.",
                },
                {
                    id: 6,
                    title: "Wallet optional flows",
                    description: "Offer custodial or no wallet options so onboarding stays simple while enabling future self custody.",
                },
                {
                    id: 7,
                    title: "Permissions and consents",
                    description: "Granular controls for what moves, who can use it, and for how long.",
                },
                {
                    id: 8,
                    title: "Portability dashboard",
                    description: "Clear controls to request copies, revoke access, and see where data is connected.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "Ownership builds resilience and trust.",
            items: [
                {
                    id: 1,
                    title: "Platform resilience",
                    description: "audience and revenue survive algorithm or policy changes",
                },
                {
                    id: 2,
                    title: "Greater trust",
                    description: "verified identity reduces impersonation and dispute costs",
                },
                {
                    id: 3,
                    title: "Lower CAC",
                    description: "cross-app portability preserves followers and shortens re-activation",
                },
                {
                    id: 4,
                    title: "Cleaner settlements",
                    description: "on-chain receipts speed reconciliation and reduce disputes",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A pragmatic path from today's stack to open rails.",
            steps: [
                {
                    title: "Map current data",
                    description: "Identify identity sources, follower lists, content stores, and payout records."
                },
                {
                    title: "Choose standards",
                    description: "Adopt DIDs for identity, ActivityPub for federation, and a simple receipt schema for earnings."
                },
                {
                    title: "Design consent flows",
                    description: "Explain what moves, why it moves, and how to reverse it. Keep language human."
                },
                {
                    title: "Build connectors",
                    description: "Create import and export paths for identity, audience, and content with rate limits and logs."
                },
                {
                    title: "Enable receipts",
                    description: "Write signed receipts for payouts or splits and surface them in the creator dashboard."
                },
                {
                    title: "Test portability",
                    description: "Run sandbox moves, verify signatures, and validate receipts against testnets before launch."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Proof of ownership and healthier economics.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Portability requests completed"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Verified identity adoption"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Cross platform follower retention"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Disputes and chargebacks down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Payout reconciliation time down"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Standards first, with a gentle developer experience.",
            items: [
                {
                    title: "Identity",
                    description: "DIDs with key rotation, verification endpoints, and optional OAuth bridges",
                },
                {
                    title: "Federation",
                    description: "ActivityPub inbox and outbox services, queue workers, and safety filters",
                },
                {
                    title: "Receipts",
                    description: "Signed payout receipts anchored on chain with human readable views",
                },
                {
                    title: "Data contracts",
                    description: "Schemas for profiles, relationships, posts, and payouts with versioning",
                },
                {
                    title: "Privacy and consent",
                    description: "Scoped tokens, revocation endpoints, and user facing logs of data moves",
                },
                {
                    title: "Developer ergonomics",
                    description: "SDKs, webhooks, and test fixtures for partners",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that make portability practical.",
            items: [
                {
                    title: "Verified handles",
                    description: "Link custom domains or ENS style names to DIDs for readable identity",
                },
                {
                    title: "Creator passports",
                    description: "Portable achievement or tier badges that travel across apps",
                },
                {
                    title: "Revenue split templates",
                    description: "Standard contracts for collaborations with clear receipt flows",
                },
                {
                    title: "Import assistants",
                    description: "Guided imports from major platforms with safe retries",
                },
                {
                    title: "Portability insights",
                    description: "Dashboards that show where audience and content move and how often they return",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Strong guarantees without heavy friction.",
            tags: [
                "Key management with rotation",
                "signed webhooks",
                "audit logs",
                "encryption at rest",
                "privacy by default",
                "SSO and SCIM for team access",
                "data moves respect consent and are fully traceable"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Do users need a crypto wallet to start?",
                    answer: "No. We support wallet optional onboarding and can enable self custody later."
                },
                {
                    id: 2,
                    question: "Which chains are supported for receipts?",
                    answer: "We recommend low fee, stable environments for anchoring receipts and can adapt to your policy."
                },
                {
                    id: 3,
                    question: "Can we integrate with our existing auth?",
                    answer: "Yes. Bridge identity through OAuth or SSO and map it to DIDs behind the scenes."
                },
                {
                    id: 4,
                    question: "How do we prevent spam and impersonation?",
                    answer: "Verified DIDs, signed posts, rate limits, and abuse reports reduce impersonation and noise."
                },
                {
                    id: 5,
                    question: "Will this break our current analytics?",
                    answer: "No. Event maps include federation and portability events so dashboards stay consistent."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Portability Playbook",
                    modalTitle: "Get the Portability Playbook"
                }
            }
        },
        seo: {
            title: "Decentralized identity and portability for creators | Desilo",
            description: "Use DIDs, ActivityPub, and on chain receipts with wallet optional flows. Give creators ownership of audience, content, and payouts with no platform lock in.",
            keywords: "Decentralized identity, DIDs, ActivityPub, creator portability, blockchain receipts, decentralized social, creator ownership",
            ogTitle: "Decentralized identity and portability for creators | Desilo",
            ogDescription: "Use DIDs, ActivityPub, and on chain receipts with wallet optional flows. Give creators ownership of audience, content, and payouts with no platform lock in.",
            twitterTitle: "Decentralized identity and portability for creators | Desilo",
            twitterDescription: "Use DIDs, ActivityPub, and on chain receipts with wallet optional flows. Give creators ownership of audience, content, and payouts with no platform lock in."
        }
    },
    {
        slug: "link-bio-storefront-social-commerce",
        title: "Link in bio storefront + Social commerce",
        description: "Turn one link into a mini site with carts, drops, bundles, and email capture so discovery converts on the spot.",
        hero: {
            label: "USE CASE",
            title: "Link in bio storefront + Social commerce",
            description: "Turn one link into a mini site with carts, drops, bundles, and email capture so discovery converts on the spot.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization Kit",
                    modalTitle: "Get the Monetization Kit"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Creators and brands that want instant conversion from social."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Everything needed to convert scrolls into sales and signups.",
            items: [
                {
                    id: 1,
                    title: "Mini site builder",
                    description: "Simple pages for bio, offers, and highlights with brandable themes.",
                },
                {
                    id: 2,
                    title: "Smart product cards",
                    description: "Courses, presets, memberships, bundles, and limited drops with timers and scarcity cues.",
                },
                {
                    id: 3,
                    title: "Cart and checkout",
                    description: "One page checkout with tax, coupons, and instant delivery or access.",
                },
                {
                    id: 4,
                    title: "Email and SMS capture",
                    description: "Capture leads before purchase with clear value and double opt in.",
                },
                {
                    id: 5,
                    title: "Bundles and upsells",
                    description: "Auto suggestions and post purchase offers that raise average order value.",
                },
                {
                    id: 6,
                    title: "Paywalls and trials",
                    description: "Soft or hard paywalls for member only content with free trials when it helps.",
                },
                {
                    id: 7,
                    title: "Link routing and UTM",
                    description: "Per platform links with UTM tagging so you see which posts convert.",
                },
                {
                    id: 8,
                    title: "Analytics",
                    description: "Clicks, conversion, AOV, LTV, and cohort retention in one simple view.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "Attention is brief; conversion must be instant. Social feeds move fast. A focused storefront removes friction so curiosity becomes purchase or subscribe in seconds. You keep the relationship with email and SMS, not just a one-time sale.",
            items: [
                {
                    id: 1,
                    title: "Higher conversion",
                    description: "one-tap checkout turns curiosity into purchases and signups",
                },
                {
                    id: 2,
                    title: "Bigger baskets",
                    description: "bundles and upsells raise average order value",
                },
                {
                    id: 3,
                    title: "Owned audience",
                    description: "email and SMS capture reduce reliance on platform reach",
                },
                {
                    id: 4,
                    title: "Clear attribution",
                    description: "per-platform UTMs reveal which posts drive revenue",
                },
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "From setup to first sale in a simple flow.",
            steps: [
                {
                    title: "Offer map",
                    description: "List what you sell now and the next logical bundle or membership."
                },
                {
                    title: "Design the mini site",
                    description: "Pick sections, add product cards, and set a clear primary action."
                },
                {
                    title: "Wire checkout and capture",
                    description: "Enable payments, tax, coupons, and email or SMS with double opt in."
                },
                {
                    title: "Tag links and launch",
                    description: "Create per-platform links with UTMs and publish your first post."
                },
                {
                    title: "Measure and refine",
                    description: "Review clicks, conversion, and AOV; test offers weekly and keep winners."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Conversion and revenue signals you can act on.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Click to purchase rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Trials to paid up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "AOV up with bundles and upsells"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Email list growth up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Refunds and failed payments down"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Fast pages, secure payments, clean data.",
            items: [
                {
                    title: "Frontend",
                    description: "Lightweight pages optimized for mobile with accessible components",
                },
                {
                    title: "Backend",
                    description: "Products, orders, entitlements, and fulfillment with clear contracts",
                },
                {
                    title: "Payments",
                    description: "Cards, wallets, and local methods with receipts and refunds",
                },
                {
                    title: "Email and SMS",
                    description: "Opt in capture, welcome sequences, and perk delivery",
                },
                {
                    title: "Data",
                    description: "UTM capture, events for views and purchases, cohort and AOV dashboards",
                },
                {
                    title: "Integrations",
                    description: "Email or CRM, file delivery, community gates, and shipping if needed",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that raise conversion and lifetime value.",
            items: [
                {
                    title: "Limited drops",
                    description: "Inventory caps, timers, and waitlists that create urgency",
                },
                {
                    title: "Creator bundles",
                    description: "Auto bundle best sellers or pair digital plus membership",
                },
                {
                    title: "Affiliate links",
                    description: "Track partner sales with unique codes and payouts",
                },
                {
                    title: "Social proof blocks",
                    description: "Ratings, testimonials, and recent purchases that build trust",
                },
                {
                    title: "AI copy assistant",
                    description: "Generate headlines and product blurbs that match your voice",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Trust built into payments and data.",
            tags: [
                "PCI compliant payments",
                "encrypted PII",
                "audit trails for orders and refunds",
                "clear consent for email and SMS",
                "SSO and team roles for brands and agencies"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Can this replace my full website?",
                    answer: "It can be your fast storefront while a full site grows. Many keep both."
                },
                {
                    id: 2,
                    question: "Can I sell both digital and membership products?",
                    answer: "Yes. Deliver files instantly and grant member access on payment events."
                },
                {
                    id: 3,
                    question: "Do I need email or SMS from day one?",
                    answer: "Yes. Capture while intent is high and send helpful follow ups."
                },
                {
                    id: 4,
                    question: "How do bundles work?",
                    answer: "Prebuilt or dynamic bundles suggest complementary items and apply a fair discount."
                },
                {
                    id: 5,
                    question: "Can I track which platform performs best?",
                    answer: "Per platform links with UTMs show clicks, conversion, and revenue."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization Kit",
                    modalTitle: "Get the Monetization Kit"
                }
            }
        },
        seo: {
            title: "Link in bio storefront and social commerce | Desilo",
            description: "Turn one link into a mini site with carts, drops, bundles, and email capture. Convert discovery on the spot and keep the relationship you build.",
            keywords: "Link in bio, social commerce, mini site, creator storefront, social selling, link tree alternative, bio link",
            ogTitle: "Link in bio storefront and social commerce | Desilo",
            ogDescription: "Turn one link into a mini site with carts, drops, bundles, and email capture. Convert discovery on the spot and keep the relationship you build.",
            twitterTitle: "Link in bio storefront and social commerce | Desilo",
            twitterDescription: "Turn one link into a mini site with carts, drops, bundles, and email capture. Convert discovery on the spot and keep the relationship you build."
        }
    },
    {
        slug: "live-shoppable-video",
        title: "Live and shoppable video",
        description: "Tag products in streams and clips with native checkout and post purchase flows so views turn into measured revenue.",
        hero: {
            label: "USE CASE",
            title: "Live and shoppable video",
            description: "Tag products in streams and clips with native checkout and post purchase flows so views turn into measured revenue.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization Kit",
                    modalTitle: "Get the Monetization Kit"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Teams turning attention into cart and checkout during video."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "The shoppable video stack from tag to receipt.",
            items: [
                {
                    id: 1,
                    title: "Product tagging",
                    description: "Tag items in live streams and clips with price, variants, and stock so buying is obvious.",
                },
                {
                    id: 2,
                    title: "Shoppable player",
                    description: "Overlays, pin to screen, and add to cart without leaving the video.",
                },
                {
                    id: 3,
                    title: "Native checkout",
                    description: "One page checkout with saved details, local methods, tax, and receipts.",
                },
                {
                    id: 4,
                    title: "Post purchase flows",
                    description: "Upsells, bundles, follow emails or SMS, and perk delivery right after payment.",
                },
                {
                    id: 5,
                    title: "Inventory and pricing",
                    description: "Real time stock, discount codes, and dynamic pricing for drops.",
                },
                {
                    id: 6,
                    title: "Affiliates and splits",
                    description: "Track creator revenue shares and partner links with transparent payouts.",
                },
                {
                    id: 7,
                    title: "Live chat and reactions",
                    description: "Chat, polls, and reactions that highlight tagged items and urgency.",
                },
                {
                    id: 8,
                    title: "Analytics",
                    description: "View to cart, cart to purchase, revenue per minute, and attribution by creator or clip.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "Video wins attention, shoppable wins revenue.",
            items: [
                {
                    id: 1,
                    title: "Revenue per minute up",
                    description: "in-player checkout lifts view-to-cart and cart-to-buy rates",
                },
                {
                    id: 2,
                    title: "Creator ROI proven",
                    description: "clip-level attribution shows which moments drive sales",
                },
                {
                    id: 3,
                    title: "Reduced drop-off",
                    description: "no redirects means fewer lost buyers on mobile",
                },
                {
                    id: 4,
                    title: "Repeat sales",
                    description: "post-purchase flows increase upsells and retention",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "From stream setup to measurable sales.",
            steps: [
                {
                    title: "Stream and clip setup",
                    description: "Connect your stream key or upload clips and sync the product catalog."
                },
                {
                    title: "Tagging and overlays",
                    description: "Tag items by timestamp and show overlays or pins during key moments."
                },
                {
                    title: "Checkout and fulfillment",
                    description: "Enable payments, tax, shipping or digital delivery, and confirmations."
                },
                {
                    title: "Lifecycle follow ups",
                    description: "Send receipts, perks, and winbacks tied to what viewers bought."
                },
                {
                    title: "Measurement",
                    description: "Track view to cart and purchase funnels and tune offers each week."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Clear commerce signals from your video.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Add to cart rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Checkout conversion up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Revenue per stream minute up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Refunds and failed payments down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Subscriber or email capture up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Low latency video with secure, reliable commerce.",
            items: [
                {
                    title: "Frontend",
                    description: "Shoppable player, overlays, and checkout optimized for mobile and desktop",
                },
                {
                    title: "Backend",
                    description: "Products, carts, orders, and entitlements with clear contracts and retries",
                },
                {
                    title: "Video",
                    description: "Ingest, low latency delivery, clipping, thumbnails, captions, and safe links",
                },
                {
                    title: "Payments",
                    description: "Cards, wallets, local methods, tax, invoices, refunds, and proration where needed",
                },
                {
                    title: "Data",
                    description: "Event map for views, tags, carts, purchases, upsells, and attribution",
                },
                {
                    title: "Integrations",
                    description: "Catalog sync, shipping, email or CRM, analytics, and affiliate platforms",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that boost urgency and lifetime value.",
            items: [
                {
                    title: "Limited drops",
                    description: "Timers, waitlists, and inventory caps that create urgency",
                },
                {
                    title: "Bundles and post purchase upsells",
                    description: "Auto pair items and offer upgrades after checkout",
                },
                {
                    title: "AI clipping and highlights",
                    description: "Auto generate short clips with the right product pinned",
                },
                {
                    title: "Creator affiliate links",
                    description: "Unique links and codes with tracked revenue and payouts",
                },
                {
                    title: "Captions and translations",
                    description: "Accessibility and reach for global viewers",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Commerce grade trust and safety for video.",
            tags: [
                "PCI compliant payments",
                "encrypted PII",
                "audit logs for orders and refunds",
                "moderation tools for chat",
                "content safety filters",
                "SSO and roles for brands, agencies, and creators"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Can we use our existing catalog and inventory?",
                    answer: "Yes. Sync products and stock from your commerce system and control pricing rules."
                },
                {
                    id: 2,
                    question: "Does this work on mobile first?",
                    answer: "Yes. The player, overlays, and checkout are designed for phones and low bandwidth."
                },
                {
                    id: 3,
                    question: "What about latency for live interactions?",
                    answer: "We target low latency so tags and chat feel in sync with the host."
                },
                {
                    id: 4,
                    question: "Can we track which creator or clip drove the sale?",
                    answer: "Yes. Attribution ties purchases to timestamps, clips, and creators."
                },
                {
                    id: 5,
                    question: "How do returns and refunds work?",
                    answer: "Orders and refunds flow through your commerce system with clear customer messaging."
                },
                {
                    id: 6,
                    question: "Do we need to stream only on our site?",
                    answer: "You can host on your site or embed. Simulcast and link out are supported when helpful."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization Kit",
                    modalTitle: "Get the Monetization Kit"
                }
            }
        },
        seo: {
            title: "Live and shoppable video that converts | Desilo",
            description: "Tag products in streams and clips with native checkout and post purchase flows. Turn views into measured revenue with clear attribution.",
            keywords: "Live shopping, shoppable video, live commerce, video commerce, live streaming, product tagging, video checkout, live selling",
            ogTitle: "Live and shoppable video that converts | Desilo",
            ogDescription: "Tag products in streams and clips with native checkout and post purchase flows. Turn views into measured revenue with clear attribution.",
            twitterTitle: "Live and shoppable video that converts | Desilo",
            twitterDescription: "Tag products in streams and clips with native checkout and post purchase flows. Turn views into measured revenue with clear attribution."
        }
    },
    {
        slug: "legal-compliance-hub",
        title: "Legal & compliance hub",
        description: "Automate contracts, rights, FTC disclosures, COPPA checks, and 1099, GST, and TDS docs so deals are clean and audit ready.",
        hero: {
            label: "USE CASE",
            title: "Legal & compliance hub",
            description: "Automate contracts, rights, FTC disclosures, COPPA checks, and 1099, GST, and TDS docs so deals are clean and audit ready.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Compliance Checklist",
                    modalTitle: "Get the Compliance Checklist"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Teams that need brand-safe campaigns and clean paperwork at scale."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "From contracts to filings in one place.",
            items: [
                {
                    id: 1,
                    title: "Contract templates and e-sign",
                    description: "Standard MSAs and SOWs with e-sign, version history, and change logs.",
                },
                {
                    id: 2,
                    title: "Rights and usage tracking",
                    description: "Usage terms, territories, expiries, and renewals with automated reminders.",
                },
                {
                    id: 3,
                    title: "Disclosure workflows",
                    description: "Built-in FTC disclosure prompts and checks across posts and creatives.",
                },
                {
                    id: 4,
                    title: "Audience and age checks",
                    description: "COPPA-aware flows, parent consent options, and flagged content types.",
                },
                {
                    id: 5,
                    title: "Creator onboarding and KYC",
                    description: "Collect IDs, tax details, and payment info with secure storage.",
                },
                {
                    id: 6,
                    title: "Tax forms and summaries",
                    description: "Generate and track 1099, GST, and TDS docs with status and exports.",
                },
                {
                    id: 7,
                    title: "Approval routing",
                    description: "Role-based reviews for legal, brand, and financial sign-off.",
                },
                {
                    id: 8,
                    title: "Audit trail and reports",
                    description: "Immutable logs of who did what and when, with exportable audit packs.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "Compliance should protect growth, not block it.",
            items: [
                {
                    id: 1,
                    title: "Faster deals",
                    description: "contracts, disclosures, and approvals stop blocking campaigns",
                },
                {
                    id: 2,
                    title: "Lower risk",
                    description: "rights tracking and audit logs prevent costly mistakes",
                },
                {
                    id: 3,
                    title: "Clean filings",
                    description: "tax docs and KYC stay current and exportable",
                },
                {
                    id: 4,
                    title: "Better renewals",
                    description: "brand safety and proof of compliance build trust",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A practical flow from intake to audit-ready.",
            steps: [
                {
                    title: "Policy setup",
                    description: "Capture your legal requirements by region, platform, and campaign type."
                },
                {
                    title: "Template and field mapping",
                    description: "Configure contracts, required fields, and disclosure rules once."
                },
                {
                    title: "Automations",
                    description: "Trigger disclosures, approvals, and reminders from campaign status or dates."
                },
                {
                    title: "Verification and checks",
                    description: "Run KYC, age checks, and content flags before launch."
                },
                {
                    title: "Records and exports",
                    description: "Generate tax forms, rights summaries, and audit packs on demand."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Less rework, fewer risks, faster deals.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Cycle time to legal sign-off down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Late disclosures down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Rights expiries missed down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "On-time tax docs up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Client renewals and margin up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Secure data, clear workflows, and reliable records.",
            items: [
                {
                    title: "Frontend",
                    description: "Manager-friendly UI for contracts, rights, and approvals with fast tables and filters",
                },
                {
                    title: "Backend",
                    description: "Services for documents, signatures, rights, disclosures, and tax records with clear APIs",
                },
                {
                    title: "Data",
                    description: "Encrypted PII, structured fields for regions and rules, and immutable audit logs",
                },
                {
                    title: "Integrations",
                    description: "E-sign, storage, payments, accounting, and CRM for end-to-end visibility",
                },
                {
                    title: "Internationalization",
                    description: "Jurisdiction fields and templates per region with locale-specific validation",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that raise trust and efficiency.",
            items: [
                {
                    title: "Clause library",
                    description: "Preapproved clauses and fallbacks for faster negotiations",
                },
                {
                    title: "Disclosure verification",
                    description: "Spot checks with screenshots and post URLs stored for proof",
                },
                {
                    title: "Rights calendar",
                    description: "A visual view of expiries, renewals, and blackout periods",
                },
                {
                    title: "Vendor and brand portals",
                    description: "Self-serve uploads, approvals, and document access",
                },
                {
                    title: "AI document assistant",
                    description: "Suggests missing fields, flags risky edits, and summarizes changes in plain language",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls that stand up to scrutiny.",
            tags: [
                "Role-based access",
                "SSO and SCIM options",
                "encryption at rest and in transit",
                "signed webhooks",
                "full audit trails",
                "data retention and deletion policies are configurable"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Can we use our own contract templates?",
                    answer: "Yes. We load your templates, lock key clauses, and track versions."
                },
                {
                    id: 2,
                    question: "How are FTC disclosures enforced?",
                    answer: "We add required fields and prompts and log proof of disclosure per post."
                },
                {
                    id: 3,
                    question: "Do you support multi-country tax workflows?",
                    answer: "Yes. Track forms and summaries such as 1099, GST, and TDS with exports for accounting."
                },
                {
                    id: 4,
                    question: "What about minors and COPPA?",
                    answer: "We support age gates, parent consent records, and flagged content types before launch."
                },
                {
                    id: 5,
                    question: "Can the system integrate with our CRM and accounting?",
                    answer: "Yes. We sync contacts, deals, invoices, and payouts for one source of truth."
                },
                {
                    id: 6,
                    question: "Who can see sensitive documents?",
                    answer: "Access is role-based with granular permissions and complete audit logs."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Compliance Checklist",
                    modalTitle: "Get the Compliance Checklist"
                }
            }
        },
        seo: {
            title: "Legal and compliance hub for creator campaigns | Desilo",
            description: "Automate contracts, rights, disclosures, COPPA checks, and 1099, GST, and TDS docs. Keep campaigns brand-safe and audit-ready with clear workflows and records.",
            keywords: "Legal compliance, creator contracts, FTC disclosures, COPPA compliance, tax forms, creator management, brand safety, compliance automation",
            ogTitle: "Legal and compliance hub for creator campaigns | Desilo",
            ogDescription: "Automate contracts, rights, disclosures, COPPA checks, and 1099, GST, and TDS docs. Keep campaigns brand-safe and audit-ready with clear workflows and records.",
            twitterTitle: "Legal and compliance hub for creator campaigns | Desilo",
            twitterDescription: "Automate contracts, rights, disclosures, COPPA checks, and 1099, GST, and TDS docs. Keep campaigns brand-safe and audit-ready with clear workflows and records."
        }
    },
    {
        slug: "creator-fintech-rails",
        title: "Creator fintech rails",
        description: "Multi-currency wallets, instant payouts, revenue splits and escrow, refunds, and chargeback handling so cash flow is predictable.",
        hero: {
            label: "USE CASE",
            title: "Creator fintech rails",
            description: "Multi-currency wallets, instant payouts, revenue splits and escrow, refunds, and chargeback handling so cash flow is predictable.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Fintech Playbook",
                    modalTitle: "Get the Fintech Playbook"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Teams moving money for creators with fewer headaches."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Everything needed to move money with clarity and control.",
            items: [
                {
                    id: 1,
                    title: "Multi-currency wallets",
                    description: "Hold balances in local currencies and reduce FX surprises.",
                },
                {
                    id: 2,
                    title: "Instant and scheduled payouts",
                    description: "Same-day or programmable payout cycles with clear statuses.",
                },
                {
                    id: 3,
                    title: "Revenue splits and escrow",
                    description: "Set splits per deal, hold funds until delivery, and release on milestones.",
                },
                {
                    id: 4,
                    title: "Refunds and chargeback handling",
                    description: "One place to trigger refunds, respond to disputes, and track outcomes.",
                },
                {
                    id: 5,
                    title: "Creator onboarding and KYC",
                    description: "Collect IDs, tax info, and payout details with secure storage.",
                },
                {
                    id: 6,
                    title: "Tax forms and summaries",
                    description: "Generate 1099, GST, and TDS docs with downloadable reports.",
                },
                {
                    id: 7,
                    title: "Compliance checks",
                    description: "Sanctions screening, fraud signals, and audit trails by default.",
                },
                {
                    id: 8,
                    title: "Reconciliation and ledger",
                    description: "Double-entry ledger, payouts vs. orders match, and error queues.",
                },
                {
                    id: 9,
                    title: "FX and fees transparency",
                    description: "Clear conversions and fee breakdowns creators and finance can trust.",
                },
                {
                    id: 10,
                    title: "Payout methods",
                    description: "Bank transfer, UPI, wallet, card, or alternative methods based on region.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "Predictable cash flow builds trust on all sides.",
            items: [
                {
                    id: 1,
                    title: "Global scale",
                    description: "multi-currency support reduces FX leakage and delays",
                },
                {
                    id: 2,
                    title: "Predictable cash flow",
                    description: "on-time payouts increase creator satisfaction and retention",
                },
                {
                    id: 3,
                    title: "Fewer disputes",
                    description: "structured refunds and chargebacks cut losses and support load",
                },
                {
                    id: 4,
                    title: "Finance clarity",
                    description: "ledgers and reconciliations speed month-end close",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "From onboarding to settlement in a few clear steps.",
            steps: [
                {
                    title: "Map flows",
                    description: "Define how funds enter, split, hold, and exit per deal and region."
                },
                {
                    title: "Configure rules",
                    description: "Set payout schedules, thresholds, and approval steps by role."
                },
                {
                    title: "Onboard safely",
                    description: "Run KYC, collect tax info, and verify payout methods."
                },
                {
                    title: "Move funds",
                    description: "Create wallets, apply splits, manage escrow, and trigger payouts."
                },
                {
                    title: "Handle exceptions",
                    description: "Process refunds and disputes, then reconcile in the ledger."
                },
                {
                    title: "Report and improve",
                    description: "Monthly summaries for finance, dispute stats, and payout reliability."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Money signals leadership and creators care about.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Payout on-time rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Dispute resolution time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Refund accuracy up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Finance close time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Creator satisfaction and renewals up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Reliable ledgers, safe data, and compliant workflows.",
            items: [
                {
                    title: "Frontend",
                    description: "Creator and manager dashboards for balances, splits, payouts, and disputes.",
                },
                {
                    title: "Backend",
                    description: "Wallets, ledger entries, payouts, refunds, and disputes with idempotent APIs.",
                },
                {
                    title: "Integrations",
                    description: "Payments, banking as a service, KYC, accounting, CRM, and tax platforms.",
                },
                {
                    title: "Data",
                    description: "Event map for orders, splits, payouts, refunds, and disputes with exports.",
                },
                {
                    title: "FX and fees",
                    description: "Providers for quotes, conversions, and transparent fee lines.",
                },
                {
                    title: "Ops",
                    description: "Queues for retries, alerting, and human review when rules flag risk.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that increase trust and speed.",
            items: [
                {
                    title: "Creator earnings portal",
                    description: "Self-serve invoices, payout history, and downloadable tax docs.",
                },
                {
                    title: "Advance on earnings",
                    description: "Offer safe advances against booked deals with clear fees.",
                },
                {
                    title: "Prepaid or virtual cards",
                    description: "Scoped spend for campaign budgets and travel.",
                },
                {
                    title: "Milestone escrow",
                    description: "Automate fund release on content approvals or delivery checks.",
                },
                {
                    title: "Affiliate payouts",
                    description: "Track partner earnings and pay on a reliable cadence.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls that stand up to audits.",
            tags: [
                "Role-based access",
                "SSO and SCIM options",
                "encryption at rest and in transit",
                "signed webhooks",
                "audit logs",
                "data-retention policies"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Do creators need to switch banks or apps?",
                    answer: "No. We support common payout methods per region and keep setup simple."
                },
                {
                    id: 2,
                    question: "Can we run splits by campaign and creator?",
                    answer: "Yes. Splits are set per deal, with overrides and effective dates."
                },
                {
                    id: 3,
                    question: "How are chargebacks handled?",
                    answer: "Disputes route to a queue with evidence capture and clear outcomes."
                },
                {
                    id: 4,
                    question: "What tax workflows are supported?",
                    answer: "Collect W-9 or W-8, generate 1099, and support GST or TDS summaries where required."
                },
                {
                    id: 5,
                    question: "Will this work across countries?",
                    answer: "Yes. Multi-currency balances, FX, and region-specific payout methods are available."
                },
                {
                    id: 6,
                    question: "Who owns financial data and records?",
                    answer: "You do. We provide exports, APIs, and audit packs for finance and compliance."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Fintech Playbook",
                    modalTitle: "Get the Fintech Playbook"
                }
            }
        },
        seo: {
            title: "Creator fintech rails for payouts and splits | Desilo",
            description: "Multi-currency wallets, instant payouts, revenue splits and escrow, refunds, and chargeback handling. Make creator cash flow predictable with clean ledgers and audits.",
            keywords: "Creator payments, fintech rails, multi-currency wallets, instant payouts, revenue splits, creator finance, payment processing, creator economy",
            ogTitle: "Creator fintech rails for payouts and splits | Desilo",
            ogDescription: "Multi-currency wallets, instant payouts, revenue splits and escrow, refunds, and chargeback handling. Make creator cash flow predictable with clean ledgers and audits.",
            twitterTitle: "Creator fintech rails for payouts and splits | Desilo",
            twitterDescription: "Multi-currency wallets, instant payouts, revenue splits and escrow, refunds, and chargeback handling. Make creator cash flow predictable with clean ledgers and audits."
        }
    },
    {
        slug: "creator-mental-health-resilience",
        title: "Creator mental health and resilience",
        description: "Check ins, quiet hours, burnout signals, coach handoffs, and crisis resources so well being is part of the product by design.",
        hero: {
            label: "USE CASE",
            title: "Creator mental health and resilience",
            description: "Check ins, quiet hours, burnout signals, coach handoffs, and crisis resources so well being is part of the product by design.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Well Being Playbook",
                    modalTitle: "Get the Well Being Playbook"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Teams that care about sustainable creation, not just output."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Lightweight tools that reduce overwhelm and support recovery.",
            items: [
                {
                    id: 1,
                    title: "Mood and energy check ins",
                    description: "Simple pulses that track how creators feel over time and flag gentle adjustments.",
                },
                {
                    id: 2,
                    title: "Quiet hours and do not disturb",
                    description: "Scheduled downtime that pauses notifications, pings, and auto replies.",
                },
                {
                    id: 3,
                    title: "Burnout signals",
                    description: "Patterns from cadence, sleep proxy, and message load that suggest a lighter plan.",
                },
                {
                    id: 4,
                    title: "Coach or peer handoffs",
                    description: "Opt in routing to coaches or peer mentors when trends dip.",
                },
                {
                    id: 5,
                    title: "Crisis resources",
                    description: "Location aware helplines and safety links surfaced only when requested.",
                },
                {
                    id: 6,
                    title: "Workload planner",
                    description: "Timeboxing and buffers that balance sponsored and personal work.",
                },
                {
                    id: 7,
                    title: "Content boundaries and filters",
                    description: "Mute topics, keywords, or accounts that harm focus or mood.",
                },
                {
                    id: 8,
                    title: "Support routing",
                    description: "Fast lane flags that tell managers when to shift deadlines or redistribute tasks.",
                },
                {
                    id: 9,
                    title: "Anonymized insights",
                    description: "Aggregate views for trends without exposing personal data.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "Healthy rhythms make better work and steadier revenue.",
            items: [
                {
                    id: 1,
                    title: "Fewer misses",
                    description: "late deliverables and cancellations drop with healthier cadence",
                },
                {
                    id: 2,
                    title: "Lower churn",
                    description: "sustainable pace keeps creators and staff longer",
                },
                {
                    id: 3,
                    title: "Less noise",
                    description: "quiet hours and boundaries reduce off-hour pings",
                },
                {
                    id: 4,
                    title: "Higher quality",
                    description: "consistent energy leads to steadier output and happier audiences",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "Practical, opt in patterns that respect privacy.",
            steps: [
                {
                    title: "Define safeguards",
                    description: "Choose check in cadence, quiet hours, and what data is collected."
                },
                {
                    title: "Design gentle prompts",
                    description: "Short, skippable questions with clear controls and private by default."
                },
                {
                    title: "Set boundaries",
                    description: "Mute windows, content filters, and escalation rules that creators control."
                },
                {
                    title: "Route support",
                    description: "Map dips to coaches, peers, or manager notes based on consent."
                },
                {
                    title: "Review trends",
                    description: "Show personal streaks and recovery patterns and only share aggregates with teams."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that correlate with resilience and reliability.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Late deliverables down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Last minute cancellations down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Off hour pings down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Consistency of posting up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Creator satisfaction up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Private by default, minimal data, clean controls.",
            items: [
                {
                    title: "Frontend",
                    description: "Lightweight prompts, schedules, and dashboards that are easy to dismiss or adjust.",
                },
                {
                    title: "Backend",
                    description: "Scoped storage for mood, schedules, and boundaries with strict retention options.",
                },
                {
                    title: "Data",
                    description: "Local first where possible, hashed identifiers, and aggregate reports.",
                },
                {
                    title: "Integrations",
                    description: "Calendar, chat, and task tools to honor quiet hours and boundaries.",
                },
                {
                    title: "Ops",
                    description: "Feature flags for gradual rollout and clear consent logs.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that support sustainable pace.",
            items: [
                {
                    title: "Focus mode",
                    description: "Temporarily hides non essential metrics and comments during deep work.",
                },
                {
                    title: "Recovery planner",
                    description: "Suggests lighter weeks after heavy launches.",
                },
                {
                    title: "Peer rooms",
                    description: "Small circles for accountability and encouragement.",
                },
                {
                    title: "Coach marketplace",
                    description: "Opt in directory of certified coaches with simple booking.",
                },
                {
                    title: "AI well being assistant",
                    description: "On brand nudges and summaries that suggest small adjustments.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Protect privacy and give creators control.",
            tags: [
                "Private by default",
                "role based access",
                "encryption in transit and at rest",
                "clear deletion paths",
                "aggregates hide personal details"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Can creators opt out entirely?",
                    answer: "Yes. Features are optional and configurable with clear controls."
                },
                {
                    id: 2,
                    question: "Who can see personal check ins?",
                    answer: "Only the creator by default. Teams see aggregates unless explicit consent is given."
                },
                {
                    id: 3,
                    question: "Will this slow teams down?",
                    answer: "Prompts are lightweight, skippable, and tuned for weekly cadence."
                },
                {
                    id: 4,
                    question: "Do you integrate with our calendars and chat?",
                    answer: "Yes. Quiet hours sync to calendars and mute rules map to chat tools."
                },
                {
                    id: 5,
                    question: "What about different regions and resources?",
                    answer: "Crisis links and helplines adapt by location with your legal review."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Well Being Playbook",
                    modalTitle: "Get the Well Being Playbook"
                }
            }
        },
        seo: {
            title: "Creator mental health and resilience tools | Desilo",
            description: "Add check ins, quiet hours, burnout signals, coach handoffs, and crisis resources. Build sustainable creation with privacy first controls and humane workflows.",
            keywords: "Creator mental health, creator wellness, burnout prevention, creator resilience, sustainable creation, mental health tools, creator support",
            ogTitle: "Creator mental health and resilience tools | Desilo",
            ogDescription: "Add check ins, quiet hours, burnout signals, coach handoffs, and crisis resources. Build sustainable creation with privacy first controls and humane workflows.",
            twitterTitle: "Creator mental health and resilience tools | Desilo",
            twitterDescription: "Add check ins, quiet hours, burnout signals, coach handoffs, and crisis resources. Build sustainable creation with privacy first controls and humane workflows."
        }
    },
    {
        slug: "creator-passport-trust-layer",
        title: "Creator passport and trust layer",
        description: "One portable ID with KYC, rights, wellness preferences, and payout info that plugs in anywhere. An AI layer assists with compliance and safety.",
        hero: {
            label: "USE CASE",
            title: "Creator passport and trust layer",
            description: "One portable ID with KYC, rights, wellness preferences, and payout info that plugs in anywhere. An AI layer assists with compliance and safety.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Portability Playbook",
                    modalTitle: "Get the Portability Playbook"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Platforms and agencies that need trusted creators at scale."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "A portable profile that carries trust signals across apps.",
            items: [
                {
                    id: 1,
                    title: "Verified identity and KYC",
                    description: "Government ID, liveness checks, and watchlist screens stored with strict controls.",
                },
                {
                    id: 2,
                    title: "Rights and usage profile",
                    description: "Licensing terms, territories, expiries, and preferred attribution ready for deals.",
                },
                {
                    id: 3,
                    title: "Wellness and boundary preferences",
                    description: "Quiet hours, sensitive topic filters, and escalation contacts the creator controls.",
                },
                {
                    id: 4,
                    title: "Payout preferences",
                    description: "Preferred methods, currencies, and tax status for fast routing.",
                },
                {
                    id: 5,
                    title: "Safety posture",
                    description: "History of disclosures, strikes, and appeals with clear remediation steps.",
                },
                {
                    id: 6,
                    title: "Compliance assistant",
                    description: "AI checks for missing disclosures, risky clauses, and flagged content before launch.",
                },
                {
                    id: 7,
                    title: "Consent ledger",
                    description: "Logs of what data is shared with whom, for what purpose, and for how long.",
                },
                {
                    id: 8,
                    title: "Single sign and connect",
                    description: "One click connect to partner apps with scoped permissions and revocation.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "Faster onboarding, cleaner deals, fewer surprises.",
            items: [
                {
                    id: 1,
                    title: "Faster onboarding",
                    description: "reusable KYC and payouts shorten time to first deal",
                },
                {
                    id: 2,
                    title: "Safer campaigns",
                    description: "AI checks and clear scopes reduce compliance errors",
                },
                {
                    id: 3,
                    title: "Lower overhead",
                    description: "fewer repeated forms and manual verifications",
                },
                {
                    id: 4,
                    title: "Higher confidence",
                    description: "brands see verified profiles and cleaner histories, boosting win rates",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "Practical steps from first verification to partner integrations.",
            steps: [
                {
                    title: "Enroll and verify",
                    description: "Creators submit KYC, set rights and payout details, and choose privacy settings."
                },
                {
                    title: "Set preferences",
                    description: "Quiet hours, sensitive categories, and escalation contacts configured by the creator."
                },
                {
                    title: "Share on consent",
                    description: "Partners request scopes; creators approve with clear explanations and expiry."
                },
                {
                    title: "AI assist",
                    description: "Automated checks flag disclosure gaps, risky edits, and policy mismatches."
                },
                {
                    title: "Transact and record",
                    description: "Deals use the passport profile, and a consent ledger records what was shared."
                },
                {
                    title: "Review and update",
                    description: "Creators update details; partners receive changes through webhooks."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Trust and speed you can see in the numbers.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Onboarding time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Deal cycle time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Disclosure misses down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Dispute rate down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Payout exceptions down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Creator satisfaction up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Standards based identity with privacy by default.",
            items: [
                {
                    title: "Identity",
                    description: "DIDs or verified IDs with rotation and proof endpoints; optional OAuth and SSO bridges.",
                },
                {
                    title: "Data model",
                    description: "Schemas for identity, rights, wellness preferences, payouts, and consents with versioning.",
                },
                {
                    title: "APIs and webhooks",
                    description: "Scoped tokens, granular permissions, revocation, and change notifications.",
                },
                {
                    title: "AI policy checks",
                    description: "Models evaluate disclosures, clause risk, and content safety with human review paths.",
                },
                {
                    title: "Security",
                    description: "Encryption in transit and at rest, audit logs, regional data residency options.",
                },
                {
                    title: "Interoperability",
                    description: "Connectors for CRM, e-sign, payments, accounting, and safety vendors.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that raise confidence and reduce manual work.",
            items: [
                {
                    title: "Verified handles",
                    description: "Map custom domains or readable handles to the creator passport.",
                },
                {
                    title: "Rights calendar",
                    description: "Visual view of expiries and renewals with alerts.",
                },
                {
                    title: "Deal templates",
                    description: "Preapproved agreements that auto pull passport fields.",
                },
                {
                    title: "Appeals workflow",
                    description: "Creators can request review, attach context, and track outcomes.",
                },
                {
                    title: "Insight dashboards",
                    description: "Benchmarks for onboarding time, dispute rate, and disclosure compliance.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Creator control with enterprise-grade safeguards.",
            tags: [
                "Role based access",
                "consent by default",
                "granular scopes",
                "full audit trails",
                "data retention is configurable",
                "deletion requests are honored"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Do creators control what partners can see?",
                    answer: "Yes. Scopes are explicit and revocable. The consent ledger records every share."
                },
                {
                    id: 2,
                    question: "Can this work with our current auth and KYC?",
                    answer: "Yes. We bridge your auth and reuse KYC providers through adapters."
                },
                {
                    id: 3,
                    question: "What if a partner needs additional checks?",
                    answer: "Partners can request extra scopes or documents with creator consent."
                },
                {
                    id: 4,
                    question: "How does the AI compliance assistant avoid false flags?",
                    answer: "It provides explanations and confidence scores and routes edge cases to human review."
                },
                {
                    id: 5,
                    question: "Will passports work across multiple brands or regions?",
                    answer: "Yes. Regional policies and data residency are supported, and scopes can vary by partner."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Portability Playbook",
                    modalTitle: "Get the Portability Playbook"
                }
            }
        },
        seo: {
            title: "Creator passport and trust layer | Desilo",
            description: "A portable ID with KYC, rights, wellness preferences, and payouts that plugs into partner apps. AI assists with compliance and safety so onboarding is fast and trusted.",
            keywords: "Creator passport, creator identity, KYC verification, creator trust, portable ID, creator onboarding, compliance automation, creator safety",
            ogTitle: "Creator passport and trust layer | Desilo",
            ogDescription: "A portable ID with KYC, rights, wellness preferences, and payouts that plugs into partner apps. AI assists with compliance and safety so onboarding is fast and trusted.",
            twitterTitle: "Creator passport and trust layer | Desilo",
            twitterDescription: "A portable ID with KYC, rights, wellness preferences, and payouts that plugs into partner apps. AI assists with compliance and safety so onboarding is fast and trusted."
        }
    },
    {
        slug: "post-article-paywalls",
        title: "Post and article paywalls",
        description: "Lock posts and longform with previews, ",
        hero: {
            label: "USE CASE",
            title: "Post and article paywalls",
            description: "Lock posts and longform with previews, ",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization Kit",
                    modalTitle: "Get the Monetization Kit"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Creators and teams turning attention into member revenue."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Paywall patterns that feel fair and convert.",
            items: [
                {
                    id: 1,
                    title: "Previews and teases",
                    description: "Show a compelling intro or summary while protecting the full post.",
                },
                {
                    id: 2,
                    title: "Metered access",
                    description: "Let readers sample a set number of free reads each month before pay.",
                },
                {
                    id: 3,
                    title: "Day passes and topic passes",
                    description: "Sell short access windows or subject bundles for casual readers.",
                },
                {
                    id: 4,
                    title: "Tiers and trials",
                    description: "Monthly or annual plans with free trials and upgrade paths.",
                },
                {
                    id: 5,
                    title: "Bundles",
                    description: "Combine posts with community, courses, or newsletters to raise value.",
                },
                {
                    id: 6,
                    title: "Checkout and receipts",
                    description: "One page checkout with tax, invoices, and instant access.",
                },
                {
                    id: 7,
                    title: "Entitlements and roles",
                    description: "Map plans and passes to content rules and expiries.",
                },
                {
                    id: 8,
                    title: "Subscriber messaging",
                    description: "Welcome, renewal, and winback messages that explain value clearly.",
                },
                {
                    id: 9,
                    title: "Analytics",
                    description: "Views to paywall, paywall to purchase, churn, ARPU, LTV, and cohort retention.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "Respect readers and revenue follows.",
            items: [
                {
                    id: 1,
                    title: "Predictable revenue",
                    description: "readers convert to members and renew reliably",
                },
                {
                    id: 2,
                    title: "Higher conversion",
                    description: "clear previews, meters, and tiers lift trials to paid",
                },
                {
                    id: 3,
                    title: "Lower churn",
                    description: "save paths, pauses, and winbacks reduce cancellations",
                },
                {
                    id: 4,
                    title: "Keep discovery",
                    description: "previews and gifted passes preserve sharing and SEO",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A simple path from free reads to paid habit.",
            steps: [
                {
                    title: "Value map",
                    description: "List content types, audience segments, and what each segment values."
                },
                {
                    title: "Choose the paywall pattern",
                    description: "Pick preview style, meter count, passes, and tiers that fit your catalog."
                },
                {
                    title: "Design flows and copy",
                    description: "Write friendly prompts, comparison tables, and upgrade cues that respect readers."
                },
                {
                    title: "Wire payments and entitlements",
                    description: "Enable plans, receipts, and access rules tied to posts and collections."
                },
                {
                    title: "Lifecycle messages",
                    description: "Send welcomes, renewal reminders, and winbacks that explain benefits."
                },
                {
                    title: "Measure and tune",
                    description: "Track funnels and cohorts, test meter counts and offers, and keep winners."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Proof that the paywall is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Views to paywall up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Trials to paid up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Churn down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "ARPU and LTV up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Support tickets about access down"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Fast pages, clean rules, and reliable access.",
            items: [
                {
                    title: "Frontend",
                    description: "Accessible article pages, paywall prompts, and comparison tables that load quickly.",
                },
                {
                    title: "Backend",
                    description: "Entitlements, meters, passes, and expiries with clear rules and logs.",
                },
                {
                    title: "Payments",
                    description: "Cards, wallets, and local methods with tax, invoices, refunds, and proration.",
                },
                {
                    title: "Data",
                    description: "Event map for views, gates, purchases, renewals, and churn with cohort dashboards.",
                },
                {
                    title: "Integrations",
                    description: "Email or CRM for lifecycle, CMS for content sync, and community for bundled perks.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that raise conversion and retention.",
            items: [
                {
                    title: "Gifted passes",
                    description: "Allow members to share a limited number of free reads each month.",
                },
                {
                    title: "Student or regional pricing",
                    description: "Discounts verified by email or provider to widen access.",
                },
                {
                    title: "Corporate seats",
                    description: "Group access for teams with admin controls and shared billing.",
                },
                {
                    title: "Referral credits",
                    description: "Give and get rewards that boost word of mouth.",
                },
                {
                    title: "AI summary block",
                    description: "Short, on brand summaries above the fold to increase preview value.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Protect content and respect privacy.",
            tags: [
                "Rate limiting",
                "signed links",
                "sensible anti scraping patterns",
                "encrypted PII",
                "role based access",
                "audit logs",
                "clear data retention"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Will a paywall hurt discovery?",
                    answer: "Use previews, meters, and gifted passes so new readers sample before subscribing."
                },
                {
                    id: 2,
                    question: "How do passes work with tiers?",
                    answer: "Passes grant short access to selected content while tiers unlock everything by plan."
                },
                {
                    id: 3,
                    question: "Can we bundle with community or courses?",
                    answer: "Yes. Entitlements can include forums, events, or course modules."
                },
                {
                    id: 4,
                    question: "How do we reduce involuntary churn?",
                    answer: "Card updater, smart retries, and gentle reminders before expiry."
                },
                {
                    id: 5,
                    question: "Can we migrate existing subscribers?",
                    answer: "We import subscribers, plans, and renewal dates with minimal disruption."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization Kit",
                    modalTitle: "Get the Monetization Kit"
                }
            }
        },
        seo: {
            title: "Post and article paywalls that convert | Desilo",
            description: "Launch previews, meters, passes, tiers, trials, and bundles. Lift trials to paid, reduce churn, and grow predictable revenue with fair, reader friendly paywalls.",
            keywords: "Article paywalls, content monetization, subscription paywalls, metered paywall, content gating, reader revenue, subscription conversion",
            ogTitle: "Post and article paywalls that convert | Desilo",
            ogDescription: "Launch previews, meters, passes, tiers, trials, and bundles. Lift trials to paid, reduce churn, and grow predictable revenue with fair, reader friendly paywalls.",
            twitterTitle: "Post and article paywalls that convert | Desilo",
            twitterDescription: "Launch previews, meters, passes, tiers, trials, and bundles. Lift trials to paid, reduce churn, and grow predictable revenue with fair, reader friendly paywalls."
        }
    },
    {
        slug: "creator-monetization-platform",
        title: "Creator monetization platform",
        description: "Subscriptions, paywalled content, fan CRM, premium experiences, bundles, and analytics built on workflow driven product engineering.",
        hero: {
            label: "USE CASE",
            title: "Creator monetization platform",
            description: "Subscriptions, paywalled content, fan CRM, premium experiences, bundles, and analytics built on workflow driven product engineering.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization MVP Checklist",
                    modalTitle: "Get the Monetization MVP Checklist"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Startup founders, platforms, studios, and product teams building creator monetization software or direct to fan revenue systems who need reliable billing, workflow aligned UX, scalable architecture, and creator behavior driven design. Ideal if revenue is fragile, workflows feel disconnected, or the product depends on engineering shortcuts instead of structured product thinking."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "From audience to predictable recurring revenue with a unified system.",
            items: [
                {
                    id: 1,
                    title: "Subscriptions and memberships",
                    description: "Tiered plans, perks, renewals, churn handling, entitlements, and lifecycle workflows.",
                },
                {
                    id: 2,
                    title: "Paywalled and pay per view content",
                    description: "Locked posts, episodic media, premium feeds, bundles, and upgrade paths.",
                },
                {
                    id: 3,
                    title: "Digital offers and bundles",
                    description: "One time products, templates, programs, bundles, upsells, coupons, and price testing.",
                },
                {
                    id: 4,
                    title: "Premium sessions and custom experiences",
                    description: "Bookable calls, coaching, consulting, shoutouts, requests, live interactions, and automated fulfillment.",
                },
                {
                    id: 5,
                    title: "Fan CRM and engagement workflows",
                    description: "Messaging, segments, fan notes, onboarding journeys, retention triggers, and relationship history.",
                },
                {
                    id: 6,
                    title: "Discovery and conversion funnels",
                    description: "Profile to paywall journeys, free to paid conversion paths, referral loops, browsing surfaces.",
                },
                {
                    id: 7,
                    title: "Safety, permissions, and policy controls",
                    description: "Access rules, moderation workflows, content flags, risk checks, and regional feature controls.",
                },
                {
                    id: 8,
                    title: "Revenue and growth analytics",
                    description: "MRR, GMV, churn, cohorts, conversion funnels, retention metrics, and offer performance insights.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when monetization follows creator workflows.",
            items: [
                {
                    id: 1,
                    title: "Higher revenue per fan",
                    description: "bundles, upsells, value tiers, and pricing aligned to creator earning behavior",
                },
                {
                    id: 2,
                    title: "More predictable recurring revenue",
                    description: "lifecycle workflows reduce churn volatility",
                },
                {
                    id: 3,
                    title: "Better creator adoption",
                    description: "UX matches how creators think, publish, and monetize",
                },
                {
                    id: 4,
                    title: "Scalable product foundation",
                    description: "architecture behaves like infrastructure, not a temporary MVP",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A workflow based approach grounded in creator behavior and UX strategy.",
            steps: [
                {
                    title: "Creator workflow mapping",
                    description: "Identify monetization patterns, audience behavior, pricing psychology, and earning loops."
                },
                {
                    title: "Funnel and pricing prototyping",
                    description: "Simulate profile, paywall, checkout, bundles, and upsell flows with real user feedback."
                },
                {
                    title: "Build the monetization spine",
                    description: "Subscriptions, PPV, bundles, entitlements, messaging, and permissions as one structured system."
                },
                {
                    title: "Safety and governance setup",
                    description: "Moderation workflows, access rules, audit logs, and compliance friendly controls."
                },
                {
                    title: "Analytics and signal design",
                    description: "Event schema for revenue, churn, discovery sources, engagement and retention cohorts."
                },
                {
                    title: "Launch and iterate",
                    description: "Run pricing experiments, onboarding optimization, UX refinements, and behavior informed improvements."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that prove the monetization system is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Time to first paid transaction down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Revenue per fan up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Revenue per creator up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Free to paid conversion up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Retention visible by cohort"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Support friction down"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Design driven product engineering with workflow aligned architecture.",
            items: [
                {
                    title: "Frontend",
                    description: "UX first modular components for profiles, feeds, paywalls, chat, checkout, dashboards.",
                },
                {
                    title: "Backend",
                    description: "Services for offers, entitlements, messaging, discovery, workflows, queues, retries.",
                },
                {
                    title: "Payments",
                    description: "Subscriptions, one time purchases, bundles, trials, refunds, entitlement mapping.",
                },
                {
                    title: "Data",
                    description: "Event taxonomy, funnel tracking, cohorts, export pipelines, analytics readiness.",
                },
                {
                    title: "AI ready hooks",
                    description: "Optional integration for assisted messaging, automation, insights, discovery enrichment.",
                },
                {
                    title: "Safety",
                    description: "Permissions framework, moderation workflows, content safety signals, governance records.",
                },
                {
                    title: "Ops",
                    description: "Environments, CI and CD, monitoring, logging, controlled rollout and experiment safety.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extensions that accelerate revenue and growth.",
            items: [
                {
                    title: "Pricing and offer testing lab",
                    description: "Run experiments for tiers, bundles, trials, PPV versus subscription.",
                },
                {
                    title: "Referral and ambassador mechanics",
                    description: "Reward loops for creators, partners, and high intent fans.",
                },
                {
                    title: "Multi tenant and roster mode",
                    description: "Support agencies, networks, and talent managers on the same core system.",
                },
                {
                    title: "Advanced analytics and warehouse integration",
                    description: "Send events to BI and product growth tools for deeper insight.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls leadership and compliance teams can trust. Human review is available where needed. Signals and decisions stay explainable for audits and risk conversations.",
            tags: [
                "Role based access control",
                "encrypted data flows",
                "audit logs",
                "retention policies",
                "environment isolation"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Will this platform support complex monetization models?",
                    answer: "Yes. It is designed for subscriptions, PPV, bundles, programs, and premium services."
                },
                {
                    id: 2,
                    question: "Is this only for one creator niche?",
                    answer: "No. The workflows adapt across creator verticals and audience behaviors."
                },
                {
                    id: 3,
                    question: "Can pricing and upsells be customized per creator?",
                    answer: "Yes. Offers, bundles, and experiments are configurable."
                },
                {
                    id: 4,
                    question: "Can we integrate third party tools and providers?",
                    answer: "Yes. The system is built as extensible product infrastructure."
                },
                {
                    id: 5,
                    question: "Who owns the platform and data?",
                    answer: "You own the codebase, data, and user assets."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization MVP Checklist",
                    modalTitle: "Get the Monetization MVP Checklist"
                }
            }
        },
        seo: {
            title: "Creator monetization platform development | Desilo",
            description: "Build a workflow driven creator monetization platform with subscriptions, paywalls, bundles, fan CRM, premium experiences, analytics, and design driven product engineering for scalable direct to fan revenue.",
            keywords: "Creator monetization, creator economy, paywalls, subscriptions, fan CRM",
            ogTitle: "Creator monetization platform development | Desilo",
            ogDescription: "Build a workflow driven creator monetization platform with subscriptions, paywalls, bundles, fan CRM, premium experiences, analytics, and design driven product engineering for scalable direct to fan revenue.",
            twitterTitle: "Creator monetization platform development | Desilo",
            twitterDescription: "Build a workflow driven creator monetization platform with subscriptions, paywalls, bundles, fan CRM, premium experiences, analytics, and design driven product engineering for scalable direct to fan revenue."
        }
    },
    {
        slug: "creator-led-saas",
        title: "Creator led SaaS (AI and workflow)",
        description: "AI powered creator SaaS prototypes built around real creator workflows, behavior insights, UX strategy, and design driven product engineering.",
        hero: {
            label: "USE CASE",
            title: "Creator led SaaS (AI and workflow)",
            description: "AI powered creator SaaS prototypes built around real creator workflows, behavior insights, UX strategy, and design driven product engineering.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Prototype Launch Checklist",
                    modalTitle: "Get the Prototype Launch Checklist"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Startup founders and product teams building creator SaaS tools, AI creator assistants, workflow automation platforms, and content productivity software who want to test real demand with working prototypes instead of investing in full engineering builds too early. Ideal for teams exploring creator tool development, AI SaaS for creators, or workflow automation for content creators, and who need proof of traction, validated workflows, and willingness to pay before scaling the product."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "AI prototypes that match creator behavior and validate product market fit.",
            items: [
                {
                    id: 1,
                    title: "AI content repurposing copilots",
                    description: "Convert long form content into shorts, captions, carousels, threads, and newsletters using workflow aligned creator journeys.",
                },
                {
                    id: 2,
                    title: "AI scriptwriting and ideation tools",
                    description: "Generate hooks, outlines, talking points, and story frameworks designed around creator creative habits and behavior patterns.",
                },
                {
                    id: 3,
                    title: "Collaboration and outreach assistants",
                    description: "Automate brand outreach, pitch workflows, follow ups, and opportunity mapping using creator friendly UX patterns.",
                },
                {
                    id: 4,
                    title: "Research and analytics copilots",
                    description: "Summarize comments, performance trends, niche signals, and audience insights to inform creator decisions and content strategy.",
                },
                {
                    id: 5,
                    title: "Workflow based automations",
                    description: "Prototypes designed around real creator loops such as ideation, creation, publish, engage, analyze, repurpose, repeat.",
                },
                {
                    id: 6,
                    title: "Behavior driven usage signals",
                    description: "Track time to value, repeat actions, skipped steps, and friction points to guide the product roadmap.",
                },
                {
                    id: 7,
                    title: "Experiment ready monetization",
                    description: "Trials, credit models, usage tiers, gating, and pricing tests built into the prototype environment.",
                },
                {
                    id: 8,
                    title: "Design driven UX shells",
                    description: "Interfaces built for trust, clarity, and usability so creators feel confident using the tool in real workflows.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "Build tech that fits creator behavior rather than forcing creators to adapt to tools.",
            items: [
                {
                    id: 1,
                    title: "Validated demand instead of assumptions",
                    description: "real activation and repeated usage signals from creators",
                },
                {
                    id: 2,
                    title: "Workflow aligned product vision",
                    description: "features shaped by behavior, not feature wishlists",
                },
                {
                    id: 3,
                    title: "Lower engineering risk",
                    description: "avoid scaling a product before confirming product market fit",
                },
                {
                    id: 4,
                    title: "Founder confidence",
                    description: "data backed direction for positioning, pricing, and niche focus",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A workflow based product engineering approach designed for early traction.",
            steps: [
                {
                    title: "Creator workflow mapping",
                    description: "Identify real creator jobs to be done, behavior loops, edge cases, and decision points before building features."
                },
                {
                    title: "Prototype UX flows",
                    description: "Design lightweight but credible journeys that follow how creators naturally think and work."
                },
                {
                    title: "AI capability stitching",
                    description: "Combine LLMs, prompts, context inputs, retrieval, and constraints inside structured workflows instead of raw text generation."
                },
                {
                    title: "Experiment and pricing setup",
                    description: "Activation milestones, value checkpoints, willingness to pay signals, and user feedback hooks included from the beginning."
                },
                {
                    title: "Creator test cohort",
                    description: "Run prototypes with real creators to gather behavioral evidence instead of theoretical feedback."
                },
                {
                    title: "Iterate and refine",
                    description: "Remove friction, validate value moments, and evolve the product thesis before scaling engineering."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that indicate the prototype is worth turning into a real product.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Activation rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Repeat task usage visible"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Time to first value down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Willingness to pay validated"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Drop off points identified"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Clear roadmap direction"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Design driven product engineering with lean, reliable prototype architecture.",
            items: [
                {
                    title: "Frontend",
                    description: "UX focused interfaces with task driven layouts, minimal friction, and behavior aligned flows.",
                },
                {
                    title: "Backend",
                    description: "Prototype ready services for workflows, prompts, asset storage, and event logging.",
                },
                {
                    title: "Models",
                    description: "LLMs for structured generation, embeddings for recall, heuristics for constraint control.",
                },
                {
                    title: "Prompt and context architecture",
                    description: "Reusable prompt frameworks, role conditioning, and consistency templates.",
                },
                {
                    title: "Data",
                    description: "Usage events, funnels, qualitative notes, exportable learning artifacts.",
                },
                {
                    title: "Integrations",
                    description: "Publishing tools, storage platforms, Notion, cloud drives, email, and creator tooling when relevant.",
                },
                {
                    title: "Ops",
                    description: "Rate limits, monitoring, fallbacks, and safe handling for unreliable AI outputs.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Enhancements that strengthen validation and founder learning.",
            items: [
                {
                    title: "Workflow template libraries",
                    description: "Reusable scripts, outreach templates, and creation frameworks organized by creator niche.",
                },
                {
                    title: "Team collaboration mode",
                    description: "Shared workspaces for creators, editors, and managers.",
                },
                {
                    title: "Light analytics dashboards",
                    description: "Usage snapshots and performance context tied to generated outputs.",
                },
                {
                    title: "Persistent project memory",
                    description: "Save and reuse context for ongoing creative projects.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Trustable prototyping designed for real creator workflows. This approach supports responsible AI usage, transparent behavior tracking, and reliable UX for live experimentation.",
            tags: [
                "Role based access",
                "scoped permissions",
                "encrypted assets",
                "explainable prompts",
                "activity logs",
                "opt in data handling"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Is this a clickable mockup or a functional prototype?",
                    answer: "It is a functional workflow driven prototype designed for real creator usage."
                },
                {
                    id: 2,
                    question: "Can this prototype evolve into the full product later?",
                    answer: "Yes. Architecture and learning loops are designed to scale instead of being scrapped."
                },
                {
                    id: 3,
                    question: "Does this require advanced AI engineering?",
                    answer: "No. The focus is on workflow quality, creator behavior alignment, and UX outcomes."
                },
                {
                    id: 4,
                    question: "Can we test multiple use cases?",
                    answer: "We prioritize one high value workflow first to avoid noisy results."
                },
                {
                    id: 5,
                    question: "How fast can we launch?",
                    answer: "Most creator SaaS prototypes validate insights within weeks instead of quarters."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Prototype Launch Checklist",
                    modalTitle: "Get the Prototype Launch Checklist"
                }
            }
        },
        seo: {
            title: "Creator SaaS prototype development with AI and workflow design | Desilo",
            description: "Build AI powered creator SaaS prototypes designed around real creator workflows, UX behavior patterns, and design driven product engineering. Validate traction before scaling full product development.",
            keywords: "Creator SaaS, AI creator tools, workflow automation, prototype development, creator economy, SaaS prototyping",
            ogTitle: "Creator SaaS prototype development with AI and workflow design | Desilo",
            ogDescription: "Build AI powered creator SaaS prototypes designed around real creator workflows, UX behavior patterns, and design driven product engineering. Validate traction before scaling full product development.",
            twitterTitle: "Creator SaaS prototype development with AI and workflow design | Desilo",
            twitterDescription: "Build AI powered creator SaaS prototypes designed around real creator workflows, UX behavior patterns, and design driven product engineering. Validate traction before scaling full product development."
        }
    }
];

// ============================================================================
// ESTABLISHED/GROWING ICP - Established companies scaling
// ============================================================================
const establishedGrowingUseCases: UseCaseData[] = [
    {
        slug: "platform-redesign-accessibility",
        title: "Platform redesign and accessibility",
        description: "New design system, WCAG 2.2 compliance, faster flows, and higher conversion.",
        hero: {
            label: "USE CASE",
            title: "Platform redesign and accessibility",
            description: "New design system, WCAG 2.2 compliance, faster flows, and higher conversion.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "See the Redesign Checklist",
                    modalTitle: "See the Redesign Checklist"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Product teams with scale that need a modern, accessible experience."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "A modern system that users love and legal reviews trust.",
            items: [
                {
                    id: 1,
                    title: "Design system and tokens",
                    description: "Unified components, states, and themes for speed and consistency.",
                },
                {
                    id: 2,
                    title: "WCAG 2.2 compliance",
                    description: "Color, focus, keyboard, motion, and assistive-tech support with verified checks.",
                },
                {
                    id: 3,
                    title: "Navigation and IA refresh",
                    description: "Clear structure and labels so people find what they need fast.",
                },
                {
                    id: 4,
                    title: "Critical flow redesigns",
                    description: "Signup, onboarding, search, purchase, and account flows that reduce friction.",
                },
                {
                    id: 5,
                    title: "Performance upgrades",
                    description: "Budgets, lazy loading, and image policies that make pages feel instant.",
                },
                {
                    id: 6,
                    title: "Content design and microcopy",
                    description: "Helpful messages, error states, and empty states that guide choices.",
                },
                {
                    id: 7,
                    title: "Localization readiness",
                    description: "Layout rules, truncation guards, and RTL options.",
                },
                {
                    id: 8,
                    title: "Instrumentation and A/B hooks",
                    description: "Event maps, IDs, and flags so experiments ship without rework.",
                },
                {
                    id: 9,
                    title: "Accessibility documentation",
                    description: "Audits, VPAT-style notes, and acceptance criteria for reviews.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when UX and accessibility level up.",
            items: [
                {
                    id: 1,
                    title: "Higher conversion",
                    description: "clearer flows and faster pages raise signup and checkout",
                },
                {
                    id: 2,
                    title: "Lower churn",
                    description: "accessible, predictable patterns increase task success",
                },
                {
                    id: 3,
                    title: "Bigger deals",
                    description: "compliance evidence and SSO readiness unblock enterprise",
                },
                {
                    id: 4,
                    title: "Faster shipping",
                    description: "a real design system cuts design and dev time",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "From audit to live system without a full rebuild.",
            steps: [
                {
                    title: "Assess and prioritize",
                    description: "Run UX and accessibility audits, then rank flows by revenue and risk."
                },
                {
                    title: "Design the system",
                    description: "Create tokens, components, and usage rules with examples."
                },
                {
                    title: "Redesign critical paths",
                    description: "Apply the system to top flows and validate with quick usability tests."
                },
                {
                    title: "Harden and document",
                    description: "Write specs, acceptance criteria, and accessibility notes per component."
                },
                {
                    title: "Ship with flags",
                    description: "Roll out behind feature flags and measure impact with events and cohorts."
                },
                {
                    title: "Enable teams",
                    description: "Deliver Figma files, code packages, and checklists for future work."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show the redesign is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Signup conversion up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Checkout conversion up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Time to first value down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Task success up for assistive tech users"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "NPS and CSAT up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Support tickets about confusion down"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Systemized UI, measurable improvements, calm releases.",
            items: [
                {
                    title: "Frontend",
                    description: "Accessible components, focus management, and keyboard paths baked in.",
                },
                {
                    title: "Backend",
                    description: "No risky rewrites; adapt existing endpoints and add flags and event IDs.",
                },
                {
                    title: "Performance",
                    description: "Budgets, audits, and guardrails to prevent regressions.",
                },
                {
                    title: "Data",
                    description: "Event taxonomy for funnels and cohorts and dashboards for before and after.",
                },
                {
                    title: "Tooling",
                    description: "Linters, tests, and CI checks for accessibility and performance.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that compound gains.",
            items: [
                {
                    title: "Design QA program",
                    description: "Checklists and reviews for every release.",
                },
                {
                    title: "Localization pass",
                    description: "Copy frameworks and layout tests for new markets.",
                },
                {
                    title: "Content audit",
                    description: "Tone and clarity passes for top pages and flows.",
                },
                {
                    title: "Motion and feedback kit",
                    description: "Standard transitions and progress patterns that feel responsive.",
                },
                {
                    title: "Accessibility training",
                    description: "Short sessions for designers and engineers with live examples.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Compliance and evidence clients will trust. We implement your legal and privacy policies and provide the proofs your sales team needs.",
            tags: [
                "Accessibility reports",
                "acceptance criteria in tickets",
                "audit logs for changes",
                "VPAT-style documentation",
                "Optional SSO and SCIM support for enterprise reviews"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Will this require a full rebuild?",
                    answer: "No. We phase changes with a design system and feature flags."
                },
                {
                    id: 2,
                    question: "Do you provide accessibility testing with assistive tech?",
                    answer: "Yes. Keyboard, screen reader, and contrast checks are part of acceptance."
                },
                {
                    id: 3,
                    question: "How do we avoid performance regressions?",
                    answer: "Set budgets, test in CI, and monitor Core Web Vitals after release."
                },
                {
                    id: 4,
                    question: "Can we keep our current component library?",
                    answer: "We can extend or wrap it and migrate incrementally."
                },
                {
                    id: 5,
                    question: "How soon will we see impact?",
                    answer: "Critical paths often show conversion and time-to-value gains within the first release window."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "See the Redesign Checklist",
                    modalTitle: "See the Redesign Checklist"
                }
            }
        },
        seo: {
            title: "Platform redesign and accessibility for growth | Desilo",
            description: "Launch a modern design system, meet WCAG 2.2, and streamline critical flows. Increase conversion, reduce churn, and ship faster with performance and measurement built in.",
            keywords: "Platform redesign, accessibility, design system, WCAG 2.2, UX redesign, conversion optimization, user experience",
            ogTitle: "Platform redesign and accessibility for growth | Desilo",
            ogDescription: "Launch a modern design system, meet WCAG 2.2, and streamline critical flows. Increase conversion, reduce churn, and ship faster with performance and measurement built in.",
            twitterTitle: "Platform redesign and accessibility for growth | Desilo",
            twitterDescription: "Launch a modern design system, meet WCAG 2.2, and streamline critical flows. Increase conversion, reduce churn, and ship faster with performance and measurement built in."
        }
    },
    {
        slug: "community-platform-modernization",
        title: "Community platform modernization",
        description: "Real time chat, events, moderation, scalable roles, and zero downtime rollout.",
        hero: {
            label: "USE CASE",
            title: "Community platform modernization",
            description: "Real time chat, events, moderation, scalable roles, and zero downtime rollout.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Community Playbook",
                    modalTitle: "Get the Community Playbook"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Teams with an existing community that needs speed, safety, and scale."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Modern community features that lift engagement and trust.",
            items: [
                {
                    id: 1,
                    title: "Real time chat and threads",
                    description: "Fast channels with typing, reactions, mentions, and thread views that keep context clear.",
                },
                {
                    id: 2,
                    title: "Events and live sessions",
                    description: "Calendars, registrations, reminders, recordings, and replays with time zone support.",
                },
                {
                    id: 3,
                    title: "Cohorts and programs",
                    description: "Limited runs with start and end dates, progress, and outcomes that create momentum.",
                },
                {
                    id: 4,
                    title: "Scalable roles and permissions",
                    description: "Granular roles, groups, and space rules that match how your org actually works.",
                },
                {
                    id: 5,
                    title: "Moderation and safety",
                    description: "Reports, queues, mute or ban, rate limits, word filters, and appeal paths.",
                },
                {
                    id: 6,
                    title: "Member profiles and discovery",
                    description: "Interests, activity, achievements, and recommendations that power relevant connections.",
                },
                {
                    id: 7,
                    title: "Perks and entitlements",
                    description: "Auto roles, spaces, and perks based on plan or milestones.",
                },
                {
                    id: 8,
                    title: "Analytics and health",
                    description: "Engagement, retention, cohort health, and revenue by tier with drill downs.",
                },
                {
                    id: 9,
                    title: "Admin console",
                    description: "Bulk actions, exports, and audit trails so ops stay calm.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when community runs on modern rails.",
            items: [
                {
                    id: 1,
                    title: "Higher engagement",
                    description: "faster chat and better programming raise weekly active members",
                },
                {
                    id: 2,
                    title: "Lower churn",
                    description: "clear roles and perks make value obvious and sticky",
                },
                {
                    id: 3,
                    title: "Less overhead",
                    description: "stronger tools reduce moderation time and support tickets",
                },
                {
                    id: 4,
                    title: "Safer growth",
                    description: "zero downtime rollout and audit trails protect trust",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A phased path that upgrades without breaking habit.",
            steps: [
                {
                    title: "Assess and plan",
                    description: "Audit performance, safety, and structure. Prioritize by impact and risk."
                },
                {
                    title: "Design the model",
                    description: "Define spaces, roles, and programs that map to tiers and goals."
                },
                {
                    title: "Build compatible features",
                    description: "Ship chat, events, cohorts, and moderation behind flags."
                },
                {
                    title: "Migrate in waves",
                    description: "Mirror spaces, sync roles, and move members with redirects and safe retries."
                },
                {
                    title: "Operate and improve",
                    description: "Review health weekly, tune programs, and scale roles as the community grows."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show modernization is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Weekly active members up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Post and reply quality up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Moderation time per incident down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Renewal rate up and churn down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Support tickets about access down"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Fast UI, reliable services, safe migrations.",
            items: [
                {
                    title: "Frontend",
                    description: "Accessible chat and event views, optimistic updates, and offline friendly patterns.",
                },
                {
                    title: "Backend",
                    description: "Services for spaces, roles, posts, events, moderation, and entitlements with clear contracts.",
                },
                {
                    title: "Messaging",
                    description: "Low latency websockets, presence, and backpressure controls for peak loads.",
                },
                {
                    title: "Data",
                    description: "Event map for engagement, safety, and renewals with cohort dashboards.",
                },
                {
                    title: "Migrations",
                    description: "Dual write or sync adapters, ID mapping, redirects, and backfills for zero downtime rollout.",
                },
                {
                    title: "Ops",
                    description: "Feature flags, logs, alerts, and disaster recovery plans with tested runbooks.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that amplify engagement and revenue.",
            items: [
                {
                    title: "Community gates for Discord or Slack",
                    description: "Sync roles to tiers so external chat matches membership.",
                },
                {
                    title: "Member directory and matchmaking",
                    description: "Search by skills or interests and auto suggest intros.",
                },
                {
                    title: "Mentor or office hours booking",
                    description: "Slots tied to tiers so higher plans get real access.",
                },
                {
                    title: "Referral credits",
                    description: "Give and get rewards that drive quality growth.",
                },
                {
                    title: "AI community assistant",
                    description: "Summaries, highlights, and gentle nudges that keep conversations useful.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls that protect members and the brand. Moderation actions and data moves are logged. Privacy and consent settings are transparent and user controlled.",
            tags: [
                "Role based access",
                "SSO and SCIM options",
                "encrypted PII",
                "signed webhooks",
                "rate limits",
                "clear audit trails"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Can we migrate without downtime or data loss?",
                    answer: "Yes. We use dual writes or sync adapters, map IDs, and cut over in waves."
                },
                {
                    id: 2,
                    question: "Will members need to relearn everything?",
                    answer: "We keep layout familiar and layer upgrades behind simple prompts and tours."
                },
                {
                    id: 3,
                    question: "Can we keep parts of our legacy stack?",
                    answer: "We can wrap or replace selectively and migrate over time."
                },
                {
                    id: 4,
                    question: "How do we prevent spam and abuse at scale?",
                    answer: "Rate limits, filters, roles, and queues reduce noise, with appeal paths for fairness."
                },
                {
                    id: 5,
                    question: "Can roles and perks match our plans?",
                    answer: "Yes. Entitlements map to plans and milestones and update automatically."
                },
                {
                    id: 6,
                    question: "How soon will we see impact?",
                    answer: "Engagement and support signals usually improve on the first phased release."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Community Playbook",
                    modalTitle: "Get the Community Playbook"
                }
            }
        },
        seo: {
            title: "Community platform modernization for scale | Desilo",
            description: "Add real time chat, events, moderation, and scalable roles with zero downtime rollout. Increase engagement, reduce churn, and protect trust with safe migrations and clear analytics.",
            keywords: "Community platform, community modernization, real-time chat, community events, community moderation, scalable community, community management",
            ogTitle: "Community platform modernization for scale | Desilo",
            ogDescription: "Add real time chat, events, moderation, and scalable roles with zero downtime rollout. Increase engagement, reduce churn, and protect trust with safe migrations and clear analytics.",
            twitterTitle: "Community platform modernization for scale | Desilo",
            twitterDescription: "Add real time chat, events, moderation, and scalable roles with zero downtime rollout. Increase engagement, reduce churn, and protect trust with safe migrations and clear analytics."
        }
    },
    {
        slug: "social-graph-feed-relevance",
        title: "Social graph and feed relevance",
        description: "AI ranking, deduplication, safety signals, and cold start friendly feeds.",
        hero: {
            label: "USE CASE",
            title: "Social graph and feed relevance",
            description: "AI ranking, deduplication, safety signals, and cold start friendly feeds.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Relevance Playbook",
                    modalTitle: "Get the Relevance Playbook"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Platforms that need a smarter feed and healthier network effects."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Ranking and graph features that make every session feel tailored.",
            items: [
                {
                    id: 1,
                    title: "Follow graph and interests",
                    description: "Build and enrich relationships plus declared interests to seed relevance.",
                },
                {
                    id: 2,
                    title: "AI ranking pipeline",
                    description: "Blend recency, quality, relationship strength, and intent signals with explainable scores.",
                },
                {
                    id: 3,
                    title: "Deduplication and diversity",
                    description: "Remove near-duplicates and balance sources so the feed stays fresh.",
                },
                {
                    id: 4,
                    title: "Cold start helpers",
                    description: "Topic pickers, starter follows, and lightweight \"like three things\" prompts.",
                },
                {
                    id: 5,
                    title: "Safety and quality signals",
                    description: "Downrank spam, clickbait, and policy-risk content with reviewable rules.",
                },
                {
                    id: 6,
                    title: "Topic and people suggestions",
                    description: "On-feed recommendations that grow the graph without breaking trust.",
                },
                {
                    id: 7,
                    title: "Time-shifted and catch-up views",
                    description: "Smart \"since you were away\" slots to compress missed value.",
                },
                {
                    id: 8,
                    title: "Feedback controls",
                    description: "Hide, mute, and \"show me more like this\" so users steer tuning.",
                },
                {
                    id: 9,
                    title: "Ranking experiments",
                    description: "Flagged variants with guardrails for lift tests and quality checks.",
                },
                {
                    id: 10,
                    title: "Transparency cues",
                    description: "Simple \"why you are seeing this\" notes that build credibility.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when relevance is real.",
            items: [
                {
                    id: 1,
                    title: "Higher engagement",
                    description: "more posts viewed and acted on per session",
                },
                {
                    id: 2,
                    title: "Faster habit formation",
                    description: "cold start friction drops and week-one retention rises",
                },
                {
                    id: 3,
                    title: "Better creator economics",
                    description: "quality distribution improves conversion and LTV",
                },
                {
                    id: 4,
                    title: "Lower risk",
                    description: "safety downranks and diversity rules reduce complaints",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A measured path from today's feed to adaptive ranking.",
            steps: [
                {
                    title: "Instrument signals",
                    description: "Log views, dwell, saves, follows, hides, and complaints with context."
                },
                {
                    title: "Model the graph",
                    description: "Combine follows, interactions, and interests into a weighted relationship map."
                },
                {
                    title: "Design the ranker",
                    description: "Choose features and weights, define caps for duplicates and author concentration."
                },
                {
                    title: "Ship behind flags",
                    description: "Roll out to slices, compare variants, and protect cohorts with guardrails."
                },
                {
                    title: "Close the loop",
                    description: "Collect explicit feedback and retrain on fresh interactions weekly."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show relevance is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "First session success up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Day-7 and Day-30 retention up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Posts per session up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Hide and complaint rate down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Follow graph density up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Conversion to creator goals up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Reliable pipelines, explainable models, safe fallbacks.",
            items: [
                {
                    title: "Frontend",
                    description: "Fast lists with optimistic updates, hide and mute controls, and context labels.",
                },
                {
                    title: "Backend",
                    description: "Feed assembly, ranking services, and feature stores with clear contracts.",
                },
                {
                    title: "Data",
                    description: "Event taxonomy, streaming ingestion, and batch jobs for features and training sets.",
                },
                {
                    title: "Models and rules",
                    description: "Logistic or gradient models with safety rules and diversity caps layered on top.",
                },
                {
                    title: "Experimentation",
                    description: "Bucketing, guardrails, and metrics for lift, fatigue, and safety.",
                },
                {
                    title: "Observability",
                    description: "Dashboards for freshness, duplicate rate, author concentration, and latency.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that boost quality and growth.",
            items: [
                {
                    title: "Starter graph seeding",
                    description: "Auto-follows based on import or declared interests to warm new accounts.",
                },
                {
                    title: "Creator fairness pack",
                    description: "Exposure balancing to avoid over-concentration on a few accounts.",
                },
                {
                    title: "Topic taxonomy",
                    description: "Lightweight tags and embeddings to group content without heavy manual work.",
                },
                {
                    title: "Quality scorecards",
                    description: "Per-creator feedback on what improves distribution.",
                },
                {
                    title: "AI summarization slots",
                    description: "Highlights that recap threads or long posts to improve catch-up.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls that keep feeds fair and defensible. We disclose controls to users and maintain explainable decisions for trust and reviews.",
            tags: [
                "Documented ranking factors",
                "audit logs for rule changes",
                "abuse throttles",
                "privacy-respecting data collection",
                "Optional SSO and SCIM for admin access"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Will this hurt chronological users?",
                    answer: "No. Offer a clean chrono view while keeping relevance as the default with clear toggles."
                },
                {
                    id: 2,
                    question: "How do we prevent filter bubbles?",
                    answer: "Diversity caps and exploratory slots ensure regular exposure to new sources."
                },
                {
                    id: 3,
                    question: "Can creators game the system?",
                    answer: "Signals are multi-factor and monitored; low-quality tactics lose weight over time."
                },
                {
                    id: 4,
                    question: "How big a data set do we need?",
                    answer: "Start with rules plus light models, then grow features as signal improves."
                },
                {
                    id: 5,
                    question: "What if a model regresses?",
                    answer: "Guardrails and quick rollback paths keep quality stable during tests."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Relevance Playbook",
                    modalTitle: "Get the Relevance Playbook"
                }
            }
        },
        seo: {
            title: "Social graph and feed relevance for growth | Desilo",
            description: "Add AI ranking, deduplication, safety signals, and cold-start helpers. Increase engagement, retention, and creator economics with explainable, testable feed systems.",
            keywords: "Social graph, feed relevance, AI ranking, social media algorithm, content recommendation, social feed optimization, social media engagement",
            ogTitle: "Social graph and feed relevance for growth | Desilo",
            ogDescription: "Add AI ranking, deduplication, safety signals, and cold-start helpers. Increase engagement, retention, and creator economics with explainable, testable feed systems.",
            twitterTitle: "Social graph and feed relevance for growth | Desilo",
            twitterDescription: "Add AI ranking, deduplication, safety signals, and cold-start helpers. Increase engagement, retention, and creator economics with explainable, testable feed systems."
        }
    },
    {
        slug: "shoppable-media-live-commerce",
        title: "Shoppable media and live commerce",
        description: "Product tagging, native checkout, post purchase flows, and clean attribution.",
        hero: {
            label: "USE CASE",
            title: "Shoppable media and live commerce",
            description: "Product tagging, native checkout, post purchase flows, and clean attribution.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization Kit",
                    modalTitle: "Get the Monetization Kit"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Product and growth teams turning views into measurable revenue."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "The commerce layer that lives where attention already is.",
            items: [
                {
                    id: 1,
                    title: "Product tagging",
                    description: "Attach items to moments in streams and clips with variants and pricing.",
                },
                {
                    id: 2,
                    title: "Shoppable player",
                    description: "Add to cart and buy without leaving video or galleries.",
                },
                {
                    id: 3,
                    title: "Native checkout",
                    description: "One page checkout with saved details, local methods, tax, and receipts.",
                },
                {
                    id: 4,
                    title: "Post purchase flows",
                    description: "Upsells, bundles, emails or SMS, and perk delivery after payment.",
                },
                {
                    id: 5,
                    title: "Inventory and pricing",
                    description: "Real time stock, discount codes, and drops with waitlists.",
                },
                {
                    id: 6,
                    title: "Affiliate and split payouts",
                    description: "Creator and partner revenue shares with transparent statuses.",
                },
                {
                    id: 7,
                    title: "Attribution and analytics",
                    description: "View to cart, cart to purchase, and revenue by creator, clip, and placement.",
                },
                {
                    id: 8,
                    title: "Content CMS hooks",
                    description: "Tag once and reuse across channels with safe edits and expiries.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when buying happens in place.",
            items: [
                {
                    id: 1,
                    title: "Higher conversion",
                    description: "no redirects means fewer lost buyers on mobile",
                },
                {
                    id: 2,
                    title: "Revenue per session up",
                    description: "tags and upsells raise cart and checkout rates",
                },
                {
                    id: 3,
                    title: "Creator ROI proven",
                    description: "clip and placement level attribution secures budgets",
                },
                {
                    id: 4,
                    title: "Repeat sales",
                    description: "post purchase flows and perks lift LTV",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A clear path from catalog to measurable sales.",
            steps: [
                {
                    title: "Connect the catalog",
                    description: "Sync products, stock, and pricing rules."
                },
                {
                    title: "Tag media",
                    description: "Place products by timestamp or frame and choose overlays and prompts."
                },
                {
                    title: "Enable checkout",
                    description: "Turn on payments, tax, shipping or digital delivery, and confirmations."
                },
                {
                    title: "Wire follow ups",
                    description: "Send receipts, perks, and winbacks tied to what viewers bought."
                },
                {
                    title: "Measure and iterate",
                    description: "Track funnels, test offers, and tune placements each week."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show shoppable media is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Add to cart rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Checkout conversion up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Revenue per minute up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Refunds and failed payments down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Subscriber or email capture up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Low latency media, reliable commerce, explainable attribution.",
            items: [
                {
                    title: "Frontend",
                    description: "Shoppable player and gallery components with accessible overlays and fast interactions.",
                },
                {
                    title: "Backend",
                    description: "Services for products, carts, orders, entitlements, and payouts with idempotent APIs.",
                },
                {
                    title: "Media",
                    description: "Ingest, clipping, thumbnails, captions, and safe delivery links.",
                },
                {
                    title: "Payments",
                    description: "Cards, wallets, local methods, tax, invoices, refunds, and proration where needed.",
                },
                {
                    title: "Data",
                    description: "Event map for views, tags, carts, purchases, upsells, and attribution.",
                },
                {
                    title: "Integrations",
                    description: "Commerce systems, shipping, email or CRM, affiliate platforms, and analytics.",
                },
                {
                    title: "Ops",
                    description: "Feature flags, logs, alerts, and sandbox modes for safe tests.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that raise urgency and lifetime value.",
            items: [
                {
                    title: "Limited drops",
                    description: "Timers, inventory caps, and waitlists that create urgency.",
                },
                {
                    title: "Bundles and post purchase upsells",
                    description: "Auto pair items and offer upgrades after checkout.",
                },
                {
                    title: "AI clipping and highlights",
                    description: "Auto generate short clips with the right product pinned.",
                },
                {
                    title: "Creator affiliate links",
                    description: "Unique links and codes with tracked revenue and payouts.",
                },
                {
                    title: "Captions and translations",
                    description: "Accessibility and global reach for viewers.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Commerce grade trust and content safety. Optional SSO and roles for brands, agencies, and creators.",
            tags: [
                "PCI compliant payments",
                "encrypted PII",
                "audit logs for orders and refunds",
                "moderation tools for chat",
                "content safety filters"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Can we use our existing catalog and inventory?",
                    answer: "Yes. Sync products and stock from your commerce system and control pricing rules."
                },
                {
                    id: 2,
                    question: "Does this work on mobile first?",
                    answer: "Yes. Player, overlays, and checkout are optimized for phones and low bandwidth."
                },
                {
                    id: 3,
                    question: "How do we handle attribution across creators and clips?",
                    answer: "Purchases tie to timestamps, clips, and creators for clean reporting."
                },
                {
                    id: 4,
                    question: "What about returns and refunds?",
                    answer: "Orders and refunds flow through your commerce system with clear customer messaging."
                },
                {
                    id: 5,
                    question: "Do we need to stream only on our site?",
                    answer: "You can host on your site or embed. Simulcast and link out are supported when useful."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization Kit",
                    modalTitle: "Get the Monetization Kit"
                }
            }
        },
        seo: {
            title: "Shoppable media and live commerce for growth | Desilo",
            description: "Add product tagging, native checkout, post purchase flows, and clean attribution. Turn views into measurable revenue with low-latency media and reliable commerce.",
            keywords: "Shoppable media, live commerce, product tagging, native checkout, video commerce, social commerce, live shopping, e-commerce integration",
            ogTitle: "Shoppable media and live commerce for growth | Desilo",
            ogDescription: "Add product tagging, native checkout, post purchase flows, and clean attribution. Turn views into measurable revenue with low-latency media and reliable commerce.",
            twitterTitle: "Shoppable media and live commerce for growth | Desilo",
            twitterDescription: "Add product tagging, native checkout, post purchase flows, and clean attribution. Turn views into measurable revenue with low-latency media and reliable commerce."
        }
    },
    {
        slug: "subscriptions-membership-kernel",
        title: "Subscriptions and membership kernel",
        description: "Tiers, trials, bundles, entitlements, proration, tax, and refunds.",
        hero: {
            label: "USE CASE",
            title: "Subscriptions and membership kernel",
            description: "Tiers, trials, bundles, entitlements, proration, tax, and refunds.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization Kit",
                    modalTitle: "Get the Monetization Kit"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Teams turning audiences into durable, predictable revenue."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "The subscription stack that scales with your business model.",
            items: [
                {
                    id: 1,
                    title: "Tiers and plans",
                    description: "Monthly and annual options with perks that make value obvious.",
                },
                {
                    id: 2,
                    title: "Trials and intro offers",
                    description: "Free or paid trials and first-month prices that convert without confusion.",
                },
                {
                    id: 3,
                    title: "Bundles and add ons",
                    description: "Combine memberships with courses, communities, or tools to raise ARPU.",
                },
                {
                    id: 4,
                    title: "Entitlements",
                    description: "Granular access rules for content, features, roles, and seats by plan.",
                },
                {
                    id: 5,
                    title: "Proration and upgrades",
                    description: "Fair mid-cycle changes with credit and charge calculations your users understand.",
                },
                {
                    id: 6,
                    title: "Tax, invoices, and receipts",
                    description: "Region-aware tax, compliant invoices, and clear receipts for every charge.",
                },
                {
                    id: 7,
                    title: "Refunds and credits",
                    description: "Operable policies and flows that keep finance and support aligned.",
                },
                {
                    id: 8,
                    title: "Account self serve",
                    description: "Members can upgrade, pause, switch billing cycles, and update payment details.",
                },
                {
                    id: 9,
                    title: "Churn save and dunning",
                    description: "Grace periods, smart retries, card updater, and winback offers.",
                },
                {
                    id: 10,
                    title: "Analytics",
                    description: "Conversion, active subs, ARPU, LTV, and churn by cohort and plan.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when memberships have real infrastructure.",
            items: [
                {
                    id: 1,
                    title: "More conversion",
                    description: "clear plans and fair trials lift trials to paid",
                },
                {
                    id: 2,
                    title: "Lower churn",
                    description: "save paths and habit loops keep members longer",
                },
                {
                    id: 3,
                    title: "Cleaner finance",
                    description: "proration, tax, refunds, and invoices reduce exceptions",
                },
                {
                    id: 4,
                    title: "Higher LTV",
                    description: "bundles, add ons, and annual upgrades raise value per member",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A practical path from paywall to membership engine.",
            steps: [
                {
                    title: "Model the plans",
                    description: "Define tiers, perks, trials, and upgrade rules tied to value."
                },
                {
                    title: "Wire payments and tax",
                    description: "Turn on methods, region taxes, invoices, and receipts."
                },
                {
                    title: "Entitle the product",
                    description: "Map plans to features, roles, and content gates with logs."
                },
                {
                    title: "Design account flows",
                    description: "Make upgrades, pauses, and cancellations self serve and predictable."
                },
                {
                    title: "Add save and dunning",
                    description: "Configure retries, reminders, and fair offers that fit your policy."
                },
                {
                    title: "Measure and iterate",
                    description: "Track cohorts, test pricing or bundles, and keep winners."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show the kernel is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Trials to paid up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Active subscribers up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Involuntary churn down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Refund exceptions down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "ARPU and LTV up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Support tickets about billing down"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Reliable billing, accurate access, explainable numbers.",
            items: [
                {
                    title: "Frontend",
                    description: "Accessible plan selectors, comparison tables, checkout, and account pages.",
                },
                {
                    title: "Backend",
                    description: "Services for subscriptions, entitlements, invoices, refunds, and proration with idempotent APIs.",
                },
                {
                    title: "Payments",
                    description: "Cards, wallets, and local methods with tax engines and compliant invoices.",
                },
                {
                    title: "Data",
                    description: "Event map for signup, renewal, churn, perks, and usage with cohort dashboards.",
                },
                {
                    title: "Integrations",
                    description: "Email or CRM for lifecycle, community gates for roles, analytics, and finance systems.",
                },
                {
                    title: "Ops",
                    description: "Feature flags for pricing tests, logs and alerts, and runbooks for billing events.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that increase conversion and retention.",
            items: [
                {
                    title: "Annual upgrade nudges",
                    description: "Timed prompts that swap monthly to annual at fair value.",
                },
                {
                    title: "Gift and group plans",
                    description: "Corporate seats or family access with admin controls.",
                },
                {
                    title: "Referral credits",
                    description: "Give and get incentives tied to renewals and plan upgrades.",
                },
                {
                    title: "Founder or creator offers",
                    description: "Limited perks and lifetime tiers that reward early supporters.",
                },
                {
                    title: "Usage or credit packs",
                    description: "Metered features with prepaid credits and clear balances.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls finance and legal can sign off on. We implement your billing and data policies and provide evidence packs for reviews.",
            tags: [
                "Role based access",
                "audit logs",
                "encrypted PII",
                "compliant tax handling",
                "documented refund policies",
                "Optional SSO and SCIM for team accounts"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Can we migrate existing subscribers without disruption?",
                    answer: "Yes. Import plans, coupons, and renewal dates and honor entitlements."
                },
                {
                    id: 2,
                    question: "Do we need both monthly and annual from day one?",
                    answer: "Start with the plan that matches value clarity. Add annual once retention is steady."
                },
                {
                    id: 3,
                    question: "How do you handle mid-cycle upgrades?",
                    answer: "Proration credits and charges are calculated and shown before confirm."
                },
                {
                    id: 4,
                    question: "Can perks span content, community, and software features?",
                    answer: "Yes. Entitlements cover content gates, community roles, and feature flags."
                },
                {
                    id: 5,
                    question: "How do we reduce involuntary churn?",
                    answer: "Card updater, smart retries, and gentle reminders before expiry."
                },
                {
                    id: 6,
                    question: "Will finance get clean records?",
                    answer: "Invoices, taxes, refunds, and payouts are logged with exports and APIs."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization Kit",
                    modalTitle: "Get the Monetization Kit"
                }
            }
        },
        seo: {
            title: "Subscriptions and membership kernel for scale | Desilo",
            description: "Launch tiers, trials, bundles, entitlements, proration, tax, and refunds with clean UX and explainable billing. Increase conversion, reduce churn, and grow LTV with confidence.",
            keywords: "Subscription management, membership platform, recurring billing, subscription billing, membership tiers, subscription analytics, subscription optimization",
            ogTitle: "Subscriptions and membership kernel for scale | Desilo",
            ogDescription: "Launch tiers, trials, bundles, entitlements, proration, tax, and refunds with clean UX and explainable billing. Increase conversion, reduce churn, and grow LTV with confidence.",
            twitterTitle: "Subscriptions and membership kernel for scale | Desilo",
            twitterDescription: "Launch tiers, trials, bundles, entitlements, proration, tax, and refunds with clean UX and explainable billing. Increase conversion, reduce churn, and grow LTV with confidence."
        }
    },
    {
        slug: "global-payments-payouts-tax",
        title: "Global payments, payouts, and tax",
        description: "Multi processor routing, wallets, splits, escrow, and 1099, GST, TDS, and e invoice support.",
        hero: {
            label: "USE CASE",
            title: "Global payments, payouts, and tax",
            description: "Multi processor routing, wallets, splits, escrow, and 1099, GST, TDS, and e invoice support.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Fintech Playbook",
                    modalTitle: "Get the Fintech Playbook"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Platforms operating across countries and currencies."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "The money movement stack that scales globally.",
            items: [
                {
                    id: 1,
                    title: "Multi processor routing",
                    description: "Smartly route cards and wallets across PSPs to improve authorization and lower fees.",
                },
                {
                    id: 2,
                    title: "Local payment methods",
                    description: "UPI, netbanking, wallets, Pix, iDEAL, ACH, SEPA, and more based on region.",
                },
                {
                    id: 3,
                    title: "Multi currency wallets",
                    description: "Hold balances in local currencies and reduce FX surprises.",
                },
                {
                    id: 4,
                    title: "Revenue splits and escrow",
                    description: "Per deal splits, milestone holds, and controlled releases.",
                },
                {
                    id: 5,
                    title: "Refunds and disputes",
                    description: "Unified flows for refunds and chargebacks with evidence capture.",
                },
                {
                    id: 6,
                    title: "KYB, KYC, and sanctions checks",
                    description: "Onboard businesses and creators safely with audit trails.",
                },
                {
                    id: 7,
                    title: "Tax capture and docs",
                    description: "Collect W-9 or W-8, generate 1099, GST, TDS, and e invoices with exports.",
                },
                {
                    id: 8,
                    title: "Invoicing and receipts",
                    description: "Compliant invoices and line item receipts in the right currency and language.",
                },
                {
                    id: 9,
                    title: "Ledger and reconciliation",
                    description: "Double entry ledger, settlement views, and error queues that make month end calm.",
                },
                {
                    id: 10,
                    title: "Payouts",
                    description: "Programmable schedules to bank, UPI, wallet, or card with statuses and retries.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when payments run on rails.",
            items: [
                {
                    id: 1,
                    title: "Higher success rate",
                    description: "smart routing and local methods lift conversions",
                },
                {
                    id: 2,
                    title: "Predictable cash flow",
                    description: "reliable payouts increase creator and partner trust",
                },
                {
                    id: 3,
                    title: "Lower cost to serve",
                    description: "fewer failures, cleaner reconciliation, and faster close",
                },
                {
                    id: 4,
                    title: "Audit ready",
                    description: "tax docs, KYB and KYC records, and clear ledgers reduce risk",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A practical path from tangled providers to one set of rails.",
            steps: [
                {
                    title: "Map flows",
                    description: "Chart how funds enter, split, hold, and exit across regions and products."
                },
                {
                    title: "Connect providers",
                    description: "Plug in PSPs, banking as a service, KYC, tax, and invoicing partners."
                },
                {
                    title: "Configure rules",
                    description: "Set routing, retries, payout schedules, thresholds, and approvals by role."
                },
                {
                    title: "Enable ledgers",
                    description: "Record orders, fees, FX, splits, and payouts with clear references."
                },
                {
                    title: "Launch and monitor",
                    description: "Ship behind flags, watch auth rates and failures, and tune routing weekly."
                },
                {
                    title: "Report and file",
                    description: "Schedule tax reports, settlement exports, and finance packs for close."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show payments and tax are working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Authorization rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Failed payment rate down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "On time payout rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Finance close time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Chargeback loss down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Tax filing errors down"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Reliable routing, clean records, safe data.",
            items: [
                {
                    title: "Frontend",
                    description: "Accessible checkout, payout preferences, and invoice downloads.",
                },
                {
                    title: "Backend",
                    description: "Services for payments, wallets, splits, escrow, refunds, disputes, and payouts with idempotent APIs.",
                },
                {
                    title: "Routing and retries",
                    description: "Rules and fallbacks per BIN, region, and method with health checks.",
                },
                {
                    title: "Ledger",
                    description: "Double entry accounting, settlement matching, and reconciliation helpers.",
                },
                {
                    title: "Tax and invoicing",
                    description: "Engines for 1099, GST, TDS, e invoice formats, and region logic.",
                },
                {
                    title: "Data",
                    description: "Event map for orders, fees, FX, payouts, and disputes with warehouse sync.",
                },
                {
                    title: "Integrations",
                    description: "PSPs, banking partners, KYC, accounting, CRM, and ticketing.",
                },
                {
                    title: "Ops",
                    description: "Alerts on spikes, error queues, audit logs, and sandbox environments.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that raise conversion and trust.",
            items: [
                {
                    title: "Network tokenization",
                    description: "Reduce declines and improve card lifecycle management.",
                },
                {
                    title: "Smart FX",
                    description: "Quote and convert at checkout or payout with transparent fees.",
                },
                {
                    title: "Advance on earnings",
                    description: "Offer safe advances against booked deals with clear terms.",
                },
                {
                    title: "Prepaid or virtual cards",
                    description: "Scoped spend for campaign budgets and travel.",
                },
                {
                    title: "Affiliate payouts",
                    description: "Automate partner earnings with schedules and statements.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls finance and legal will sign off on.",
            tags: [
                "Role based access",
                "SSO and SCIM options",
                "encryption at rest and in transit",
                "signed webhooks",
                "PII vaulting",
                "complete audit trails"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Will this replace our current PSP?",
                    answer: "No. We can route across multiple PSPs and keep your existing contracts."
                },
                {
                    id: 2,
                    question: "How do we handle multi currency pricing and payouts?",
                    answer: "Use local pricing, hold balances in region, and settle with fair FX rules."
                },
                {
                    id: 3,
                    question: "Can we migrate existing subscriptions and invoices?",
                    answer: "Yes. Import customers, mandates, plans, and invoice history with minimal disruption."
                },
                {
                    id: 4,
                    question: "What regions and tax regimes are supported?",
                    answer: "We support common formats like 1099, GST, TDS, and e invoices and add markets via adapters."
                },
                {
                    id: 5,
                    question: "How do we keep reconciliation simple?",
                    answer: "Every movement is ledgered with references to orders, fees, FX, and payouts and exported to accounting."
                },
                {
                    id: 6,
                    question: "What happens during disputes?",
                    answer: "We queue chargebacks, collect evidence, and track outcomes with clear statuses."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Fintech Playbook",
                    modalTitle: "Get the Fintech Playbook"
                }
            }
        },
        seo: {
            title: "Global payments, payouts, and tax for scale | Desilo",
            description: "Add multi processor routing, local methods, wallets, splits, escrow, and compliant 1099, GST, TDS, and e invoices. Increase conversion, speed payouts, and close the books with confidence.",
            keywords: "Global payments, multi processor routing, international payments, payment processing, tax compliance, fintech, payment infrastructure, cross border payments",
            ogTitle: "Global payments, payouts, and tax for scale | Desilo",
            ogDescription: "Add multi processor routing, local methods, wallets, splits, escrow, and compliant 1099, GST, TDS, and e invoices. Increase conversion, speed payouts, and close the books with confidence.",
            twitterTitle: "Global payments, payouts, and tax for scale | Desilo",
            twitterDescription: "Add multi processor routing, local methods, wallets, splits, escrow, and compliant 1099, GST, TDS, and e invoices. Increase conversion, speed payouts, and close the books with confidence."
        }
    },
    {
        slug: "ai-content-pipeline",
        title: "AI content pipeline",
        description: "Auto cutdowns, captions, thumbnails, metadata, and multi variant testing at scale.",
        hero: {
            label: "USE CASE",
            title: "AI content pipeline",
            description: "Auto cutdowns, captions, thumbnails, metadata, and multi variant testing at scale.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Content Ops Playbook",
                    modalTitle: "Get the Content Ops Playbook"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Teams with lots of media and not enough hours."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "From master file to channel ready variants automatically.",
            items: [
                {
                    id: 1,
                    title: "Auto cutdowns",
                    description: "Detect highlights, chapters, and hooks and export short clips per channel spec.",
                },
                {
                    id: 2,
                    title: "Captions and translations",
                    description: "Accurate captions, subtitles, and multi language files with style rules.",
                },
                {
                    id: 3,
                    title: "Thumbnail generation",
                    description: "Pick frames, add safe overlays, and test options with clear winners.",
                },
                {
                    id: 4,
                    title: "Metadata and SEO",
                    description: "Write titles, tags, and descriptions that match search intent and platform rules.",
                },
                {
                    id: 5,
                    title: "Brand and policy checks",
                    description: "Logo, color, tone, and disclosure checks before publish.",
                },
                {
                    id: 6,
                    title: "Multi variant testing",
                    description: "Publish A and B on safe cohorts and promote winners automatically.",
                },
                {
                    id: 7,
                    title: "Channel formatting",
                    description: "Aspect ratios, durations, bitrates, and safe loudness per platform.",
                },
                {
                    id: 8,
                    title: "Content calendar and queues",
                    description: "Schedule variants per region and channel with dependencies.",
                },
                {
                    id: 9,
                    title: "Rights and expiries",
                    description: "Track usage windows and auto archive or swap assets.",
                },
                {
                    id: 10,
                    title: "Analytics hooks",
                    description: "Views, watch time, saves, CTR, and conversion tied back to each variant.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when packaging is automated.",
            items: [
                {
                    id: 1,
                    title: "More output",
                    description: "editors focus on creative while routine cuts happen automatically",
                },
                {
                    id: 2,
                    title: "Higher CTR and watch time",
                    description: "better hooks, captions, and thumbnails lift performance",
                },
                {
                    id: 3,
                    title: "Fewer misses",
                    description: "policy and brand checks reduce takedowns and rework",
                },
                {
                    id: 4,
                    title: "Clear learning loop",
                    description: "variant tests reveal what actually moves metrics",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A simple path from source to scaled distribution.",
            steps: [
                {
                    title: "Ingest and detect",
                    description: "Pull masters, detect chapters, speakers, and moments worth testing."
                },
                {
                    title: "Package and check",
                    description: "Generate captions, thumbnails, and metadata and run brand and policy checks."
                },
                {
                    title: "Format and schedule",
                    description: "Export per channel spec, attach UTMs, and queue the calendar."
                },
                {
                    title: "Test and promote",
                    description: "Launch A and B, watch early metrics, and roll forward winners."
                },
                {
                    title: "Measure and refine",
                    description: "Send results to dashboards and tune hooks, titles, and lengths weekly."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show the pipeline is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Time to publish down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Output per editor up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "CTR up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Average watch time up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Cost per view down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Conversion from media up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Reliable AI, explainable choices, calm operations.",
            items: [
                {
                    title: "Frontend",
                    description: "Operator console for cuts, captions, thumbnails, checks, and schedules.",
                },
                {
                    title: "Backend",
                    description: "Workers for detection, transcription, translation, rendering, and QC with retries.",
                },
                {
                    title: "Models",
                    description: "ASR for captions, vision for frame picking, embeddings for hook detection, and lightweight rankers for titles.",
                },
                {
                    title: "Rules and prompts",
                    description: "Brand tone, banned terms, and disclosure templates applied consistently.",
                },
                {
                    title: "Data",
                    description: "Event map for every variant with CTR, watch time, and downstream conversion.",
                },
                {
                    title: "Integrations",
                    description: "Storage, MAM, render farms, posting tools, link shorteners, email or CRM, and analytics.",
                },
                {
                    title: "Ops",
                    description: "Queues, quotas, alerts, and rollback for bad variants or model drift.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that raise quality and reach.",
            items: [
                {
                    title: "Template packs",
                    description: "Hook frameworks, title formulas, and lower thirds per channel.",
                },
                {
                    title: "Talent highlights",
                    description: "Auto reels per creator with best moments and safe quotes.",
                },
                {
                    title: "Topic clustering",
                    description: "Group clips by theme to build playlists and series.",
                },
                {
                    title: "Creator affiliate overlays",
                    description: "Dynamic codes and links rendered into variants.",
                },
                {
                    title: "Localization kit",
                    description: "Voiceover cloning, subtitles, and regional thumbnails.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls legal and brand will trust.",
            tags: [
                "Role based access",
                "encrypted assets",
                "signed webhooks",
                "audit logs",
                "rights windows",
                "Captions and overlays respect disclosure and copyright rules"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Will AI replace editors?",
                    answer: "No. AI handles repetitive packaging so editors focus on storytelling and quality."
                },
                {
                    id: 2,
                    question: "Can we lock brand rules?",
                    answer: "Yes. Colors, fonts, logos, tone, and disclosures are enforced with checks."
                },
                {
                    id: 3,
                    question: "How do we avoid spammy titles and thumbnails?",
                    answer: "Use templates with thresholds for clarity, not clickbait, and review early tests."
                },
                {
                    id: 4,
                    question: "Does this support short and long video?",
                    answer: "Yes. Cutdowns for shorts and packaging for longform with chapters and previews."
                },
                {
                    id: 5,
                    question: "Can we plug into our posting stack?",
                    answer: "We export to your scheduler or post directly with the same UTMs and tags."
                },
                {
                    id: 6,
                    question: "How do we measure lift from variants?",
                    answer: "Cohorted A and B tests with promotion rules and dashboards for CTR, watch time, and conversion."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Content Ops Playbook",
                    modalTitle: "Get the Content Ops Playbook"
                }
            }
        },
        seo: {
            title: "AI content pipeline for scale | Desilo",
            description: "Auto cutdowns, captions, thumbnails, metadata, and multi variant testing. Increase output, lift CTR and watch time, and ship on schedule with brand safe automation.",
            keywords: "AI content pipeline, automated video editing, content automation, video processing, AI captions, thumbnail generation, content optimization, video marketing",
            ogTitle: "AI content pipeline for scale | Desilo",
            ogDescription: "Auto cutdowns, captions, thumbnails, metadata, and multi variant testing. Increase output, lift CTR and watch time, and ship on schedule with brand safe automation.",
            twitterTitle: "AI content pipeline for scale | Desilo",
            twitterDescription: "Auto cutdowns, captions, thumbnails, metadata, and multi variant testing. Increase output, lift CTR and watch time, and ship on schedule with brand safe automation."
        }
    },
    {
        slug: "ai-safety-brand-suitability",
        title: "AI safety and brand suitability",
        description: "Multimodal moderation, IP detection, disclosure checks, and policy guardrails.",
        hero: {
            label: "USE CASE",
            title: "AI safety and brand suitability",
            description: "Multimodal moderation, IP detection, disclosure checks, and policy guardrails.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Safety Playbook",
                    modalTitle: "Get the Safety Playbook"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Teams shipping AI features or UGC at scale who must protect brand and users."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "End to end safety that is multimodal, explainable, and fast.",
            items: [
                {
                    id: 1,
                    title: "Multimodal moderation",
                    description: "Classify text, images, audio, and video for hate, harassment, sexual content, self harm, violence, and spam with confidence scores.",
                },
                {
                    id: 2,
                    title: "IP and likeness detection",
                    description: "Check for copyrighted media, brand marks, and faces with similarity search and human review paths.",
                },
                {
                    id: 3,
                    title: "Disclosure and ad policy checks",
                    description: "FTC disclosure prompts, branded content tags, and platform specific rules verified at submit and post.",
                },
                {
                    id: 4,
                    title: "Policy guardrails",
                    description: "Rule packs per market and client that block, warn, or throttle risky content.",
                },
                {
                    id: 5,
                    title: "Context aware filters",
                    description: "Understand intent and setting to reduce false positives for news, education, or art.",
                },
                {
                    id: 6,
                    title: "User controls and appeals",
                    description: "Explain decisions, allow appeal, and track outcomes to improve precision.",
                },
                {
                    id: 7,
                    title: "Publisher and model gates",
                    description: "Control which AI models, prompts, and outputs are allowed in each surface.",
                },
                {
                    id: 8,
                    title: "Incident logging",
                    description: "Immutable records with evidence files, timestamps, and reviewer notes.",
                },
                {
                    id: 9,
                    title: "Dashboards and alerts",
                    description: "Live risk views, drift monitors, and SLA queues for human review.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when safety is built in.",
            items: [
                {
                    id: 1,
                    title: "Fewer incidents",
                    description: "risky posts and model outputs are caught before harm",
                },
                {
                    id: 2,
                    title: "Lower review load",
                    description: "automation triages routine cases so humans handle edge cases",
                },
                {
                    id: 3,
                    title: "Protected revenue",
                    description: "brand suitability boosts advertiser confidence and CPMs",
                },
                {
                    id: 4,
                    title: "Audit ready",
                    description: "clear rules, evidence packs, and logs reduce legal risk",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A practical path from policy to production.",
            steps: [
                {
                    title: "Define policy",
                    description: "Codify brand, regulatory, and platform rules by market and use case."
                },
                {
                    title: "Instrument the funnel",
                    description: "Add checks at upload, edit, and publish with clear user messages."
                },
                {
                    title: "Tune models and thresholds",
                    description: "Pick base models, add rules, set confidence thresholds, and test with golden sets."
                },
                {
                    title: "Route for review",
                    description: "Send borderline items to queues with context, evidence, and SLAs."
                },
                {
                    title: "Close the loop",
                    description: "Capture decisions and appeals and retrain or retune based on outcomes."
                },
                {
                    title: "Report and improve",
                    description: "Share incident trends and adjust rules and thresholds with stakeholders."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show safety is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Violations caught pre publish up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "False positive rate down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Human review time per item down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Policy escalations down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Brand suitability score up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Explainable checks, strong evidence, calm operations.",
            items: [
                {
                    title: "Frontend",
                    description: "Clear inline messages, disclosure prompts, and appeal flows that respect users.",
                },
                {
                    title: "Backend",
                    description: "Services for classification, rules, queues, and incidents with idempotent APIs.",
                },
                {
                    title: "Models",
                    description: "Text and vision models for policy categories, OCR for text in images, ASR for audio.",
                },
                {
                    title: "Similarity and IP",
                    description: "Perceptual hashes and embedding search for logos, marks, and near duplicates.",
                },
                {
                    title: "Rules engine",
                    description: "Deterministic guardrails layered on model outputs with versioned policies.",
                },
                {
                    title: "Data",
                    description: "Evidence storage, signed URLs, audit logs, and dataset versioning for tests.",
                },
                {
                    title: "Observability",
                    description: "Drift monitors, threshold health, queue depth, and SLA dashboards.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that reduce risk and improve precision.",
            items: [
                {
                    title: "Regional policy packs",
                    description: "Prebuilt rules for USA, EU, India, and other markets with translation notes.",
                },
                {
                    title: "Advertiser suitability labels",
                    description: "Tiered labels mapped to ad stack for safe targeting.",
                },
                {
                    title: "Creator safety brief",
                    description: "Shareable guidelines and checklists per platform.",
                },
                {
                    title: "Watermark and provenance checks",
                    description: "Detect AI watermarks and attach provenance metadata to outputs.",
                },
                {
                    title: "Human review network",
                    description: "On demand reviewers with calibrated rubrics and secure environments.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls legal and partners will trust.",
            tags: [
                "Role based access",
                "SSO and SCIM options",
                "encryption at rest and in transit",
                "PII minimization",
                "signed webhooks"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Will this over block creators?",
                    answer: "We tune thresholds per surface, explain decisions, and allow appeals to reduce false positives."
                },
                {
                    id: 2,
                    question: "Can policies differ by client or region?",
                    answer: "Yes. Rules and disclosures vary by market and campaign and are versioned."
                },
                {
                    id: 3,
                    question: "Do we check AI generated outputs too?",
                    answer: "Yes. Guardrails validate prompts and outputs including images, audio, and video."
                },
                {
                    id: 4,
                    question: "How do we handle copyrighted logos or music?",
                    answer: "Similarity search and rights lists flag matches and route to review with evidence."
                },
                {
                    id: 5,
                    question: "What happens when models drift?",
                    answer: "Monitors catch drift, and we retrain or swap models with safe rollbacks."
                },
                {
                    id: 6,
                    question: "Can advertisers see suitability scores?",
                    answer: "Optional labels and dashboards expose suitability while protecting user privacy."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Safety Playbook",
                    modalTitle: "Get the Safety Playbook"
                }
            }
        },
        seo: {
            title: "AI safety and brand suitability for scale | Desilo",
            description: "Add multimodal moderation, IP detection, disclosure checks, and policy guardrails. Reduce incidents, protect revenue, and stay audit ready with explainable, operational safety.",
            keywords: "AI safety, brand suitability, content moderation, AI safety tools, brand protection, content safety, AI governance, multimodal moderation",
            ogTitle: "AI safety and brand suitability for scale | Desilo",
            ogDescription: "Add multimodal moderation, IP detection, disclosure checks, and policy guardrails. Reduce incidents, protect revenue, and stay audit ready with explainable, operational safety.",
            twitterTitle: "AI safety and brand suitability for scale | Desilo",
            twitterDescription: "Add multimodal moderation, IP detection, disclosure checks, and policy guardrails. Reduce incidents, protect revenue, and stay audit ready with explainable, operational safety."
        }
    },
    {
        slug: "cloud-migration-cost-optimization",
        title: "Cloud migration and cost optimization",
        description: "Replatform to AWS or GCP with blue green deploys, autoscaling, and FinOps that keeps spend in check.",
        hero: {
            label: "USE CASE",
            title: "Cloud migration and cost optimization",
            description: "Replatform to AWS or GCP with blue green deploys, autoscaling, and FinOps that keeps spend in check.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Cloud Migration Checklist",
                    modalTitle: "Get the Cloud Migration Checklist"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Teams ready to modernize infrastructure without slowing product."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Modern cloud foundations with reliability and cost controls.",
            items: [
                {
                    id: 1,
                    title: "Containerization and orchestration",
                    description: "Docker and Kubernetes or managed containers with autoscaling and health checks.",
                },
                {
                    id: 2,
                    title: "Blue green and canary deploys",
                    description: "Safe releases with instant rollback and traffic shifting.",
                },
                {
                    id: 3,
                    title: "Managed databases and caching",
                    description: "RDS or Cloud SQL, managed Redis, and read replicas with zero downtime cutovers.",
                },
                {
                    id: 4,
                    title: "Object storage and CDN",
                    description: "S3 or GCS with CloudFront or Cloud CDN and smart cache policies.",
                },
                {
                    id: 5,
                    title: "Observability stack",
                    description: "Metrics, logs, traces, dashboards, and SLOs wired before go live.",
                },
                {
                    id: 6,
                    title: "Infrastructure as code",
                    description: "Terraform and pipelines so environments are reproducible and auditable.",
                },
                {
                    id: 7,
                    title: "FinOps and cost allocation",
                    description: "Tags, budgets, alerts, and dashboards for service and team level spend.",
                },
                {
                    id: 8,
                    title: "Resilience and backups",
                    description: "Multi AZ, backups, PITR, disaster recovery plans, and runbooks.",
                },
                {
                    id: 9,
                    title: "Security baseline",
                    description: "VPCs, IAM least privilege, KMS, secrets management, and vulnerability scanning.",
                },
                {
                    id: 10,
                    title: "Data pipelines",
                    description: "ETL to BigQuery or Redshift with schema versioning and freshness monitors.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when cloud is engineered, not improvised.",
            items: [
                {
                    id: 1,
                    title: "Higher reliability",
                    description: "safer deploys and autoscaling reduce incidents and pager fatigue",
                },
                {
                    id: 2,
                    title: "Faster shipping",
                    description: "pipelines, IaC, and predictable releases speed feature velocity",
                },
                {
                    id: 3,
                    title: "Lower cost to serve",
                    description: "rightsizing, spot where safe, and FinOps trim waste",
                },
                {
                    id: 4,
                    title: "Bigger deals",
                    description: "security baselines and uptime evidence unlock enterprise",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A phased path that avoids big bang rewrites.",
            steps: [
                {
                    title: "Assess and model",
                    description: "Map services, data, traffic, SLAs, and current cost. Choose AWS or GCP targets."
                },
                {
                    title: "Design the target architecture",
                    description: "Pick managed services, networking, observability, and security patterns."
                },
                {
                    title: "Prepare the runway",
                    description: "Set up accounts, org policies, Terraform, CI and CD, and tagging standards."
                },
                {
                    title: "Migrate safely",
                    description: "Containerize, add health checks, run dual writes or CDC, and cut over with blue green."
                },
                {
                    title: "Wire observability and FinOps",
                    description: "Dashboards for latency, errors, saturation, and cost with budgets and alerts."
                },
                {
                    title: "Harden and optimize",
                    description: "Rightsize, add autoscaling rules, cache where it pays, and negotiate committed use."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show migration and FinOps are working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Deployment lead time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Change failure rate down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Cost per request down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Error budget burn down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "MTTR down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Forecast accuracy up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Proven patterns, measurable reliability, explainable spend.",
            items: [
                {
                    title: "Runtime",
                    description: "EKS, GKE, or Cloud Run with autoscaling, HPA, and pod disruption budgets.",
                },
                {
                    title: "Data",
                    description: "RDS or Cloud SQL migrations with DMS or Datastream and online cutovers.",
                },
                {
                    title: "Networking",
                    description: "Load balancers, private subnets, NAT, WAF, and service mesh where useful.",
                },
                {
                    title: "CI and CD",
                    description: "Branch builds, artifact registries, staged releases, and policy checks.",
                },
                {
                    title: "Observability",
                    description: "Prometheus compatible metrics, OpenTelemetry traces, log retention, and SLO alerts.",
                },
                {
                    title: "FinOps",
                    description: "Cost tags, anomaly detection, unit economics dashboards, and savings plans or committed use.",
                },
                {
                    title: "Security",
                    description: "IAM guardrails, KMS, secret stores, container scanning, and regular posture reviews.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that compound reliability and savings.",
            items: [
                {
                    title: "Performance tuning",
                    description: "Profiling, query plans, indexes, and cache keys that cut latency.",
                },
                {
                    title: "Spot and priority mixes",
                    description: "Safe spot usage for workers with graceful drain and fallback.",
                },
                {
                    title: "Queue and job redesign",
                    description: "Idempotent workers with retries and dead letter queues.",
                },
                {
                    title: "Autoscaling policies",
                    description: "CPU and custom metrics based scaling with warm pools.",
                },
                {
                    title: "Cost showback",
                    description: "Team or product level cost reports and budgets.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls enterprises expect, without slowing teams.",
            tags: [
                "Least privilege IAM",
                "SSO and SCIM",
                "VPC peering",
                "encryption in transit and at rest",
                "private images",
                "signed releases",
                "change logs"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Do we have to rewrite the whole app?",
                    answer: "No. We phase by service and data cutovers with blue green and canary releases."
                },
                {
                    id: 2,
                    question: "How do we avoid downtime during database migration?",
                    answer: "Use CDC or dual writes, verify parity, and switch traffic with quick rollback."
                },
                {
                    id: 3,
                    question: "Will Kubernetes be overkill for us?",
                    answer: "We choose managed containers or serverless when it keeps ops simple."
                },
                {
                    id: 4,
                    question: "How do we keep costs from creeping back up?",
                    answer: "Tags, budgets, alerts, and monthly rightsizing keep spend accountable."
                },
                {
                    id: 5,
                    question: "Can we keep parts of our current stack?",
                    answer: "Yes. Hybrid patterns and VPNs or interconnects bridge during transition."
                },
                {
                    id: 6,
                    question: "How fast to first cutover?",
                    answer: "After a short assessment, a first service can move in weeks while baselines are set."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Cloud Migration Checklist",
                    modalTitle: "Get the Cloud Migration Checklist"
                }
            }
        },
        seo: {
            title: "Cloud migration and cost optimization for scale | Desilo",
            description: "Replatform to AWS or GCP with blue green deploys, autoscaling, and FinOps. Increase reliability, ship faster, and reduce cost to serve with measurable guardrails.",
            keywords: "cloud migration, AWS migration, GCP migration, cloud cost optimization, FinOps, infrastructure as code, containerization, cloud architecture",
            ogTitle: "Cloud migration and cost optimization for scale | Desilo",
            ogDescription: "Replatform to AWS or GCP with blue green deploys, autoscaling, and FinOps. Increase reliability, ship faster, and reduce cost to serve with measurable guardrails.",
            twitterTitle: "Cloud migration and cost optimization for scale | Desilo",
            twitterDescription: "Replatform to AWS or GCP with blue green deploys, autoscaling, and FinOps. Increase reliability, ship faster, and reduce cost to serve with measurable guardrails."
        }
    },
    {
        slug: "data-warehouse-cdc-unification",
        title: "Data warehouse and CDC unification",
        description: "BigQuery or Snowflake with clean event schema, reverse ETL, and self serve BI.",
        hero: {
            label: "USE CASE",
            title: "Data warehouse and CDC unification",
            description: "BigQuery or Snowflake with clean event schema, reverse ETL, and self serve BI.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Data Stack Checklist",
                    modalTitle: "Get the Data Stack Checklist"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Teams ready to trust their numbers and move faster."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "One warehouse, one language, many reliable views.",
            items: [
                {
                    id: 1,
                    title: "Warehouse on BigQuery or Snowflake",
                    description: "A central store with sensible cost controls and role based access.",
                },
                {
                    id: 2,
                    title: "Change data capture pipelines",
                    description: "Stream app and DB changes with CDC so facts stay fresh without heavy loads.",
                },
                {
                    id: 3,
                    title: "Unified event schema",
                    description: "A single taxonomy for sessions, clicks, purchases, payouts, and more.",
                },
                {
                    id: 4,
                    title: "Modeled tables",
                    description: "Clean dimensions and facts for product, marketing, finance, and support.",
                },
                {
                    id: 5,
                    title: "Reverse ETL",
                    description: "Sync modeled fields back to tools like CRM, email, ads, and support.",
                },
                {
                    id: 6,
                    title: "Identity resolution",
                    description: "Merge anonymous, logged in, and cross device events into one user.",
                },
                {
                    id: 7,
                    title: "Quality checks and lineage",
                    description: "Tests, expectations, and lineage maps so issues are visible and fixable.",
                },
                {
                    id: 8,
                    title: "Cost and freshness monitors",
                    description: "Budgets, alerts, and SLAs for pipelines and scheduled jobs.",
                },
                {
                    id: 9,
                    title: "Self serve BI",
                    description: "Dashboards and governed metrics that non analysts can explore safely.",
                },
                {
                    id: 10,
                    title: "Data privacy and retention",
                    description: "Policies and tools for consent, delete requests, and region rules.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when data agrees.",
            items: [
                {
                    id: 1,
                    title: "Less chaos",
                    description: "one source of truth beats spreadsheets and screenshot wars",
                },
                {
                    id: 2,
                    title: "Higher win rate",
                    description: "faster, trusted insights power better experiments and spend",
                },
                {
                    id: 3,
                    title: "More capacity",
                    description: "engineers ship product while analysts stop chasing mismatches",
                },
                {
                    id: 4,
                    title: "Trusted results",
                    description: "governed metrics and lineage make reviews and audits calm",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A practical path from scattered data to governed insight.",
            steps: [
                {
                    title: "Assess and map",
                    description: "Inventory sources, tables, key metrics, and stakeholders."
                },
                {
                    title: "Choose the target",
                    description: "Pick BigQuery or Snowflake, organize projects, roles, and budgets."
                },
                {
                    title: "Stand up CDC",
                    description: "Stream changes from databases and services with safe checkpoints."
                },
                {
                    title: "Define the schema",
                    description: "Write the event and entity contracts and backfill historical data."
                },
                {
                    title: "Model and test",
                    description: "Build facts and dimensions with tests, docs, and lineage maps."
                },
                {
                    title: "Activate and govern",
                    description: "Wire reverse ETL, publish metrics, and set SLAs for freshness and cost."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show unification is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Dashboard prep time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Metric mismatches down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Freshness SLA hit rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Experiment cycle time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Forecast accuracy up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Reliable pipelines, documented models, explainable spend.",
            items: [
                {
                    title: "Ingest",
                    description: "CDC from OLTP, event collectors, and third parties with idempotent loads.",
                },
                {
                    title: "Storage",
                    description: "Partitioning and clustering for performance with row and column access controls.",
                },
                {
                    title: "Transform",
                    description: "Versioned SQL models with tests, documentation, and environments.",
                },
                {
                    title: "Orchestration",
                    description: "Pipelines with retries, backfills, and dependency aware scheduling.",
                },
                {
                    title: "Activation",
                    description: "Reverse ETL to CRM, email, ads, and support with sync logs.",
                },
                {
                    title: "Observability",
                    description: "Data quality tests, freshness checks, lineage graphs, and cost dashboards.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that compound clarity and actionability.",
            items: [
                {
                    title: "Metrics layer",
                    description: "A governed definitions layer so \"active user\" means one thing everywhere.",
                },
                {
                    title: "Attribution models",
                    description: "Rule based or MMM friendly tables for marketing and growth.",
                },
                {
                    title: "Finance pack",
                    description: "Revenue, refunds, fees, FX, and payouts reconciled for close.",
                },
                {
                    title: "Privacy ops",
                    description: "Consent tables, delete queues, and audit exports.",
                },
                {
                    title: "Experimentation hub",
                    description: "Prebuilt tables for tests, cohorts, and guardrail metrics.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls legal, finance, and IT will sign off on.",
            tags: [
                "Least privilege roles",
                "SSO and SCIM",
                "encryption at rest and in transit",
                "masked columns for PII",
                "audit logs",
                "documented retention"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Do we need to rewrite our trackers?",
                    answer: "Not always. We can map current events to the new schema and close gaps over time."
                },
                {
                    id: 2,
                    question: "Can teams keep their BI tool?",
                    answer: "Yes. We feed governed tables to your BI and keep a shared metrics layer."
                },
                {
                    id: 3,
                    question: "How do we control warehouse costs?",
                    answer: "Partitioning, caching, query limits, and alerts keep spend predictable."
                },
                {
                    id: 4,
                    question: "What if our production DB is sensitive to load?",
                    answer: "CDC streams changes with minimal impact and recoverable checkpoints."
                },
                {
                    id: 5,
                    question: "How fast until dashboards align?",
                    answer: "After schema and the first modeled tables land, core metrics usually align in the first release window."
                },
                {
                    id: 6,
                    question: "Will reverse ETL cause data loops?",
                    answer: "We use scoped fields, sync logs, and dedupe keys to avoid feedback loops."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Data Stack Checklist",
                    modalTitle: "Get the Data Stack Checklist"
                }
            }
        },
        seo: {
            title: "Data warehouse and CDC unification for scale | Desilo",
            description: "Stand up BigQuery or Snowflake with unified event schema, CDC pipelines, reverse ETL, and self serve BI. End dashboard debates and ship faster with governed data.",
            keywords: "data warehouse, BigQuery, Snowflake, CDC, data pipeline, reverse ETL, data governance, business intelligence, data analytics",
            ogTitle: "Data warehouse and CDC unification for scale | Desilo",
            ogDescription: "Stand up BigQuery or Snowflake with unified event schema, CDC pipelines, reverse ETL, and self serve BI. End dashboard debates and ship faster with governed data.",
            twitterTitle: "Data warehouse and CDC unification for scale | Desilo",
            twitterDescription: "Stand up BigQuery or Snowflake with unified event schema, CDC pipelines, reverse ETL, and self serve BI. End dashboard debates and ship faster with governed data."
        }
    }
];

// ============================================================================
// CREATOR ICP - Solo creators, studios, and creator managers
// ============================================================================
const creatorUseCases: UseCaseData[] = [
    {
        slug: "posting-os-content-cadence",
        title: "Posting OS and content cadence",
        description: "Queues, calendars, idea vaults, approvals, and auto publish so output stays steady without last minute scramble.",
        hero: {
            label: "USE CASE",
            title: "Posting OS and content cadence",
            description: "Queues, calendars, idea vaults, approvals, and auto publish so output stays steady without last minute scramble.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Posting Playbook",
                    modalTitle: "Get the Posting Playbook"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Creators and small teams who want consistent output without chaos."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "A lightweight OS from idea to scheduled post.",
            items: [
                {
                    id: 1,
                    title: "Idea vault",
                    description: "Capture hooks, topics, and references with tags and quick search.",
                },
                {
                    id: 2,
                    title: "Content queues",
                    description: "Per channel queues that keep a healthy buffer of ready posts.",
                },
                {
                    id: 3,
                    title: "Calendars",
                    description: "Month and week views with drag and drop and per platform filters.",
                },
                {
                    id: 4,
                    title: "Templates and frameworks",
                    description: "Reusable post shells, caption patterns, and thumbnail notes that fit your voice.",
                },
                {
                    id: 5,
                    title: "Approval flows",
                    description: "Assign reviewers, collect comments, and mark ready to schedule.",
                },
                {
                    id: 6,
                    title: "Auto publish",
                    description: "Schedule once and publish to multiple platforms with safe throttles.",
                },
                {
                    id: 7,
                    title: "Asset library",
                    description: "Central files with versions, rights info, and quick resize or crop.",
                },
                {
                    id: 8,
                    title: "Checklists and disclosures",
                    description: "Required fields and FTC prompts baked into the post card.",
                },
                {
                    id: 9,
                    title: "UTMs and link routing",
                    description: "Per platform links with tracking so results are clear.",
                },
                {
                    id: 10,
                    title: "Performance context",
                    description: "Benchmarks and best times surfaced next to the card.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when cadence is consistent.",
            items: [
                {
                    id: 1,
                    title: "Less chaos",
                    description: "one source of truth beats chats and scattered notes",
                },
                {
                    id: 2,
                    title: "Higher quality",
                    description: "templates and reviews raise hit rate without slowing you down",
                },
                {
                    id: 3,
                    title: "More output",
                    description: "a steady buffer prevents gaps and late nights",
                },
                {
                    id: 4,
                    title: "Trusted results",
                    description: "clean tracking shows what works and feeds the next plan",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A simple path from raw ideas to scheduled consistency.",
            steps: [
                {
                    title: "Collect and sort",
                    description: "Drop ideas in the vault and tag by theme, format, and channel."
                },
                {
                    title: "Plan the calendar",
                    description: "Pick slots, balance themes, and set targets for the week."
                },
                {
                    title: "Draft and review",
                    description: "Use templates, attach assets, and gather quick comments."
                },
                {
                    title: "Schedule and publish",
                    description: "Queue posts per channel with time zone and best time helpers."
                },
                {
                    title: "Measure and refine",
                    description: "Check results, keep winners, and update templates every week."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show the OS is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "On time posts up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Buffer size healthy"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Last minute edits down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Weekly output up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "CTR or watch time up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Brand and brief misses down"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Fast UI, safe publishes, clear data.",
            items: [
                {
                    title: "Frontend",
                    description: "Calendars, boards, and side by side preview with quick edits.",
                },
                {
                    title: "Backend",
                    description: "Services for posts, assets, approvals, schedules, and links with retries.",
                },
                {
                    title: "Publishing",
                    description: "API adapters per platform with throttles, error queues, and status checks.",
                },
                {
                    title: "Data",
                    description: "Events for drafts, approvals, schedules, and performance with dashboards.",
                },
                {
                    title: "Integrations",
                    description: "Drive or cloud storage, link shortener, email or CRM, and your analytics.",
                },
                {
                    title: "Ops",
                    description: "Feature flags, logs, alerts, and role controls for collaborators.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that raise quality and reduce effort.",
            items: [
                {
                    title: "AI caption and title helper",
                    description: "On brand drafts and variations with alt text suggestions.",
                },
                {
                    title: "Thumbnail kit",
                    description: "Preset layouts and safe overlays for faster packaging.",
                },
                {
                    title: "Topic and hook library",
                    description: "Reusable prompts and angles mapped to goals.",
                },
                {
                    title: "Best time recommender",
                    description: "Suggests slots by audience and channel from your data.",
                },
                {
                    title: "Client or partner portal",
                    description: "Scoped approvals when sponsors or collaborators are involved.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls that protect your accounts and data.",
            tags: [
                "Role based access",
                "SSO options",
                "encrypted assets",
                "audit logs",
                "scoped publisher permissions",
                "Rights metadata travels with files",
                "deletion and export controls are clear"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Will this replace my current posting tool?",
                    answer: "You can publish here or export to the tool you use today."
                },
                {
                    id: 2,
                    question: "Can I manage multiple channels and formats?",
                    answer: "Yes. Queues and templates are per platform with shared assets."
                },
                {
                    id: 3,
                    question: "Do I have to approve every post?",
                    answer: "Set rules. Solo creators can auto publish; teams can require review."
                },
                {
                    id: 4,
                    question: "What if a post fails to publish?",
                    answer: "Retries and alerts trigger with a safe queue for quick fixes."
                },
                {
                    id: 5,
                    question: "Can sponsors review before publish?",
                    answer: "Yes. Invite scoped reviewers to comment and approve specific cards."
                },
                {
                    id: 6,
                    question: "How fast can we pilot?",
                    answer: "A short setup, then your first week of scheduled posts usually goes live quickly."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Posting Playbook",
                    modalTitle: "Get the Posting Playbook"
                }
            }
        },
        seo: {
            title: "Posting OS and content cadence for creators | Desilo",
            description: "Launch queues, calendars, idea vaults, approvals, and auto publish. Keep output steady, reduce scramble, and learn what works with clean tracking.",
            keywords: "content creation, posting schedule, content calendar, creator tools, social media management, content planning, creator workflow",
            ogTitle: "Posting OS and content cadence for creators | Desilo",
            ogDescription: "Launch queues, calendars, idea vaults, approvals, and auto publish. Keep output steady, reduce scramble, and learn what works with clean tracking.",
            twitterTitle: "Posting OS and content cadence for creators | Desilo",
            twitterDescription: "Launch queues, calendars, idea vaults, approvals, and auto publish. Keep output steady, reduce scramble, and learn what works with clean tracking."
        }
    },
    {
        slug: "monetization-subscriber-hubs",
        title: "Monetization and subscriber hubs",
        description: "Tiers, perks, paywalls, and member DMs that turn fans into reliable monthly revenue.",
        hero: {
            label: "USE CASE",
            title: "Monetization and subscriber hubs",
            description: "Tiers, perks, paywalls, and member DMs that turn fans into reliable monthly revenue.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization Kit",
                    modalTitle: "Get the Monetization Kit"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Creators and studios ready to turn audience into members."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Everything needed to convert followers into paying members.",
            items: [
                {
                    id: 1,
                    title: "Membership tiers",
                    description: "Clear plans with monthly and annual options and perks that make value obvious.",
                },
                {
                    id: 2,
                    title: "Paywalls and trials",
                    description: "Previews, meters, and fair trials that lift trials to paid without hurting discovery.",
                },
                {
                    id: 3,
                    title: "Perks library",
                    description: "Member posts, drops, discounts, and early access mapped to each tier.",
                },
                {
                    id: 4,
                    title: "Member DMs and inbox",
                    description: "Private messages and group threads so high tiers feel close and cared for.",
                },
                {
                    id: 5,
                    title: "Live sessions and events",
                    description: "Member only AMAs, workshops, and replays with reminders and calendars.",
                },
                {
                    id: 6,
                    title: "Courses and downloads",
                    description: "Gate lessons, presets, and packs with entitlements and expiry rules.",
                },
                {
                    id: 7,
                    title: "Community spaces",
                    description: "Gated channels and roles that match tiers and reward participation.",
                },
                {
                    id: 8,
                    title: "Checkout and receipts",
                    description: "Fast, compliant checkout with invoices and proration that feels fair.",
                },
                {
                    id: 9,
                    title: "Account self serve",
                    description: "Upgrade, pause, switch billing cycles, and update payment details.",
                },
                {
                    id: 10,
                    title: "Analytics",
                    description: "Trials to paid, churn, ARPU, LTV, and perk usage by cohort and tier.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The outcomes that improve when fans become members.",
            items: [
                {
                    id: 1,
                    title: "Predictable revenue",
                    description: "subscriptions smooth the spikes and fund bigger bets",
                },
                {
                    id: 2,
                    title: "Lower churn",
                    description: "real perks, DMs, and events keep members engaged",
                },
                {
                    id: 3,
                    title: "Higher LTV",
                    description: "bundles and annual plans raise value per member",
                },
                {
                    id: 4,
                    title: "Owned growth",
                    description: "you keep the relationship and the data, not just the reach",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A practical path from audience to paid community.",
            steps: [
                {
                    title: "Model the tiers",
                    description: "Define perks, trials, and upgrade rules tied to what members value."
                },
                {
                    title: "Set the gates",
                    description: "Choose paywalls, meters, and member spaces with clear previews."
                },
                {
                    title: "Wire payments",
                    description: "Turn on methods, tax, invoices, and proration with clean receipts."
                },
                {
                    title: "Launch the hub",
                    description: "Publish the membership page, perks calendar, and welcome flow."
                },
                {
                    title: "Program the first 90 days",
                    description: "Plan drops, DMs, and events that create early wins and habit."
                },
                {
                    title: "Measure and iterate",
                    description: "Track cohorts, test offers, and tune perks based on what members use."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show your hub is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Trials to paid up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Active members up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Churn down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "ARPU and LTV up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Support tickets about access down"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Reliable billing, accurate access, calm operations.",
            items: [
                {
                    title: "Frontend",
                    description: "Accessible plan selectors, paywall prompts, perk pages, and member inbox.",
                },
                {
                    title: "Backend",
                    description: "Subscriptions, entitlements, perks, and messaging with idempotent APIs.",
                },
                {
                    title: "Payments",
                    description: "Cards, wallets, and local methods with tax engines and compliant invoices.",
                },
                {
                    title: "Data",
                    description: "Events for signup, renewals, perks, DMs, and churn with cohort dashboards.",
                },
                {
                    title: "Integrations",
                    description: "Email or CRM for lifecycle, community gates for roles, analytics, and commerce.",
                },
                {
                    title: "Ops",
                    description: "Feature flags for pricing tests, logs and alerts, and runbooks for billing events.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that raise conversion and retention.",
            items: [
                {
                    title: "Annual upgrade nudges",
                    description: "Smart prompts that move monthly members to annual at fair value.",
                },
                {
                    title: "Gift and group plans",
                    description: "Gifting and team seats with admin controls.",
                },
                {
                    title: "Referral credits",
                    description: "Give and get incentives that drive quality growth.",
                },
                {
                    title: "Creator affiliate offers",
                    description: "Partner perks that add value without heavy lift.",
                },
                {
                    title: "Member surveys",
                    description: "Light pulses that guide the next perk and event schedule.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls that protect members and your brand.",
            tags: [
                "Role based access",
                "encrypted PII",
                "audit logs",
                "clear retention policies",
                "SSO and SCIM are available for team accounts"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Will a paywall hurt my reach?",
                    answer: "Use previews and meters so discovery continues while members get full value."
                },
                {
                    id: 2,
                    question: "Can DMs scale beyond a few superfans?",
                    answer: "Yes. Group threads, filters, and message limits keep comms manageable."
                },
                {
                    id: 3,
                    question: "What if someone upgrades mid cycle?",
                    answer: "Proration credits and charges are calculated and shown before confirm."
                },
                {
                    id: 4,
                    question: "Can I bundle courses or community with membership?",
                    answer: "Yes. Entitlements map perks across posts, courses, and gated spaces."
                },
                {
                    id: 5,
                    question: "How do I reduce involuntary churn?",
                    answer: "Card updater, smart retries, and gentle reminders before expiry help keep members."
                },
                {
                    id: 6,
                    question: "Can I migrate existing subscribers?",
                    answer: "We import members, plans, and renewal dates and keep access intact."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization Kit",
                    modalTitle: "Get the Monetization Kit"
                }
            }
        },
        seo: {
            title: "Monetization and subscriber hubs for creators | Desilo",
            description: "Launch tiers, perks, paywalls, and member DMs. Convert fans to paying members, reduce churn, and grow predictable revenue with clean billing and clear value.",
            keywords: "creator monetization, subscriber management, membership tiers, creator paywalls, fan monetization, creator revenue, membership platform",
            ogTitle: "Monetization and subscriber hubs for creators | Desilo",
            ogDescription: "Launch tiers, perks, paywalls, and member DMs. Convert fans to paying members, reduce churn, and grow predictable revenue with clean billing and clear value.",
            twitterTitle: "Monetization and subscriber hubs for creators | Desilo",
            twitterDescription: "Launch tiers, perks, paywalls, and member DMs. Convert fans to paying members, reduce churn, and grow predictable revenue with clean billing and clear value."
        }
    },
    {
        slug: "digital-store-productized-assets",
        title: "Digital store for productized assets",
        description: "Courses, presets, templates, and downloads with smooth checkout, bundles, and upsells.",
        hero: {
            label: "USE CASE",
            title: "Digital store for productized assets",
            description: "Courses, presets, templates, and downloads with smooth checkout, bundles, and upsells.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization Kit",
                    modalTitle: "Get the Monetization Kit"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Creators and studios turning expertise into products."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Everything needed to sell digital products without duct tape.",
            items: [
                {
                    id: 1,
                    title: "Product pages",
                    description: "Clean, branded pages for courses, presets, templates, packs, and bundles.",
                },
                {
                    id: 2,
                    title: "Checkout and fulfillment",
                    description: "One-page checkout with tax, coupons, instant file delivery, and access emails.",
                },
                {
                    id: 3,
                    title: "Bundles and upsells",
                    description: "Auto-suggest complements and post-purchase upgrades that raise AOV.",
                },
                {
                    id: 4,
                    title: "Licensing and terms",
                    description: "Personal, commercial, or team licenses with clear usage rights.",
                },
                {
                    id: 5,
                    title: "Drip courses and lessons",
                    description: "Gate modules, quizzes, and progress with certificates if you want them.",
                },
                {
                    id: 6,
                    title: "Trials and samples",
                    description: "Free previews and sample files that nudge purchase.",
                },
                {
                    id: 7,
                    title: "Affiliate links",
                    description: "Track partner sales with unique codes and payouts.",
                },
                {
                    id: 8,
                    title: "Email and SMS capture",
                    description: "Collect leads on product pages and deliver welcome sequences.",
                },
                {
                    id: 9,
                    title: "Analytics",
                    description: "Views, conversion, refunds, AOV, and LTV by product and cohort.",
                },
                {
                    id: 10,
                    title: "Support blocks",
                    description: "FAQ, updates, and version history right on the product page.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The outcomes that improve when your store is built for digital.",
            items: [
                {
                    id: 1,
                    title: "Higher conversion",
                    description: "fewer steps, clearer offers, instant delivery",
                },
                {
                    id: 2,
                    title: "Bigger baskets",
                    description: "bundles and upsells lift average order value",
                },
                {
                    id: 3,
                    title: "Owned audience",
                    description: "email and SMS capture turn buyers into repeat customers",
                },
                {
                    id: 4,
                    title: "Less support",
                    description: "licenses, updates, and access are predictable and self-serve",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A simple path from idea to first sale.",
            steps: [
                {
                    title: "Package the offer",
                    description: "Pick products, licenses, and price points. Outline bundles and samples."
                },
                {
                    title: "Design pages",
                    description: "Add copy, media, reviews, and a single clear call to action."
                },
                {
                    title: "Enable checkout",
                    description: "Turn on payments, tax, coupons, and receipts with clean confirmations."
                },
                {
                    title: "Set fulfillment",
                    description: "Upload files, link lessons, and configure access emails and updates."
                },
                {
                    title: "Launch and measure",
                    description: "Share the link, tag UTMs, and watch conversion and AOV to iterate weekly."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals your store is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Product page conversion up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "AOV up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Refund rate down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Email list growth up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Repeat purchase rate up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Fast pages, reliable delivery, clean records.",
            items: [
                {
                    title: "Frontend",
                    description: "Mobile-first product and checkout pages with accessible components.",
                },
                {
                    title: "Backend",
                    description: "Products, orders, entitlements, and file delivery with signed links and expiries.",
                },
                {
                    title: "Payments",
                    description: "Cards, wallets, and local methods with tax engines and compliant invoices.",
                },
                {
                    title: "Licensing",
                    description: "License keys or terms attached to orders with audit logs.",
                },
                {
                    title: "Data",
                    description: "Events for views, carts, purchases, refunds, and upgrades with dashboards.",
                },
                {
                    title: "Integrations",
                    description: "Email or CRM, analytics, affiliate platforms, community gates, and course players.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that raise revenue and trust.",
            items: [
                {
                    title: "Versioned downloads",
                    description: "Notify buyers of updates and changelogs by product.",
                },
                {
                    title: "Customer portal",
                    description: "Self-serve access to files, invoices, licenses, and upgrades.",
                },
                {
                    title: "Pay-what-you-want",
                    description: "Floor pricing for experimental drops and community support.",
                },
                {
                    title: "Regional pricing",
                    description: "Fair prices by market with rounding rules.",
                },
                {
                    title: "Gift purchases",
                    description: "Let buyers send products with scheduled delivery.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls that protect your IP and buyer data.",
            tags: [
                "Signed download URLs",
                "rate limits",
                "basic anti-sharing patterns",
                "encrypted PII",
                "compliant tax invoices",
                "role-based access",
                "audit trails"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Can I sell courses and files in the same store?",
                    answer: "Yes. Courses, downloads, and bundles share one checkout and entitlement system."
                },
                {
                    id: 2,
                    question: "How do updates work for presets or templates?",
                    answer: "Upload a new version and notify buyers automatically with changelogs."
                },
                {
                    id: 3,
                    question: "Can I offer commercial licenses?",
                    answer: "Yes. Set license types and price tiers with clear terms on the product page."
                },
                {
                    id: 4,
                    question: "What about refunds for digital goods?",
                    answer: "We implement your policy with clear windows and automated access revocation."
                },
                {
                    id: 5,
                    question: "Does this replace my current link-in-bio?",
                    answer: "Your store can be the primary link with sections for featured bundles and lead magnets."
                },
                {
                    id: 6,
                    question: "Can affiliates get paid out automatically?",
                    answer: "Yes. Track codes and route partner payouts on a schedule with statements."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization Kit",
                    modalTitle: "Get the Monetization Kit"
                }
            }
        },
        seo: {
            title: "Digital store for productized assets | Desilo",
            description: "Sell courses, presets, templates, and downloads with smooth checkout, bundles, upsells, licensing, and instant delivery. Increase conversion and AOV while building an owned customer base.",
            keywords: "digital store, creator products, course sales, digital downloads, creator monetization, online store, digital assets, creator commerce",
            ogTitle: "Digital store for productized assets | Desilo",
            ogDescription: "Sell courses, presets, templates, and downloads with smooth checkout, bundles, upsells, licensing, and instant delivery. Increase conversion and AOV while building an owned customer base.",
            twitterTitle: "Digital store for productized assets | Desilo",
            twitterDescription: "Sell courses, presets, templates, and downloads with smooth checkout, bundles, upsells, licensing, and instant delivery. Increase conversion and AOV while building an owned customer base."
        }
    },
    {
        slug: "community-spaces-engagement-loops",
        title: "Community spaces and engagement loops",
        description: "Live rooms, chats, challenges, and streaks that deepen connection and improve retention.",
        hero: {
            label: "USE CASE",
            title: "Community spaces and engagement loops",
            description: "Live rooms, chats, challenges, and streaks that deepen connection and improve retention.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Community Playbook",
                    modalTitle: "Get the Community Playbook"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Creators and studios ready to turn followers into a real community."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Spaces and loops that turn attention into belonging.",
            items: [
                {
                    id: 1,
                    title: "Gated spaces and roles",
                    description: "Member only channels that map to tiers with clear perks and visibility.",
                },
                {
                    id: 2,
                    title: "Live rooms and AMAs",
                    description: "Low friction live audio or video sessions with Q&A, reactions, and replays.",
                },
                {
                    id: 3,
                    title: "Chat and threads",
                    description: "Fast conversations with mentions, replies, and topic tags so context stays clear.",
                },
                {
                    id: 4,
                    title: "Challenges and prompts",
                    description: "Weekly themes and small tasks that nudge participation and create momentum.",
                },
                {
                    id: 5,
                    title: "Streaks and milestones",
                    description: "Gentle streaks, badges, and milestones that reward consistency without pressure.",
                },
                {
                    id: 6,
                    title: "Events and cohorts",
                    description: "Time boxed programs and small groups that help people progress together.",
                },
                {
                    id: 7,
                    title: "Member directory",
                    description: "Profiles with interests and skills so fans find peers and collaborators.",
                },
                {
                    id: 8,
                    title: "Perks and unlocks",
                    description: "Auto access to rooms, discounts, and drops based on tier or participation.",
                },
                {
                    id: 9,
                    title: "Moderation and safety",
                    description: "Reports, filters, and roles that keep spaces welcoming and brand safe.",
                },
                {
                    id: 10,
                    title: "Analytics",
                    description: "Engagement, retention, and LTV by cohort, space, and program with drill downs.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The outcomes that improve when community becomes a habit.",
            items: [
                {
                    id: 1,
                    title: "Higher retention",
                    description: "programs and streaks keep members coming back",
                },
                {
                    id: 2,
                    title: "More revenue",
                    description: "engaged cohorts upgrade tiers and buy more often",
                },
                {
                    id: 3,
                    title: "Lower support load",
                    description: "clear spaces and norms reduce confusion and conflict",
                },
                {
                    id: 4,
                    title: "Owned growth",
                    description: "a real home builds trust beyond timelines and algorithms",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A simple path from scattered comments to a thriving hub.",
            steps: [
                {
                    title: "Design the spaces",
                    description: "Define public, member, and high tier rooms with clear promises."
                },
                {
                    title: "Plan the loops",
                    description: "Set weekly prompts, monthly challenges, and a live cadence people can trust."
                },
                {
                    title: "Launch and welcome",
                    description: "Run a kickoff week with orientation, first wins, and posts that spark replies."
                },
                {
                    title: "Run cohorts and events",
                    description: "Schedule small groups, workshops, and AMAs and capture highlights."
                },
                {
                    title: "Reward and showcase",
                    description: "Celebrate streaks, feature member work, and unlock perks for participation."
                },
                {
                    title: "Measure and refine",
                    description: "Watch engagement, retention, and upgrades and tune programs quarterly."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show the community is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Weekly active members up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Post and reply rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Event attendance up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Upgrade rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Churn down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Support tickets about access down"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Fast rooms, fair perks, safe vibes.",
            items: [
                {
                    title: "Frontend",
                    description: "Accessible chat, live rooms, and event pages with optimistic updates.",
                },
                {
                    title: "Backend",
                    description: "Services for spaces, roles, posts, events, and entitlements with clear logs.",
                },
                {
                    title: "Messaging",
                    description: "Low latency sockets, presence, and sensible rate limits for peak moments.",
                },
                {
                    title: "Perks and gating",
                    description: "Entitlements tied to tiers and milestones, updated in real time.",
                },
                {
                    title: "Data",
                    description: "Event maps for engagement and retention with cohort dashboards.",
                },
                {
                    title: "Integrations",
                    description: "Email and push for reminders, calendars for events, payments for tiers, and analytics.",
                },
                {
                    title: "Ops",
                    description: "Feature flags, moderation queues, audit trails, and disaster recovery runbooks.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that deepen connection and reduce effort.",
            items: [
                {
                    title: "Community gates for Discord or Slack",
                    description: "Sync roles so external chat reflects membership.",
                },
                {
                    title: "Matchmaking and intros",
                    description: "Auto introduce members with shared interests or goals.",
                },
                {
                    title: "Office hours booking",
                    description: "Tier based access to your time with simple scheduling.",
                },
                {
                    title: "Referral credits",
                    description: "Give and get rewards for inviting the right people.",
                },
                {
                    title: "AI community assistant",
                    description: "Summaries, highlights, and nudges that surface the best threads.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls that protect members and your brand.",
            tags: [
                "Role based access",
                "encrypted PII",
                "signed webhooks",
                "clear retention",
                "Moderation actions, data moves, and entitlements are fully logged"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Will streaks feel spammy or stressful?",
                    answer: "We design gentle streaks with pauses and recovery so motivation stays healthy."
                },
                {
                    id: 2,
                    question: "Can I run member only and public spaces together?",
                    answer: "Yes. Public rooms feed discovery while member rooms deliver real value."
                },
                {
                    id: 3,
                    question: "How do I prevent low quality posts?",
                    answer: "Prompts, clear norms, and light moderation tools keep quality high."
                },
                {
                    id: 4,
                    question: "Do events have replays and highlights?",
                    answer: "Yes. Recordings, timestamps, and short clips live in the hub for members."
                },
                {
                    id: 5,
                    question: "Can perks change as we grow?",
                    answer: "Entitlements update instantly and announcements guide members through changes."
                },
                {
                    id: 6,
                    question: "How soon will I see lift?",
                    answer: "Most creators see a participation bump during the first challenge cycle."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Community Playbook",
                    modalTitle: "Get the Community Playbook"
                }
            }
        },
        seo: {
            title: "Community spaces and engagement loops for creators | Desilo",
            description: "Launch live rooms, chats, challenges, and streaks with clear perks and safe moderation. Deepen connection, lift retention, and grow member revenue with a true home for your community.",
            keywords: "creator community, engagement loops, live rooms, community building, creator retention, member engagement, community spaces, creator community management",
            ogTitle: "Community spaces and engagement loops for creators | Desilo",
            ogDescription: "Launch live rooms, chats, challenges, and streaks with clear perks and safe moderation. Deepen connection, lift retention, and grow member revenue with a true home for your community.",
            twitterTitle: "Community spaces and engagement loops for creators | Desilo",
            twitterDescription: "Launch live rooms, chats, challenges, and streaks with clear perks and safe moderation. Deepen connection, lift retention, and grow member revenue with a true home for your community."
        }
    },
    {
        slug: "sponsorship-brand-kit-automation",
        title: "Sponsorship and brand kit automation",
        description: "Auto media kits, rate cards, booking links, and deliverable trackers so you land and run deals faster.",
        hero: {
            label: "USE CASE",
            title: "Sponsorship and brand kit automation",
            description: "Auto media kits, rate cards, booking links, and deliverable trackers so you land and run deals faster.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Deal Desk Starter Pack",
                    modalTitle: "Get the Deal Desk Starter Pack"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Creators and studios ready to professionalize brand deals."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Everything needed to win and deliver sponsorships with less back and forth.",
            items: [
                {
                    id: 1,
                    title: "Auto media kit",
                    description: "Always-fresh audience, reach, and case studies pulled from your data.",
                },
                {
                    id: 2,
                    title: "Smart rate cards",
                    description: "Suggested pricing by format and channel with ranges and negotiables.",
                },
                {
                    id: 3,
                    title: "Booking and inquiry links",
                    description: "Hosted pages with availability, intake questions, and calendar holds.",
                },
                {
                    id: 4,
                    title: "Brief and scope builder",
                    description: "Collect goals, deliverables, usage rights, and timelines in one form.",
                },
                {
                    id: 5,
                    title: "Contract and e-sign",
                    description: "Preapproved terms with variables for rates, rights, and renewals.",
                },
                {
                    id: 6,
                    title: "Disclosure and brand-safety checks",
                    description: "Built-in FTC prompts and policy screens before publish.",
                },
                {
                    id: 7,
                    title: "Deliverable tracker",
                    description: "One board for drafts, approvals, posts, proofs, and links.",
                },
                {
                    id: 8,
                    title: "Invoices and payouts",
                    description: "Milestone or net terms with compliant invoices and status.",
                },
                {
                    id: 9,
                    title: "Proof of performance",
                    description: "Auto capture of links, screenshots, UTMs, and codes with a recap.",
                },
                {
                    id: 10,
                    title: "Client portal",
                    description: "Brands see status, assets, approvals, and results without email threads.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The outcomes that improve when deals run on rails.",
            items: [
                {
                    id: 1,
                    title: "Less chaos",
                    description: "one source of truth beats spreadsheets and DMs",
                },
                {
                    id: 2,
                    title: "Higher win rate",
                    description: "fast kits, clear rates, and smooth booking close more deals",
                },
                {
                    id: 3,
                    title: "More capacity",
                    description: "one manager handles more sponsors without missed steps",
                },
                {
                    id: 4,
                    title: "Trusted results",
                    description: "clean proofs and invoices make renewals easier",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A simple path from inquiry to renewal.",
            steps: [
                {
                    title: "Set your kit and rates",
                    description: "Auto-populate stats and case studies and publish a rate card with ranges."
                },
                {
                    title: "Share booking links",
                    description: "Collect scope, budget, and timing and place a hold on your calendar."
                },
                {
                    title: "Lock scope and sign",
                    description: "Generate a contract, e-sign, and schedule payment milestones."
                },
                {
                    title: "Deliver and approve",
                    description: "Track drafts, approvals, posts, and disclosures with receipts of proof."
                },
                {
                    title: "Report and invoice",
                    description: "Send a branded recap with links and metrics and issue the invoice."
                },
                {
                    title: "Repeat and optimize",
                    description: "Save what worked, adjust rates, and templatize the next pitch."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show the deal desk is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Inquiry to booked time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Win rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "On-time delivery up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Invoice paid on time up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Renewal rate up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Fast UI, reliable workflows, clean records.",
            items: [
                {
                    title: "Frontend",
                    description: "Kits, forms, boards, and portals with quick edits and comments.",
                },
                {
                    title: "Backend",
                    description: "Services for contacts, briefs, contracts, invoices, and proofs with idempotent APIs.",
                },
                {
                    title: "Data",
                    description: "Audience and performance pulls, UTM and code capture, and recap exports.",
                },
                {
                    title: "Integrations",
                    description: "Calendar, e-sign, payments, storage, CRM, and your analytics.",
                },
                {
                    title: "Ops",
                    description: "Roles and permissions, logs and alerts, and SLA dashboards for deals.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that raise speed and confidence.",
            items: [
                {
                    title: "AI pitch assistant",
                    description: "Drafts sponsor-specific emails using your kit and recent wins.",
                },
                {
                    title: "Case study generator",
                    description: "Auto builds one-pagers from past campaigns with metrics and quotes.",
                },
                {
                    title: "Rate intelligence",
                    description: "Benchmarks by category and channel to guide pricing.",
                },
                {
                    title: "Renewal nudges",
                    description: "Reminders with performance highlights timed to the next launch.",
                },
                {
                    title: "Affiliate and promo codes",
                    description: "Track redemptions and share clean proof in recaps.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls that protect sponsors and your brand.",
            tags: [
                "Role-based access",
                "encrypted PII",
                "signed webhooks",
                "audit trails",
                "Rights, disclosures, and consent records are stored with each deal"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Will this replace my inbox and sheets?",
                    answer: "It centralizes intake, kits, contracts, and delivery while still emailing stakeholders."
                },
                {
                    id: 2,
                    question: "Can I keep flexible pricing?",
                    answer: "Yes. Use suggested ranges and lock final rates per deal."
                },
                {
                    id: 3,
                    question: "Do brands need an account to review?",
                    answer: "No. Share scoped portal links for status, approvals, and files."
                },
                {
                    id: 4,
                    question: "How do you track proof of performance?",
                    answer: "We capture post URLs, screenshots, UTMs, and code redemptions automatically."
                },
                {
                    id: 5,
                    question: "Can I handle international brands and taxes?",
                    answer: "Invoices, currency, and tax fields adapt to region with exports for accounting."
                },
                {
                    id: 6,
                    question: "How fast can I pilot?",
                    answer: "A short setup, then a live kit, booking link, and your first tracked deal quickly."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Deal Desk Starter Pack",
                    modalTitle: "Get the Deal Desk Starter Pack"
                }
            }
        },
        seo: {
            title: "Sponsorship and brand kit automation for creators | Desilo",
            description: "Auto media kits, rate cards, booking links, deliverable trackers, contracts, invoices, and proofs. Close more deals, deliver on time, and renew with confidence.",
            keywords: "sponsorship automation, brand kit, creator deals, media kit, rate cards, sponsorship management, creator partnerships, brand collaboration",
            ogTitle: "Sponsorship and brand kit automation for creators | Desilo",
            ogDescription: "Auto media kits, rate cards, booking links, deliverable trackers, contracts, invoices, and proofs. Close more deals, deliver on time, and renew with confidence.",
            twitterTitle: "Sponsorship and brand kit automation for creators | Desilo",
            twitterDescription: "Auto media kits, rate cards, booking links, deliverable trackers, contracts, invoices, and proofs. Close more deals, deliver on time, and renew with confidence."
        }
    },
    {
        slug: "creator-analytics-growth-systems",
        title: "Creator analytics and growth systems",
        description: "Dashboards for reach, LTV, churn, content ROI, and attribution that inform what to make next.",
        hero: {
            label: "USE CASE",
            title: "Creator analytics and growth systems",
            description: "Dashboards for reach, LTV, churn, content ROI, and attribution that inform what to make next.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Growth Audit",
                    modalTitle: "Get the Growth Audit"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Creators and studios that want decisions driven by signal, not vibes."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "One source of truth from post to revenue.",
            items: [
                {
                    id: 1,
                    title: "Unified dashboards",
                    description: "Reach, watch time, saves, clicks, subscribers, purchases, refunds in one view.",
                },
                {
                    id: 2,
                    title: "Attribution",
                    description: "UTMs, codes, and link routing that map revenue to posts, creators, and channels.",
                },
                {
                    id: 3,
                    title: "Content ROI",
                    description: "Cost and time per asset vs revenue, trials, and upgrades to see payback.",
                },
                {
                    id: 4,
                    title: "Cohorts and retention",
                    description: "Day 1, 7, 30 curves for members and buyers with churn and comeback rates.",
                },
                {
                    id: 5,
                    title: "LTV and ARPU",
                    description: "Per tier and product with forecasts that guide pricing and perks.",
                },
                {
                    id: 6,
                    title: "Funnel analytics",
                    description: "Views to click, click to trial, trial to paid, and renewal loops.",
                },
                {
                    id: 7,
                    title: "Creator scorecards",
                    description: "Health metrics per creator or channel with targets and trends.",
                },
                {
                    id: 8,
                    title: "Alerts and benchmarks",
                    description: "Nudges when metrics drift and normal ranges by format and platform.",
                },
                {
                    id: 9,
                    title: "Exports and decks",
                    description: "Branded recaps for sponsors or investors without spreadsheet chaos.",
                },
                {
                    id: 10,
                    title: "Warehouse and BI sync",
                    description: "Clean tables for your BI with governed metric definitions.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The outcomes that improve when the numbers tell you what to make next.",
            items: [
                {
                    id: 1,
                    title: "Less chaos",
                    description: "one source of truth beats screenshots and debates",
                },
                {
                    id: 2,
                    title: "Higher win rate",
                    description: "invest in formats and topics that return real revenue",
                },
                {
                    id: 3,
                    title: "More capacity",
                    description: "auto recaps free time to produce, not reconcile",
                },
                {
                    id: 4,
                    title: "Trusted results",
                    description: "clean attribution and cohorts unlock better sponsors and plans",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A simple path from raw signals to clear choices.",
            steps: [
                {
                    title: "Instrument the funnel",
                    description: "Standardize UTMs, codes, and events for impressions, clicks, trials, and purchases."
                },
                {
                    title: "Connect the systems",
                    description: "Pull social, web, email, commerce, and membership data into one model."
                },
                {
                    title: "Normalize and model",
                    description: "Clean names, dedupe, and build cohorts, LTV, churn, and ROI tables."
                },
                {
                    title: "Build the views",
                    description: "Dashboards for content, membership, and store with filters and notes."
                },
                {
                    title: "Report and act",
                    description: "Weekly highlights and tests with next best action suggestions."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show the growth system is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Report prep time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Content payback time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Trials to paid up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Churn down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "ARPU and LTV up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Sponsor renewal rate up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Clean data, explainable metrics, fast views.",
            items: [
                {
                    title: "Frontend",
                    description: "Creator friendly dashboards with filters, segments, and time windows.",
                },
                {
                    title: "Backend",
                    description: "Services for events, orders, subscribers, and attribution with idempotent ingestion.",
                },
                {
                    title: "Models",
                    description: "Rule based attribution, cohort retention, LTV, churn, and ROI with documented inputs.",
                },
                {
                    title: "Pipelines",
                    description: "ETL to a warehouse, freshness checks, and versioned schemas.",
                },
                {
                    title: "Integrations",
                    description: "Social APIs, link shorteners, email and CRM, commerce and membership tools.",
                },
                {
                    title: "Ops",
                    description: "Monitors for pulls, row counts, and freshness with alerting and retries.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that speed insight and action.",
            items: [
                {
                    title: "ROI narrative blocks",
                    description: "Auto write highlights and lowlights for the weekly recap.",
                },
                {
                    title: "Creator vs channel splits",
                    description: "Compare formats and people to guide staffing and collabs.",
                },
                {
                    title: "Price and perk tester",
                    description: "Track impact of trials, annual nudges, and perk changes.",
                },
                {
                    title: "Topical taxonomy",
                    description: "Tag posts by theme to see what actually converts.",
                },
                {
                    title: "Sponsor impact pack",
                    description: "Attribution views that secure renewals with proof.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls sponsors and finance will trust.",
            tags: [
                "Role based access",
                "SSO options",
                "encrypted PII",
                "consent-aware tracking",
                "audit logs",
                "Metric definitions are documented and versioned"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Can I keep my current BI tool?",
                    answer: "Yes. We sync modeled tables to your warehouse and you can use your BI on top."
                },
                {
                    id: 2,
                    question: "Do I need UTMs on every link?",
                    answer: "Yes. We generate them automatically and route short links per platform."
                },
                {
                    id: 3,
                    question: "Will this slow down posting?",
                    answer: "No. Instrumentation runs behind the scenes and templates make tagging easy."
                },
                {
                    id: 4,
                    question: "How do we measure content ROI?",
                    answer: "We combine time or cost to produce with downstream revenue and upgrades."
                },
                {
                    id: 5,
                    question: "What if a platform API is flaky?",
                    answer: "We queue pulls, backfill gaps, and flag freshness in the dashboard."
                },
                {
                    id: 6,
                    question: "Can sponsors see performance?",
                    answer: "Share scoped, read only views or export branded PDFs with the right metrics."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Growth Audit",
                    modalTitle: "Get the Growth Audit"
                }
            }
        },
        seo: {
            title: "Creator analytics and growth systems | Desilo",
            description: "Dashboards for reach, LTV, churn, content ROI, and attribution. Replace guesswork with a clear growth system that shows what to make next and why.",
            keywords: "creator analytics, growth systems, content ROI, creator metrics, attribution tracking, creator dashboards, LTV analysis, churn analytics",
            ogTitle: "Creator analytics and growth systems | Desilo",
            ogDescription: "Dashboards for reach, LTV, churn, content ROI, and attribution. Replace guesswork with a clear growth system that shows what to make next and why.",
            twitterTitle: "Creator analytics and growth systems | Desilo",
            twitterDescription: "Dashboards for reach, LTV, churn, content ROI, and attribution. Replace guesswork with a clear growth system that shows what to make next and why."
        }
    },
    {
        slug: "starter-saas-modules-creator-tools",
        title: "Starter SaaS modules for creator tools",
        description: "Lightweight utilities tailored to your audience such as membership managers, growth helpers, or mini apps.",
        hero: {
            label: "USE CASE",
            title: "Starter SaaS modules for creator tools",
            description: "Lightweight utilities tailored to your audience such as membership managers, growth helpers, or mini apps.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Starter SaaS Kit",
                    modalTitle: "Get the Starter SaaS Kit"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Creators and studios ready to ship small tools that feel like products."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Plug and play modules that look custom and ship fast.",
            items: [
                {
                    id: 1,
                    title: "Membership manager",
                    description: "Basic tiers, perks, and access rules with clean checkout and self serve.",
                },
                {
                    id: 2,
                    title: "Growth helpers",
                    description: "Referral widget, giveaways, and waitlists with fair anti fraud checks.",
                },
                {
                    id: 3,
                    title: "Mini analytics",
                    description: "Simple dashboards for clicks, signups, and sales with UTMs baked in.",
                },
                {
                    id: 4,
                    title: "Content gates",
                    description: "Lite paywall, file unlocks, and coupon gates for drops and posts.",
                },
                {
                    id: 5,
                    title: "Course or lesson micro",
                    description: "Chapters, progress, and certificates for short formats.",
                },
                {
                    id: 6,
                    title: "Booking and calendar",
                    description: "Paid sessions or office hours with time zones and reminders.",
                },
                {
                    id: 7,
                    title: "Link router",
                    description: "Smart links per platform with A and B tests and clean reporting.",
                },
                {
                    id: 8,
                    title: "Tip jar and donations",
                    description: "One click support with transparent fees and receipts.",
                },
                {
                    id: 9,
                    title: "Forms and surveys",
                    description: "Lead capture, sponsor intake, and member polls with exports.",
                },
                {
                    id: 10,
                    title: "Email and SMS captures",
                    description: "Opt ins on every module with welcome sequences.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The outcomes that improve when your audience gets useful tools.",
            items: [
                {
                    id: 1,
                    title: "Faster validation",
                    description: "prove demand with a real utility and clear metrics",
                },
                {
                    id: 2,
                    title: "New revenue",
                    description: "small paid features and upgrades stack into meaningful MRR",
                },
                {
                    id: 3,
                    title: "Higher retention",
                    description: "sticky helpers increase habit and membership value",
                },
                {
                    id: 4,
                    title: "Owned growth",
                    description: "emails and usage data fuel smarter launches",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A practical path from idea to shipping your first module.",
            steps: [
                {
                    title: "Pick the module",
                    description: "Choose the utility that unblocks value for your audience today."
                },
                {
                    title: "Brand and scope",
                    description: "Add your look, copy, and rules for tiers, pricing, or limits."
                },
                {
                    title: "Wire payments and auth",
                    description: "Enable checkout, receipts, and basic sign in or SSO if needed."
                },
                {
                    title: "Launch and learn",
                    description: "Publish a simple page or embed, then watch usage and feedback."
                },
                {
                    title: "Iterate or bundle",
                    description: "Tune pricing, add perks, or combine modules into a starter suite."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show modules are working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Time to first revenue down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Signups and opt ins up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Conversion to paid up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Churn down for members using tools"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "ARPU up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Clean UX, reliable billing, safe data.",
            items: [
                {
                    title: "Frontend",
                    description: "Responsive components and themes that match your brand.",
                },
                {
                    title: "Backend",
                    description: "Services for users, sessions, entitlements, and lightweight content with retries.",
                },
                {
                    title: "Payments",
                    description: "Cards, wallets, and local methods with tax, invoices, refunds, and proration.",
                },
                {
                    title: "Data",
                    description: "Events for signups, usage, purchases, and churn with simple dashboards.",
                },
                {
                    title: "Integrations",
                    description: "Email and CRM, calendars, analytics, link shorteners, and community gates.",
                },
                {
                    title: "Ops",
                    description: "Feature flags, logs, alerts, and sandbox mode for safe pilots.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that turn a module into a mini product.",
            items: [
                {
                    title: "API and webhooks",
                    description: "Let partners or power users extend actions and sync data.",
                },
                {
                    title: "Theme and component kit",
                    description: "Figma and code tokens so future modules match out of the box.",
                },
                {
                    title: "Affiliate and referral credits",
                    description: "Reward sharing with tracked links and payouts.",
                },
                {
                    title: "Localization",
                    description: "Copy packs and currency rules for global audiences.",
                },
                {
                    title: "Mobile wrappers",
                    description: "Ship a lightweight iOS or Android shell for the same module.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls that protect members and your brand.",
            tags: [
                "Role based access",
                "encrypted PII",
                "audit logs",
                "rate limits",
                "clear retention",
                "Optional SSO and SCIM for team accounts"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Do I need all modules to start?",
                    answer: "No. Launch one that solves a real problem and add more as signal grows."
                },
                {
                    id: 2,
                    question: "Will this replace my current tools?",
                    answer: "It can live alongside them or consolidate over time."
                },
                {
                    id: 3,
                    question: "Can I charge for modules separately?",
                    answer: "Yes. Price per module, bundle, or include in tiers."
                },
                {
                    id: 4,
                    question: "How much branding control do I get?",
                    answer: "Full control over theme, copy, and components to match your look."
                },
                {
                    id: 5,
                    question: "What if I outgrow a module?",
                    answer: "We migrate data and entitlements into a fuller product when you are ready."
                },
                {
                    id: 6,
                    question: "How fast can we ship the first one?",
                    answer: "After a short setup, most creators launch a branded module quickly."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Starter SaaS Kit",
                    modalTitle: "Get the Starter SaaS Kit"
                }
            }
        },
        seo: {
            title: "Starter SaaS modules for creator tools | Desilo",
            description: "Ship lightweight utilities like membership managers, growth helpers, analytics, and mini apps. Validate fast, add new revenue, and grow with clean UX, reliable billing, and owned data.",
            keywords: "creator tools, SaaS modules, membership manager, growth helpers, mini apps, creator utilities, lightweight SaaS, creator software",
            ogTitle: "Starter SaaS modules for creator tools | Desilo",
            ogDescription: "Ship lightweight utilities like membership managers, growth helpers, analytics, and mini apps. Validate fast, add new revenue, and grow with clean UX, reliable billing, and owned data.",
            twitterTitle: "Starter SaaS modules for creator tools | Desilo",
            twitterDescription: "Ship lightweight utilities like membership managers, growth helpers, analytics, and mini apps. Validate fast, add new revenue, and grow with clean UX, reliable billing, and owned data."
        }
    },
    {
        slug: "link-in-bio-storefront",
        title: "Link in bio storefront",
        description: "One link that becomes a mini site with cart, bundles, gating, and email capture.",
        hero: {
            label: "USE CASE",
            title: "Link in bio storefront",
            description: "One link that becomes a mini site with cart, bundles, gating, and email capture.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization Kit",
                    modalTitle: "Get the Monetization Kit"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Creators who want every click to count."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "A high converting mini site that lives behind one link.",
            items: [
                {
                    id: 1,
                    title: "Featured blocks",
                    description: "Showcase your top offer, latest drop, or live event above the fold.",
                },
                {
                    id: 2,
                    title: "Smart product tiles",
                    description: "Courses, presets, templates, and bundles with price, badges, and quick view.",
                },
                {
                    id: 3,
                    title: "Cart and checkout",
                    description: "One page checkout with tax, coupons, and instant delivery or access.",
                },
                {
                    id: 4,
                    title: "Gated content",
                    description: "Lock files, posts, or videos for buyers or members with fair previews.",
                },
                {
                    id: 5,
                    title: "Bundles and upsells",
                    description: "Auto suggest complements and post purchase upgrades that raise AOV.",
                },
                {
                    id: 6,
                    title: "Lead capture",
                    description: "Email and SMS opt in with welcome sequences and tags.",
                },
                {
                    id: 7,
                    title: "Link routing",
                    description: "Different landing for each platform and campaign with clean UTMs.",
                },
                {
                    id: 8,
                    title: "Social proof",
                    description: "Ratings, testimonials, and recent buyers to build trust.",
                },
                {
                    id: 9,
                    title: "Analytics",
                    description: "Views, clicks, conversion, AOV, LTV, and source breakdowns.",
                },
                {
                    id: 10,
                    title: "Theme and branding",
                    description: "Fonts, colors, and layouts that feel native to your brand.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The outcomes that improve when your link works like a store.",
            items: [
                {
                    id: 1,
                    title: "Higher conversion",
                    description: "fewer taps and faster checkout turn curiosity into sales",
                },
                {
                    id: 2,
                    title: "Bigger baskets",
                    description: "bundles and upsells raise average order value",
                },
                {
                    id: 3,
                    title: "Owned audience",
                    description: "email and SMS capture turn visitors into repeat buyers",
                },
                {
                    id: 4,
                    title: "Clear attribution",
                    description: "UTMs and codes show which posts drive revenue",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A simple path from profile click to purchase and sign up.",
            steps: [
                {
                    title: "Assemble the page",
                    description: "Pick featured blocks, add products, drops, or membership gates."
                },
                {
                    title: "Enable checkout",
                    description: "Turn on payments, tax, receipts, and instant delivery or entitlements."
                },
                {
                    title: "Set routing and tracking",
                    description: "Attach UTMs per platform and campaign and shorten links."
                },
                {
                    title: "Publish and promote",
                    description: "Replace your bio link and share the storefront link in posts and stories."
                },
                {
                    title: "Measure and iterate",
                    description: "Watch sources, conversion, and AOV and test tiles and order of blocks weekly."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show the storefront is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Click to cart rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Checkout conversion up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "AOV up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Email capture rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Refunds down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Repeat purchase rate up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Fast pages, reliable delivery, clean data.",
            items: [
                {
                    title: "Frontend",
                    description: "Mobile first storefront with accessible components and quick interactions.",
                },
                {
                    title: "Backend",
                    description: "Products, carts, orders, entitlements, and file delivery with signed links.",
                },
                {
                    title: "Payments",
                    description: "Cards, wallets, and local methods with tax engines and compliant invoices.",
                },
                {
                    title: "Data",
                    description: "Events for views, clicks, carts, purchases, and signups with dashboards.",
                },
                {
                    title: "Integrations",
                    description: "Email or CRM, analytics, link shorteners, community gates, and course players.",
                },
                {
                    title: "Ops",
                    description: "Feature flags, logs, alerts, and sandbox mode for safe tests.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that boost revenue and trust.",
            items: [
                {
                    title: "Countdowns and drops",
                    description: "Timers and inventory caps that create urgency without spam.",
                },
                {
                    title: "Regional pricing",
                    description: "Fair prices by market with smart rounding.",
                },
                {
                    title: "Gift purchases",
                    description: "Let fans buy for others with scheduled delivery.",
                },
                {
                    title: "Affiliate tracking",
                    description: "Partner links with clean payout statements.",
                },
                {
                    title: "AI copy and thumbnail helper",
                    description: "On brand titles, blurbs, and images for faster launches.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls that protect your IP and buyer data.",
            tags: [
                "Signed download URLs",
                "encrypted PII",
                "audit logs",
                "rate limits",
                "clear retention",
                "Entitlements and refunds are logged for finance"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Does this replace my current link page?",
                    answer: "Yes. Your storefront becomes the primary link with sections for featured items and lead magnets."
                },
                {
                    id: 2,
                    question: "Can I sell memberships and downloads together?",
                    answer: "Yes. One checkout powers both and entitlements unlock access instantly."
                },
                {
                    id: 3,
                    question: "What if I need different links for each platform?",
                    answer: "Use routing to show platform specific versions and track performance with UTMs."
                },
                {
                    id: 4,
                    question: "How do I avoid spammy urgency?",
                    answer: "Use honest timers for real drops and keep copies clear and fair."
                },
                {
                    id: 5,
                    question: "Can I migrate existing products?",
                    answer: "We import products, buyers, and past orders where possible."
                },
                {
                    id: 6,
                    question: "Will this slow down my page?",
                    answer: "No. Pages are optimized for mobile and cached with quick first interaction."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization Kit",
                    modalTitle: "Get the Monetization Kit"
                }
            }
        },
        seo: {
            title: "Link in bio storefront for creators | Desilo",
            description: "Turn one link into a mini site with cart, bundles, gating, and email capture. Lift conversion and AOV and build an owned audience with fast, mobile first checkout.",
            keywords: "link in bio, creator storefront, bio link, mini site, creator commerce, mobile checkout, bio page, creator monetization",
            ogTitle: "Link in bio storefront for creators | Desilo",
            ogDescription: "Turn one link into a mini site with cart, bundles, gating, and email capture. Lift conversion and AOV and build an owned audience with fast, mobile first checkout.",
            twitterTitle: "Link in bio storefront for creators | Desilo",
            twitterDescription: "Turn one link into a mini site with cart, bundles, gating, and email capture. Lift conversion and AOV and build an owned audience with fast, mobile first checkout."
        }
    },
    {
        slug: "live-shoppable-videos",
        title: "Live and shoppable video",
        description: "Tag products in streams and clips with native checkout and post purchase flows.",
        hero: {
            label: "USE CASE",
            title: "Live and shoppable video",
            description: "Tag products in streams and clips with native checkout and post purchase flows.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization Kit",
                    modalTitle: "Get the Monetization Kit"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Creators and studios turning views into measured sales."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Commerce that lives inside your video.",
            items: [
                {
                    id: 1,
                    title: "Product tagging",
                    description: "Pin products to timestamps and scenes with variants and price badges.",
                },
                {
                    id: 2,
                    title: "Shoppable player",
                    description: "Add to cart and buy without leaving the stream or clip.",
                },
                {
                    id: 3,
                    title: "Native checkout",
                    description: "One page checkout with saved details, local methods, tax, and receipts.",
                },
                {
                    id: 4,
                    title: "Post purchase flows",
                    description: "Upsells, bundles, emails or SMS, and perk delivery after payment.",
                },
                {
                    id: 5,
                    title: "Inventory and drops",
                    description: "Real time stock, waitlists, and countdowns that create fair urgency.",
                },
                {
                    id: 6,
                    title: "Affiliate and split payouts",
                    description: "Automatic revenue shares with transparent statuses.",
                },
                {
                    id: 7,
                    title: "Attribution and analytics",
                    description: "View to cart, cart to buy, and revenue by creator, clip, and placement.",
                },
                {
                    id: 8,
                    title: "Replay commerce",
                    description: "Keep tags live on replays and shorts so the long tail converts.",
                },
                {
                    id: 9,
                    title: "Content CMS hooks",
                    description: "Tag once and reuse across channels with safe edits and expiries.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The outcomes that improve when buying happens in place.",
            items: [
                {
                    id: 1,
                    title: "Higher conversion",
                    description: "no redirects means fewer lost buyers on mobile",
                },
                {
                    id: 2,
                    title: "Revenue per minute up",
                    description: "tags and upsells raise cart and checkout rates",
                },
                {
                    id: 3,
                    title: "Creator ROI proven",
                    description: "clip and placement level attribution secures sponsor budgets",
                },
                {
                    id: 4,
                    title: "Repeat sales",
                    description: "post purchase flows and perks lift LTV",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A clear path from catalog to cart inside your stream.",
            steps: [
                {
                    title: "Connect the catalog",
                    description: "Sync products, stock, variants, and pricing rules."
                },
                {
                    title: "Tag your content",
                    description: "Place products by timestamp or frame and choose overlays and prompts."
                },
                {
                    title: "Enable checkout",
                    description: "Turn on payments, tax, shipping or digital delivery, and confirmations."
                },
                {
                    title: "Run the show",
                    description: "Go live or publish clips; highlight tags at the right moments."
                },
                {
                    title: "Follow up automatically",
                    description: "Send receipts, perks, and winbacks tied to what viewers bought."
                },
                {
                    title: "Measure and tune",
                    description: "Track funnels, test offers, and adjust tag timing each week."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show shoppable video is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Add to cart rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Checkout conversion up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Revenue per minute up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Refunds and failed payments down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Email or subscriber capture up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Low latency media, reliable commerce, explainable attribution.",
            items: [
                {
                    title: "Frontend",
                    description: "Shoppable player components with accessible overlays, captions, and quick interactions.",
                },
                {
                    title: "Backend",
                    description: "Services for products, carts, orders, entitlements, and payouts with idempotent APIs.",
                },
                {
                    title: "Media",
                    description: "Ingest, clipping, thumbnails, captions, and safe delivery links.",
                },
                {
                    title: "Payments",
                    description: "Cards, wallets, local methods, tax, invoices, refunds, and proration where needed.",
                },
                {
                    title: "Data",
                    description: "Event map for views, tags, carts, purchases, upsells, and attribution.",
                },
                {
                    title: "Integrations",
                    description: "Commerce systems, shipping, email or CRM, affiliate platforms, and analytics.",
                },
                {
                    title: "Ops",
                    description: "Feature flags, logs, alerts, and sandbox modes for safe tests.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that raise urgency and lifetime value.",
            items: [
                {
                    title: "Limited drops",
                    description: "Timers, inventory caps, and waitlists that create excitement without spam.",
                },
                {
                    title: "Bundles and post purchase upsells",
                    description: "Auto pair items and offer upgrades after checkout.",
                },
                {
                    title: "AI clipping and highlights",
                    description: "Auto generate short clips with the right product pinned.",
                },
                {
                    title: "Creator affiliate links",
                    description: "Unique links and codes with tracked revenue and payouts.",
                },
                {
                    title: "Translations and captions",
                    description: "Accessibility and global reach for viewers.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Commerce grade trust and content safety.",
            tags: [
                "PCI compliant payments",
                "encrypted PII",
                "audit logs for orders and refunds",
                "moderation tools for chat",
                "content safety filters",
                "Optional SSO and scoped roles for brands, agencies, and collaborators"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Can I use my existing store or catalog?",
                    answer: "Yes. Sync products and stock from your commerce system and control pricing rules."
                },
                {
                    id: 2,
                    question: "Does this work on mobile first?",
                    answer: "Yes. Player, overlays, and checkout are optimized for phones and low bandwidth."
                },
                {
                    id: 3,
                    question: "How do we handle attribution across clips and creators?",
                    answer: "Purchases tie to timestamps, clips, and creators for clean reporting."
                },
                {
                    id: 4,
                    question: "What about returns and refunds?",
                    answer: "Orders and refunds flow through your commerce system with clear customer messaging."
                },
                {
                    id: 5,
                    question: "Do I have to stream on my site?",
                    answer: "Host on your site or embed. Simulcast and link out are supported when useful."
                },
                {
                    id: 6,
                    question: "Will tagging distract viewers?",
                    answer: "Overlays are subtle, timed, and dismissible, and you control prompts."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Monetization Kit",
                    modalTitle: "Get the Monetization Kit"
                }
            }
        },
        seo: {
            title: "Live and shoppable video for creators | Desilo",
            description: "Tag products in streams and clips with native checkout and post purchase flows. Turn views into measurable revenue with low latency media and reliable commerce.",
            keywords: "shoppable video, live commerce, video shopping, live streaming, product tagging, video commerce, live sales, creator monetization",
            ogTitle: "Live and shoppable video for creators | Desilo",
            ogDescription: "Tag products in streams and clips with native checkout and post purchase flows. Turn views into measurable revenue with low latency media and reliable commerce.",
            twitterTitle: "Live and shoppable video for creators | Desilo",
            twitterDescription: "Tag products in streams and clips with native checkout and post purchase flows. Turn views into measurable revenue with low latency media and reliable commerce."
        }
    },
    {
        slug: "wellness-workload-guardrails",
        title: "Wellness and workload guardrails",
        description: "Focus modes, quiet hours, burnout signals, and coach handoffs to sustain the pace.",
        hero: {
            label: "USE CASE",
            title: "Wellness and workload guardrails",
            description: "Focus modes, quiet hours, burnout signals, and coach handoffs to sustain the pace.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Wellbeing Playbook",
                    modalTitle: "Get the Wellbeing Playbook"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Creators and small teams that want longevity, not just output."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Practical tools that protect your time and energy.",
            items: [
                {
                    id: 1,
                    title: "Focus mode",
                    description: "Hide non essential metrics and comments while you draft or edit.",
                },
                {
                    id: 2,
                    title: "Quiet hours",
                    description: "Schedule downtime that pauses pings, posts, and non urgent tasks.",
                },
                {
                    id: 3,
                    title: "Burnout signals",
                    description: "Detect overload from cadence, sleep proxy, and message volume with gentle nudges.",
                },
                {
                    id: 4,
                    title: "Workload planner",
                    description: "Balance sponsored and personal work with buffers and recovery days.",
                },
                {
                    id: 5,
                    title: "Coach and peer handoffs",
                    description: "Opt in routing to coaches or peer mentors when trends dip.",
                },
                {
                    id: 6,
                    title: "Boundaries and filters",
                    description: "Mute topics or accounts that harm focus or mood.",
                },
                {
                    id: 7,
                    title: "Task batching",
                    description: "Group similar tasks for fewer context switches and smoother days.",
                },
                {
                    id: 8,
                    title: "Check ins",
                    description: "Light pulses you control to track mood and adjust plans privately.",
                },
                {
                    id: 9,
                    title: "Support routing",
                    description: "Flag weeks that need reduced commitments or deadline shifts.",
                },
                {
                    id: 10,
                    title: "Anonymized trends",
                    description: "See personal patterns; share aggregates with managers only if you choose.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The outcomes that improve when pace is sustainable.",
            items: [
                {
                    id: 1,
                    title: "Fewer misses",
                    description: "late deliverables and cancellations drop with healthier cadence",
                },
                {
                    id: 2,
                    title: "Lower churn",
                    description: "a steady rhythm keeps members and sponsors longer",
                },
                {
                    id: 3,
                    title: "Less noise",
                    description: "quiet hours reduce off hour pings and distractions",
                },
                {
                    id: 4,
                    title: "Higher quality",
                    description: "consistent energy leads to better work and happier audiences",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A simple path from overdrive to sustainable flow.",
            steps: [
                {
                    title: "Set safeguards",
                    description: "Pick quiet hours, focus rules, and which signals to track."
                },
                {
                    title: "Plan the week",
                    description: "Timebox work, add buffers, and mark recovery blocks."
                },
                {
                    title: "Run gentle check ins",
                    description: "Skippable pulses guide small adjustments before burnout hits."
                },
                {
                    title: "Route help when needed",
                    description: "Send trends to a coach or peer and adjust workload with consent."
                },
                {
                    title: "Review and refine",
                    description: "Look at streaks and outcomes and keep the habits that work."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show guardrails are working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Late deliverables down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Off hour pings down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "On time publishing up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Consistency of posting up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Creator satisfaction up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Private by default, minimal data, clear controls.",
            items: [
                {
                    title: "Frontend",
                    description: "Lightweight prompts, schedules, and dashboards you can dismiss or adjust quickly.",
                },
                {
                    title: "Backend",
                    description: "Scoped storage for mood, schedules, and boundaries with strict retention options.",
                },
                {
                    title: "Data",
                    description: "Local first where possible, hashed identifiers, and aggregate views.",
                },
                {
                    title: "Integrations",
                    description: "Calendar, chat, and task tools to honor quiet hours and boundaries.",
                },
                {
                    title: "Ops",
                    description: "Feature flags for gradual rollout and transparent consent logs.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that support healthy rhythm.",
            items: [
                {
                    title: "Recovery planner",
                    description: "Suggests lighter weeks after heavy launches.",
                },
                {
                    title: "Focus presets",
                    description: "One click modes for writing, editing, or live sessions.",
                },
                {
                    title: "Peer rooms",
                    description: "Small accountability circles with simple weekly prompts.",
                },
                {
                    title: "Coach marketplace",
                    description: "Opt in directory of certified coaches with easy booking.",
                },
                {
                    title: "AI wellbeing assistant",
                    description: "On brand nudges and summaries that suggest small adjustments.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Protect privacy and give creators control.",
            tags: [
                "Private by default",
                "role based access",
                "encryption in transit and at rest",
                "clear deletion paths",
                "Aggregates hide personal details"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Do I have to share check ins with anyone?",
                    answer: "No. Personal signals stay private unless you choose to share aggregates."
                },
                {
                    id: 2,
                    question: "Will guardrails slow me down?",
                    answer: "They reduce noise and context switching so you ship more consistently."
                },
                {
                    id: 3,
                    question: "Can I change quiet hours on launch days?",
                    answer: "Yes. Override rules for special events and restore them after."
                },
                {
                    id: 4,
                    question: "Does this work with my calendar and chat apps?",
                    answer: "Yes. Quiet hours sync to calendars and map to chat mute rules."
                },
                {
                    id: 5,
                    question: "Can teams see when I am overloaded?",
                    answer: "Only if you opt in to share a simple status like take it light this week."
                },
                {
                    id: 6,
                    question: "How fast can I try it?",
                    answer: "A short setup, then a first week with focus mode and quiet hours goes live quickly."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Wellbeing Playbook",
                    modalTitle: "Get the Wellbeing Playbook"
                }
            }
        },
        seo: {
            title: "Wellness and workload guardrails for creators | Desilo",
            description: "Add focus modes, quiet hours, burnout signals, and coach handoffs. Protect energy, ship consistently, and build sustainable growth with privacy first controls.",
            keywords: "creator wellness, workload management, burnout prevention, focus modes, creator sustainability, work life balance, creator mental health, productivity tools",
            ogTitle: "Wellness and workload guardrails for creators | Desilo",
            ogDescription: "Add focus modes, quiet hours, burnout signals, and coach handoffs. Protect energy, ship consistently, and build sustainable growth with privacy first controls.",
            twitterTitle: "Wellness and workload guardrails for creators | Desilo",
            twitterDescription: "Add focus modes, quiet hours, burnout signals, and coach handoffs. Protect energy, ship consistently, and build sustainable growth with privacy first controls."
        }
    }
];

// ============================================================================
// AGENCY MANAGER ICP - Agencies and creator managers
// ============================================================================
const agencyManagerUseCases: UseCaseData[] = [
    {
        slug: "influencer-discovery-vetting",
        title: "Influencer discovery and vetting",
        description: "Find relevant creators with rich filters and automated verification so partnerships are authentic and on brand.",
        hero: {
            label: "USE CASE",
            title: "Influencer discovery and vetting",
            description: "Find relevant creators with rich filters and automated verification so partnerships are authentic and on brand.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the 5 Minute Growth Audit",
                    modalTitle: "Get the 5 Minute Growth Audit"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Agencies and creator managers who need fit, safety, and speed."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Search, screen, and shortlist with proof.",
            items: [
                {
                    id: 1,
                    title: "Unified creator profiles",
                    description: "One view of handles, bios, niches, rates, past work, and contact details.",
                },
                {
                    id: 2,
                    title: "Rich filters",
                    description: "Filter by platform, audience size, geography, language, category, growth trend, and engagement quality.",
                },
                {
                    id: 3,
                    title: "Audience authenticity checks",
                    description: "Signals for suspicious spikes, bot-like patterns, and paid-only engagement.",
                },
                {
                    id: 4,
                    title: "Brand-safety screening",
                    description: "Policy keywords, risky topics, and strike histories with human-review notes.",
                },
                {
                    id: 5,
                    title: "Brief matching",
                    description: "Auto-rank creators to a brief by niche, audience overlap, tone, and past performance.",
                },
                {
                    id: 6,
                    title: "Content fit and sentiment",
                    description: "Embeddings and classifiers score tone, topics, and alignment with brand values.",
                },
                {
                    id: 7,
                    title: "Rate intelligence",
                    description: "Benchmarks by platform and category with estimated CPMs and expected performance.",
                },
                {
                    id: 8,
                    title: "Disclosure history",
                    description: "Flags for FTC disclosures and patterns that raise risk.",
                },
                {
                    id: 9,
                    title: "Shortlist and outreach",
                    description: "Save lists, generate on-brand outreach, and track responses in a light CRM.",
                },
                {
                    id: 10,
                    title: "Export and share",
                    description: "Client-ready shortlists with evidence, screenshots, and performance estimates.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The outcomes that improve when discovery is data-driven.",
            items: [
                {
                    id: 1,
                    title: "Less chaos",
                    description: "one source of truth beats spreadsheets and DMs",
                },
                {
                    id: 2,
                    title: "Higher win rate",
                    description: "better fit and faster proposals land more deals",
                },
                {
                    id: 3,
                    title: "Lower risk",
                    description: "safety checks and disclosure histories prevent costly issues",
                },
                {
                    id: 4,
                    title: "Better margins",
                    description: "fewer false starts and tighter shortlists save hours",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A clear flow from brief to vetted shortlist.",
            steps: [
                {
                    title: "Intake the brief",
                    description: "Define goals, audience, regions, tone, and must-avoid topics."
                },
                {
                    title: "Search and filter",
                    description: "Pull from connected datasets and apply fit and safety filters."
                },
                {
                    title: "Score and verify",
                    description: "Run authenticity, sentiment, and policy checks. Attach evidence."
                },
                {
                    title: "Build the shortlist",
                    description: "Rank by fit and predicted performance. Add rate guidance and notes."
                },
                {
                    title: "Share and outreach",
                    description: "Export a client-ready pack. Send on-brand outreach and capture replies."
                },
                {
                    title: "Track outcomes",
                    description: "Log wins, costs, and performance so benchmarks improve each cycle."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show discovery is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Shortlist time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Win rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Safety incidents down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Cost per booked creator down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Campaign performance predictability up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Fast search, clean data, clear evidence.",
            items: [
                {
                    title: "Frontend",
                    description: "Manager-first UI with fast tables, filters, saved views, and bulk actions.",
                },
                {
                    title: "Backend",
                    description: "Services for creators, briefs, lists, and checks with idempotent APIs.",
                },
                {
                    title: "Data",
                    description: "Ingest pipelines, deduped profiles, and feature stores for fit and risk scores.",
                },
                {
                    title: "Models",
                    description: "Embeddings and classifiers for topic, tone, and brand alignment with human-review paths.",
                },
                {
                    title: "Integrations",
                    description: "Email and calendar, e-sign, storage, CRM, and ad platforms for performance data.",
                },
                {
                    title: "Evidence",
                    description: "Screenshots, links, and logs that make each score explainable.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that lift confidence and speed.",
            items: [
                {
                    title: "Look-alike finder",
                    description: "Expand a strong pick into similar creators by audience and tone.",
                },
                {
                    title: "Creator opt-in portal",
                    description: "Self-serve updates for rates and categories with basic verification.",
                },
                {
                    title: "Talent exclusions",
                    description: "Blocklists by brand or region to avoid conflicts.",
                },
                {
                    title: "Regional compliance pack",
                    description: "Policy presets by market with translation notes.",
                },
                {
                    title: "AI outreach assistant",
                    description: "Drafts personalized messages in your voice, with brief context.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Data you can defend with controls clients trust.",
            tags: [
                "Role-based access",
                "SSO and SCIM options",
                "audit logs",
                "encrypted PII",
                "consent records for creator submissions",
                "clear data-retention and deletion policies"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Where do profiles come from?",
                    answer: "From connected datasets and creator opt-ins. We dedupe and enrich with checks."
                },
                {
                    id: 2,
                    question: "Can we enforce custom brand-safety rules?",
                    answer: "Yes. Add policy keywords and risk categories per client."
                },
                {
                    id: 3,
                    question: "How accurate is \"audience authenticity\"?",
                    answer: "We combine multiple signals and always keep evidence visible for human review."
                },
                {
                    id: 4,
                    question: "Can we integrate with our CRM?",
                    answer: "Yes. Sync shortlists, outreach, and deal stages."
                },
                {
                    id: 5,
                    question: "How quickly can we pilot?",
                    answer: "A short discovery, then a few weeks to a live search and vetted shortlist."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the 5 Minute Growth Audit",
                    modalTitle: "Get the 5 Minute Growth Audit"
                }
            }
        },
        seo: {
            title: "Influencer discovery and vetting for agencies | Desilo",
            description: "Find relevant creators with rich filters, authenticity checks, brand-safety screening, rate intelligence, and client-ready shortlists. Increase win rate and reduce risk.",
            keywords: "Influencer discovery, creator vetting, influencer marketing, creator search, brand safety, influencer analytics, creator management",
            ogTitle: "Influencer discovery and vetting for agencies | Desilo",
            ogDescription: "Find relevant creators with rich filters, authenticity checks, brand-safety screening, rate intelligence, and client-ready shortlists. Increase win rate and reduce risk.",
            twitterTitle: "Influencer discovery and vetting for agencies | Desilo",
            twitterDescription: "Find relevant creators with rich filters, authenticity checks, brand-safety screening, rate intelligence, and client-ready shortlists. Increase win rate and reduce risk."
        }
    },
    {
        slug: "end-to-end-campaign-management",
        title: "End to end campaign management",
        description: "Organize multi channel campaigns and manage deliverables, deadlines, and approvals in one place.",
        hero: {
            label: "USE CASE",
            title: "End to end campaign management",
            description: "Organize multi channel campaigns and manage deliverables, deadlines, and approvals in one place.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the 5 Minute Growth Audit",
                    modalTitle: "Get the 5 Minute Growth Audit"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Agencies and creator managers running many campaigns at once."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "The operating system for campaigns from brief to billing.",
            items: [
                {
                    id: 1,
                    title: "Campaign workspaces",
                    description: "One hub for goals, channels, budgets, and timelines with roles and permissions.",
                },
                {
                    id: 2,
                    title: "Briefs and scopes",
                    description: "Structured deliverables, usage terms, rates, and required checkpoints.",
                },
                {
                    id: 3,
                    title: "Creator assignments",
                    description: "Match and book creators, set splits, and track availability and status.",
                },
                {
                    id: 4,
                    title: "Calendars and milestones",
                    description: "Publish schedules with due dates, reminders, and dependencies.",
                },
                {
                    id: 5,
                    title: "Asset and content management",
                    description: "Upload, version, comment, and approve content with clear status.",
                },
                {
                    id: 6,
                    title: "Approval routing",
                    description: "Legal, brand, and client sign off paths with change logs.",
                },
                {
                    id: 7,
                    title: "Disclosure and policy checks",
                    description: "Inline FTC disclosure prompts and flagged content for review.",
                },
                {
                    id: 8,
                    title: "Payouts and invoicing",
                    description: "Generate invoices, track payouts and splits, and reconcile to budgets.",
                },
                {
                    id: 9,
                    title: "Performance and attribution",
                    description: "UTMs, coupon codes, and dashboards for reach, engagement, and revenue.",
                },
                {
                    id: 10,
                    title: "Client portal",
                    description: "Share live status, assets, and results without back and forth emails.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when campaigns run on rails.",
            items: [
                {
                    id: 1,
                    title: "Less chaos",
                    description: "one source of truth beats spreadsheets and DMs",
                },
                {
                    id: 2,
                    title: "Higher win rate",
                    description: "clearer timelines and faster approvals close more deals",
                },
                {
                    id: 3,
                    title: "More capacity",
                    description: "one manager handles more creators with fewer misses",
                },
                {
                    id: 4,
                    title: "Trusted results",
                    description: "branded reports and clean reconciliations make renewals easier",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A clear flow from intake to results.",
            steps: [
                {
                    title: "Capture the brief",
                    description: "Goals, audience, channels, deliverables, brand rules, and budget."
                },
                {
                    title: "Plan and staff",
                    description: "Create timelines, assign creators, and set approvals and checklists."
                },
                {
                    title: "Produce and review",
                    description: "Collect assets, comment, version, and route for sign off."
                },
                {
                    title: "Launch and monitor",
                    description: "Schedule posts, track disclosures, and watch performance in real time."
                },
                {
                    title: "Pay and reconcile",
                    description: "Payout creators, invoice the client, and match costs to budget."
                },
                {
                    title: "Report and learn",
                    description: "Publish branded results and feed benchmarks back into rate and fit models."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show operations are working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Cycle time to approval down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Missed deadlines down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Creators per manager up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Budget variance down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Client renewal rate up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Fast UI, reliable workflows, and explainable status.",
            items: [
                {
                    title: "Frontend",
                    description: "Manager-first tables and boards with filters, bulk actions, and inline comments.",
                },
                {
                    title: "Backend",
                    description: "Services for briefs, assets, approvals, payouts, and reports with idempotent APIs.",
                },
                {
                    title: "Integrations",
                    description: "Email and calendar, e-sign, storage, payments, accounting, CRM, and ad platforms.",
                },
                {
                    title: "Data",
                    description: "Event map for status changes, approvals, deliveries, spend, and results.",
                },
                {
                    title: "Ops",
                    description: "Roles and permissions, logs and alerts, and SLA dashboards for teams.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that reduce manual work and raise confidence.",
            items: [
                {
                    title: "Rate card intelligence",
                    description: "Guidance from past wins and performance by category and channel.",
                },
                {
                    title: "Auto disclosure verifier",
                    description: "Collects post URLs and screenshots and logs proof of disclosure.",
                },
                {
                    title: "Talent and brand portals",
                    description: "Self-serve uploads, approvals, and updates with scoped access.",
                },
                {
                    title: "Template library",
                    description: "Briefs, SOWs, shot lists, and content outlines that speed setup.",
                },
                {
                    title: "AI assistant",
                    description: "Drafts briefs, comments, and recaps in your brand voice.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls clients and finance will trust.",
            tags: [
                "Role based access",
                "SSO and SCIM options",
                "encrypted PII",
                "signed webhooks",
                "immutable audit trails",
                "data retention and deletion policies are configurable per client"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Can this replace our PM tool or connect to it?",
                    answer: "Either. Run campaigns here or sync key objects with your PM tool."
                },
                {
                    id: 2,
                    question: "How do clients see status without email threads?",
                    answer: "Client portals show timelines, approvals, assets, and results in real time."
                },
                {
                    id: 3,
                    question: "Can we enforce custom approval paths?",
                    answer: "Yes. Create role based routes and required checkpoints by campaign type."
                },
                {
                    id: 4,
                    question: "Do payouts and invoices live here?",
                    answer: "Yes. Track splits and payouts and generate invoices or export to accounting."
                },
                {
                    id: 5,
                    question: "How fast can we pilot?",
                    answer: "A short discovery, then a few weeks to a live campaign with your team."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the 5 Minute Growth Audit",
                    modalTitle: "Get the 5 Minute Growth Audit"
                }
            }
        },
        seo: {
            title: "End to end campaign management for agencies | Desilo",
            description: "Organize multi channel campaigns and manage deliverables, deadlines, approvals, payouts, and reporting in one place. Increase capacity, speed, and client trust.",
            keywords: "Campaign management, creator campaigns, campaign tracking, agency management, campaign workflow, creator management, campaign operations",
            ogTitle: "End to end campaign management for agencies | Desilo",
            ogDescription: "Organize multi channel campaigns and manage deliverables, deadlines, approvals, payouts, and reporting in one place. Increase capacity, speed, and client trust.",
            twitterTitle: "End to end campaign management for agencies | Desilo",
            twitterDescription: "Organize multi channel campaigns and manage deliverables, deadlines, approvals, payouts, and reporting in one place. Increase capacity, speed, and client trust."
        }
    },
    {
        slug: "collaborative-content-planning",
        title: "Collaborative content planning",
        description: "Co-design, review, and schedule posts with real time approvals and feedback.",
        hero: {
            label: "USE CASE",
            title: "Collaborative content planning",
            description: "Co-design, review, and schedule posts with real time approvals and feedback.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the 5 Minute Growth Audit",
                    modalTitle: "Get the 5 Minute Growth Audit"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Agencies and creator managers coordinating many posts across many teams."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "A shared workspace for ideas to approved posts.",
            items: [
                {
                    id: 1,
                    title: "Campaign and content calendars",
                    description: "Plan across platforms with filters by client, creator, channel, and status.",
                },
                {
                    id: 2,
                    title: "Briefed content cards",
                    description: "Each post carries goal, format, copy, assets, links, hashtags, and disclosure notes.",
                },
                {
                    id: 3,
                    title: "Real time comments and markup",
                    description: "Pin feedback on frames or copy and resolve threads with clear owners.",
                },
                {
                    id: 4,
                    title: "Approval routing",
                    description: "Role-based steps for creator, brand, legal, and final sign-off with change logs.",
                },
                {
                    id: 5,
                    title: "Versioning and history",
                    description: "See what changed, who changed it, and roll back safely.",
                },
                {
                    id: 6,
                    title: "Asset library",
                    description: "Centralized files with variants, rights info, and usage expiries.",
                },
                {
                    id: 7,
                    title: "Scheduling and posting",
                    description: "Queue and schedule per platform or export to your social tool.",
                },
                {
                    id: 8,
                    title: "Checklists and disclosures",
                    description: "Required fields and FTC prompts baked into the post template.",
                },
                {
                    id: 9,
                    title: "Performance context",
                    description: "Surface past performance, best times, and benchmarks next to the card.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when planning is collaborative.",
            items: [
                {
                    id: 1,
                    title: "Less chaos",
                    description: "one source of truth beats spreadsheets and DMs",
                },
                {
                    id: 2,
                    title: "Higher win rate",
                    description: "faster approvals and on-brand posts close more deals",
                },
                {
                    id: 3,
                    title: "More capacity",
                    description: "one manager coordinates more creators with fewer misses",
                },
                {
                    id: 4,
                    title: "Trusted results",
                    description: "consistent briefs and history make reporting and renewals easier",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A clear flow from idea to scheduled post.",
            steps: [
                {
                    title: "Create the plan",
                    description: "Set goals, channels, and cadence. Build the calendar for the month or quarter."
                },
                {
                    title: "Add content cards",
                    description: "Attach copy, assets, links, and disclosure fields to each slot."
                },
                {
                    title: "Collaborate and review",
                    description: "Comment in real time, request edits, and resolve with owners."
                },
                {
                    title: "Approve and schedule",
                    description: "Route to the right people and schedule or export with one click."
                },
                {
                    title: "Measure and learn",
                    description: "Tag UTMs, watch results, and feed benchmarks back into the calendar."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show planning is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Time to approval down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Last minute edits down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "On-time publishing up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Creators per manager up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Campaign performance predictability up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Fast UI, clear workflows, and explainable status.",
            items: [
                {
                    title: "Frontend",
                    description: "Calendars, boards, and side-by-side preview with fast filters and bulk actions.",
                },
                {
                    title: "Backend",
                    description: "Services for posts, assets, approvals, schedules, and comments with idempotent APIs.",
                },
                {
                    title: "Integrations",
                    description: "Storage, e-sign, email and calendar, social posting tools, analytics, and CRM.",
                },
                {
                    title: "Data",
                    description: "Event map for status changes, approvals, scheduled posts, and performance.",
                },
                {
                    title: "Ops",
                    description: "Roles and permissions, change logs, notifications, and SLA dashboards.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that speed creation and keep quality high.",
            items: [
                {
                    title: "AI copy assistant",
                    description: "Drafts on-brand variations, hashtags, and alt text.",
                },
                {
                    title: "Template library",
                    description: "Reusable briefs, caption frameworks, and platform checklists.",
                },
                {
                    title: "Shot lists and storyboards",
                    description: "Pre-production tools for photo and video teams.",
                },
                {
                    title: "Best-time recommender",
                    description: "Suggests slots per audience and channel.",
                },
                {
                    title: "Client portal",
                    description: "Invite clients to comment and approve without exposing internal tools.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls clients and creators trust.",
            tags: [
                "Role-based access",
                "SSO and SCIM options",
                "encrypted PII",
                "signed webhooks",
                "full audit trails",
                "rights metadata and expiries travel with assets"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Can clients review without logging into our internal tools?",
                    answer: "Yes. Share scoped client-portal links with comment and approve permissions."
                },
                {
                    id: 2,
                    question: "Will this replace our posting tool?",
                    answer: "You can post directly or export to the tool you use today."
                },
                {
                    id: 3,
                    question: "How do we enforce brand and disclosure rules?",
                    answer: "Templates add required fields and inline checks before approval."
                },
                {
                    id: 4,
                    question: "Can creators see only their tasks?",
                    answer: "Yes. Roles and views limit access to assigned cards and assets."
                },
                {
                    id: 5,
                    question: "How fast can we pilot?",
                    answer: "A short discovery, then a few weeks to a live calendar and approvals."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the 5 Minute Growth Audit",
                    modalTitle: "Get the 5 Minute Growth Audit"
                }
            }
        },
        seo: {
            title: "Collaborative content planning for agencies | Desilo",
            description: "Co-design, review, and schedule posts with real-time approvals, version history, and performance context. Reduce chaos and publish on time across channels.",
            keywords: "Content planning, collaborative content, content calendar, content approval, content management, social media planning, content workflow",
            ogTitle: "Collaborative content planning for agencies | Desilo",
            ogDescription: "Co-design, review, and schedule posts with real-time approvals, version history, and performance context. Reduce chaos and publish on time across channels.",
            twitterTitle: "Collaborative content planning for agencies | Desilo",
            twitterDescription: "Co-design, review, and schedule posts with real-time approvals, version history, and performance context. Reduce chaos and publish on time across channels."
        }
    },
    {
        slug: "performance-analytics-reporting",
        title: "Performance analytics and reporting",
        description: "Track reach, engagement, conversions, and ROI with client ready dashboards and exports.",
        hero: {
            label: "USE CASE",
            title: "Performance analytics and reporting",
            description: "Track reach, engagement, conversions, and ROI with client ready dashboards and exports.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the 5 Minute Growth Audit",
                    modalTitle: "Get the 5 Minute Growth Audit"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Agencies and creator managers who need clear results clients can trust."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "End to end measurement from post to revenue.",
            items: [
                {
                    id: 1,
                    title: "Unified dashboards",
                    description: "One place for reach, engagement, clicks, conversions, and revenue by creator, campaign, and channel.",
                },
                {
                    id: 2,
                    title: "Attribution model options",
                    description: "First touch, last touch, and assisted views so you explain influence, not guess.",
                },
                {
                    id: 3,
                    title: "UTM and link management",
                    description: "Auto tag links and route short links so source and campaign are always known.",
                },
                {
                    id: 4,
                    title: "Coupon and code tracking",
                    description: "Map redemptions to creators and campaigns for clean ROI views.",
                },
                {
                    id: 5,
                    title: "Commerce and subscription hooks",
                    description: "Pull orders, AOV, trials, churn, and LTV to show real business impact.",
                },
                {
                    id: 6,
                    title: "Media and content analytics",
                    description: "Views, watch time, saves, and shares with post level rollups.",
                },
                {
                    id: 7,
                    title: "Cost and margin views",
                    description: "Spend, payouts, and fees alongside revenue so margin is visible.",
                },
                {
                    id: 8,
                    title: "Benchmarks and alerts",
                    description: "See normal ranges and get nudges when metrics drift.",
                },
                {
                    id: 9,
                    title: "Client ready exports",
                    description: "Branded PDFs, decks, and CSVs that match your reporting cadence.",
                },
                {
                    id: 10,
                    title: "API and warehouse sync",
                    description: "Send clean events and tables to your BI tool or warehouse.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when measurement is clear.",
            items: [
                {
                    id: 1,
                    title: "Less chaos",
                    description: "one source of truth beats spreadsheets and screenshots",
                },
                {
                    id: 2,
                    title: "Higher win rate",
                    description: "defensible ROI secures renewals and bigger briefs",
                },
                {
                    id: 3,
                    title: "More capacity",
                    description: "automated recaps free managers to run more campaigns",
                },
                {
                    id: 4,
                    title: "Trusted results",
                    description: "consistent methods and branded views build confidence",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A simple path from raw signals to client ready insight.",
            steps: [
                {
                    title: "Instrument the funnel",
                    description: "Standard UTMs, codes, and events for impressions, clicks, and purchases."
                },
                {
                    title: "Connect the systems",
                    description: "Pull from ads, social, web, commerce, and subscription tools."
                },
                {
                    title: "Normalize and model",
                    description: "Clean names, dedupe, and apply the chosen attribution models."
                },
                {
                    title: "Build the views",
                    description: "Dashboards by creator, campaign, and client with filters and notes."
                },
                {
                    title: "Report and improve",
                    description: "Schedule exports and use benchmarks and alerts to guide tests."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show analytics are working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Report prep time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Data corrections down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Renewal rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Cost per booked creator down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Predictability of ROI up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Clean data, explainable models, and fast views.",
            items: [
                {
                    title: "Frontend",
                    description: "Fast dashboards with filters, segments, and date ranges and client mode for sharing.",
                },
                {
                    title: "Backend",
                    description: "Services for events, sessions, orders, and payouts with idempotent ingestion.",
                },
                {
                    title: "Attribution and models",
                    description: "Rule based models with clear inputs and explanations for each metric.",
                },
                {
                    title: "Data pipelines",
                    description: "ETL to the warehouse, schema versioning, and late data handling.",
                },
                {
                    title: "Integrations",
                    description: "Ads and social APIs, link shorteners, commerce and subscription platforms, and CRM.",
                },
                {
                    title: "Ops",
                    description: "Monitors for failed pulls, row counts, and freshness with alerting.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that raise confidence and speed.",
            items: [
                {
                    title: "Creator scorecard",
                    description: "Standard health metrics per creator with targets and trends.",
                },
                {
                    title: "ROI narrative blocks",
                    description: "Auto write highlights and lowlights for the weekly recap.",
                },
                {
                    title: "Lift tests",
                    description: "Geos or holdouts to estimate incremental impact.",
                },
                {
                    title: "Client portal",
                    description: "Read only dashboards with comments and scheduled emails.",
                },
                {
                    title: "BI pack",
                    description: "Prebuilt warehouse schemas and dashboards for your existing BI tool.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls clients and finance will trust.",
            tags: [
                "Role based access",
                "SSO and SCIM options",
                "audit logs",
                "encrypted PII",
                "data retention policies",
                "access tokens are vaulted and rotated"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Can we keep our current BI tool?",
                    answer: "Yes. Sync modeled tables to your warehouse and layer your BI on top."
                },
                {
                    id: 2,
                    question: "Do you support creator specific attribution?",
                    answer: "Yes. UTMs, codes, and link routing map conversions to creators and campaigns."
                },
                {
                    id: 3,
                    question: "What if platforms throttle APIs?",
                    answer: "We queue pulls, backfill late data, and flag gaps in freshness."
                },
                {
                    id: 4,
                    question: "Can we compare against past campaigns?",
                    answer: "Benchmarks show typical ranges and highlight changes over time."
                },
                {
                    id: 5,
                    question: "How do clients get reports?",
                    answer: "Schedule branded PDFs or grant portal access with scoped views."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the 5 Minute Growth Audit",
                    modalTitle: "Get the 5 Minute Growth Audit"
                }
            }
        },
        seo: {
            title: "Performance analytics and reporting for agencies | Desilo",
            description: "Track reach, engagement, conversions, and ROI with unified dashboards, attribution, clean exports, and warehouse sync. Win renewals with clear, trusted results.",
            keywords: "Performance analytics, campaign reporting, ROI tracking, attribution modeling, creator analytics, campaign measurement, performance dashboards",
            ogTitle: "Performance analytics and reporting for agencies | Desilo",
            ogDescription: "Track reach, engagement, conversions, and ROI with unified dashboards, attribution, clean exports, and warehouse sync. Win renewals with clear, trusted results.",
            twitterTitle: "Performance analytics and reporting for agencies | Desilo",
            twitterDescription: "Track reach, engagement, conversions, and ROI with unified dashboards, attribution, clean exports, and warehouse sync. Win renewals with clear, trusted results."
        }
    },
    {
        slug: "automated-outreach-crm",
        title: "Automated outreach and CRM",
        description: "Bulk messaging, pipeline tracking, contract templates, and centralized relationship management.",
        hero: {
            label: "USE CASE",
            title: "Automated outreach and CRM",
            description: "Bulk messaging, pipeline tracking, contract templates, and centralized relationship management.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the 5 Minute Growth Audit",
                    modalTitle: "Get the 5 Minute Growth Audit"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Agencies and creator managers scaling outreach and follow ups."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Prospect, personalize, negotiate, and close in one workspace.",
            items: [
                {
                    id: 1,
                    title: "Contact graph",
                    description: "Creators and brand contacts with roles, notes, history, and consent flags.",
                },
                {
                    id: 2,
                    title: "Lists and segments",
                    description: "Build dynamic lists by niche, platform, region, tags, and performance.",
                },
                {
                    id: 3,
                    title: "Bulk and sequenced outreach",
                    description: "Personalized email or DM sequences with safe daily limits and reply tracking.",
                },
                {
                    id: 4,
                    title: "Templates and snippets",
                    description: "On-brand outreach, follow ups, and negotiation blocks your team can reuse.",
                },
                {
                    id: 5,
                    title: "Pipeline and stages",
                    description: "Track outreach, brief review, negotiation, booked, delivered, and paid with owners and SLAs.",
                },
                {
                    id: 6,
                    title: "Tasks and reminders",
                    description: "Auto-create follow ups and due dates based on stage and activity.",
                },
                {
                    id: 7,
                    title: "Contract templates and e-sign",
                    description: "Standard MSAs and SOWs with variables, version history, and signatures.",
                },
                {
                    id: 8,
                    title: "Rates and proposals",
                    description: "Generate rate cards and proposals with benchmark guidance.",
                },
                {
                    id: 9,
                    title: "Notes and activity timeline",
                    description: "One history for emails, calls, comments, and document changes.",
                },
                {
                    id: 10,
                    title: "Reporting and exports",
                    description: "Win rate, cycle time, value by stage, and creator or brand health with CSVs and PDFs.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when outreach is a system.",
            items: [
                {
                    id: 1,
                    title: "Less chaos",
                    description: "one source of truth beats spreadsheets and DMs",
                },
                {
                    id: 2,
                    title: "Higher win rate",
                    description: "faster responses and clearer proposals close more deals",
                },
                {
                    id: 3,
                    title: "More capacity",
                    description: "one manager runs more outreach without missed follow ups",
                },
                {
                    id: 4,
                    title: "Trusted results",
                    description: "consistent templates and logs make renewals easier",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A clear flow from prospect to signed deal.",
            steps: [
                {
                    title: "Import and segment",
                    description: "Bring in contacts, dedupe, and tag by niche, region, and fit."
                },
                {
                    title: "Compose and sequence",
                    description: "Personalize templates, set steps, and schedule sends with throttles."
                },
                {
                    title: "Track and advance",
                    description: "Move deals through stages, attach briefs, and record decisions."
                },
                {
                    title: "Negotiate and sign",
                    description: "Generate proposals, send contracts for e-sign, and log changes."
                },
                {
                    title: "Handoff to delivery",
                    description: "Push booked deals to the campaign workspace and set payouts and tasks."
                },
                {
                    title: "Measure and improve",
                    description: "Review win rate, cycle time, and reply quality and tune templates weekly."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show outreach and CRM are working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Reply rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Win rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Cycle time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Missed follow ups down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Creators per manager up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Booked value predictability up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Fast UI, reliable sends, clear pipelines.",
            items: [
                {
                    title: "Frontend",
                    description: "Manager-first tables, boards, and compose windows with merge fields and previews.",
                },
                {
                    title: "Backend",
                    description: "Services for contacts, stages, emails, templates, and signatures with idempotent APIs.",
                },
                {
                    title: "Messaging",
                    description: "Email and optional DM connectors with throttles, warmup, and deliverability checks.",
                },
                {
                    title: "Data",
                    description: "Event map for sends, opens, replies, stage changes, and bookings.",
                },
                {
                    title: "Integrations",
                    description: "E-sign, storage, calendar, accounting, payments, and your central CRM.",
                },
                {
                    title: "Ops",
                    description: "Roles and permissions, logs and alerts, rate limit guards, and bounce handling.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that raise speed and confidence.",
            items: [
                {
                    title: "AI outreach assistant",
                    description: "Drafts personalized emails and follow ups in your tone with brief context.",
                },
                {
                    title: "Lead enrichment",
                    description: "Auto-pull platform stats, niches, and recent posts.",
                },
                {
                    title: "Do-not-contact and conflicts",
                    description: "Blocklists by brand or region with alerts.",
                },
                {
                    title: "Multilingual templates",
                    description: "Localized outreach and contract packs.",
                },
                {
                    title: "Client portal",
                    description: "Share shortlists and outreach status without exposing internal tools.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls clients and partners trust.",
            tags: [
                "Role based access",
                "SSO and SCIM options",
                "encrypted PII",
                "consent tracking",
                "signed webhooks",
                "data retention and deletion policies are configurable",
                "audit logs record every change"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Will this replace our CRM or connect to it?",
                    answer: "Either. Run outreach here or sync contacts, deals, and stages to your CRM."
                },
                {
                    id: 2,
                    question: "How do we avoid spam issues?",
                    answer: "We throttle sends, warm domains, honor consent flags, and track bounces."
                },
                {
                    id: 3,
                    question: "Can we use our own contract templates?",
                    answer: "Yes. Load templates with variables and lock required clauses."
                },
                {
                    id: 4,
                    question: "Does this support brand and creator outreach?",
                    answer: "Yes. Segment and template separately while keeping one pipeline."
                },
                {
                    id: 5,
                    question: "How fast can we pilot?",
                    answer: "A short discovery, then a few weeks to live sequences and a working pipeline."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the 5 Minute Growth Audit",
                    modalTitle: "Get the 5 Minute Growth Audit"
                }
            }
        },
        seo: {
            title: "Automated outreach and CRM for agencies | Desilo",
            description: "Bulk messaging, pipeline tracking, contract templates, and centralized relationship management. Increase win rate, reduce chaos, and scale outreach with confidence.",
            keywords: "Automated outreach, CRM for agencies, creator outreach, pipeline management, relationship management, outreach automation, creator CRM",
            ogTitle: "Automated outreach and CRM for agencies | Desilo",
            ogDescription: "Bulk messaging, pipeline tracking, contract templates, and centralized relationship management. Increase win rate, reduce chaos, and scale outreach with confidence.",
            twitterTitle: "Automated outreach and CRM for agencies | Desilo",
            twitterDescription: "Bulk messaging, pipeline tracking, contract templates, and centralized relationship management. Increase win rate, reduce chaos, and scale outreach with confidence."
        }
    },
    {
        slug: "legal-contract-workflow-management",
        title: "Legal and contract workflow management",
        description: "Author contracts, collect e signatures, schedule payments, and stay compliant.",
        hero: {
            label: "USE CASE",
            title: "Legal and contract workflow management",
            description: "Author contracts, collect e signatures, schedule payments, and stay compliant.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Compliance Checklist",
                    modalTitle: "Get the Compliance Checklist"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Agencies and creator managers that need clean paperwork without slowing deals."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "From authoring to payout in one workspace.",
            items: [
                {
                    id: 1,
                    title: "Template library",
                    description: "Preapproved MSAs, SOWs, and addenda with variables, locked clauses, and version history.",
                },
                {
                    id: 2,
                    title: "Smart authoring",
                    description: "Guided fields for rates, splits, usage rights, territories, and expiries to prevent mistakes.",
                },
                {
                    id: 3,
                    title: "E signature flows",
                    description: "One click signatures for creators and brands with reminders and completion proofs.",
                },
                {
                    id: 4,
                    title: "Approval routing",
                    description: "Role based paths for legal, brand, finance, and client sign off with change logs.",
                },
                {
                    id: 5,
                    title: "Payment schedules",
                    description: "Milestone, delivery, and net terms with automatic invoice and payout creation.",
                },
                {
                    id: 6,
                    title: "Rights and disclosure checks",
                    description: "Usage terms and FTC prompts recorded alongside each contract.",
                },
                {
                    id: 7,
                    title: "KYC and tax capture",
                    description: "Collect IDs, W-9 or W-8, GST or TDS details, and payout info securely.",
                },
                {
                    id: 8,
                    title: "Contract status and search",
                    description: "Find any agreement by creator, brand, campaign, date, or clause.",
                },
                {
                    id: 9,
                    title: "Audit trail and exports",
                    description: "Immutable logs, document packs, and CSVs for finance and compliance.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when legal runs on rails.",
            items: [
                {
                    id: 1,
                    title: "Less chaos",
                    description: "one source of truth beats spreadsheets and DMs",
                },
                {
                    id: 2,
                    title: "Higher win rate",
                    description: "faster contracts and clearer terms close more deals",
                },
                {
                    id: 3,
                    title: "More capacity",
                    description: "managers spend less time chasing signatures and invoices",
                },
                {
                    id: 4,
                    title: "Trusted results",
                    description: "clean records and payouts make renewals easier",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A clear flow from draft to paid.",
            steps: [
                {
                    title: "Set policy and templates",
                    description: "Load your clauses, locked sections, regions, and approval rules."
                },
                {
                    title: "Author and review",
                    description: "Fill guided fields, check disclosures and rights, and route for approval."
                },
                {
                    title: "Sign and store",
                    description: "Send for e signature, collect proofs, and archive with searchable metadata."
                },
                {
                    title: "Schedule and pay",
                    description: "Create invoices or payouts on milestones or delivery and track status."
                },
                {
                    title: "Report and audit",
                    description: "Export summaries for finance, tax, and compliance with full change history."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show the workflow is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Cycle time to signature down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Errors in terms down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Late payouts down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Finance close time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Client renewal rate up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Secure data, explainable workflows, reliable records.",
            items: [
                {
                    title: "Frontend",
                    description: "Manager-first UI for drafting, approvals, and status with fast tables and filters.",
                },
                {
                    title: "Backend",
                    description: "Services for documents, signatures, rights, disclosures, invoices, and payouts with idempotent APIs.",
                },
                {
                    title: "Data",
                    description: "Encrypted PII, structured contract fields, immutable audit logs, and document storage.",
                },
                {
                    title: "Integrations",
                    description: "E-sign, storage, payments, accounting, CRM, and identity providers.",
                },
                {
                    title: "Internationalization",
                    description: "Jurisdiction aware templates, currency handling, and locale validation.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that reduce risk and manual work.",
            items: [
                {
                    title: "Clause library",
                    description: "Preapproved language and fallbacks to speed negotiations.",
                },
                {
                    title: "Disclosure verifier",
                    description: "Collects post URLs and screenshots and stores proof against the contract.",
                },
                {
                    title: "Rate card guidance",
                    description: "Benchmarks for platform and category to inform proposals.",
                },
                {
                    title: "Vendor and client portals",
                    description: "Self-serve access for uploads, approvals, and document pulls.",
                },
                {
                    title: "AI contract assistant",
                    description: "Flags risky edits, missing fields, and summarizes changes in plain language.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls clients and finance will trust. We provide the rails and records. Your counsel owns final templates and policy.",
            tags: [
                "Role based access",
                "SSO and SCIM options",
                "encryption at rest and in transit",
                "signed webhooks",
                "clear retention policies"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Can we use our own contract templates?",
                    answer: "Yes. Upload templates, lock required clauses, and add variables."
                },
                {
                    id: 2,
                    question: "How do approvals work for different clients?",
                    answer: "Create custom routes by client or campaign type with required checkpoints."
                },
                {
                    id: 3,
                    question: "Do payouts and invoices tie to the contract?",
                    answer: "Yes. Schedules live on the contract and trigger invoices and payouts automatically."
                },
                {
                    id: 4,
                    question: "Can we handle multiple regions and taxes?",
                    answer: "Yes. Templates support regional terms, currencies, and tax capture such as W-9, W-8, GST, and TDS."
                },
                {
                    id: 5,
                    question: "What if a creator edits a clause?",
                    answer: "Changes are logged, flagged for review, and summarized for approvers before signature."
                },
                {
                    id: 6,
                    question: "How quickly can we pilot?",
                    answer: "A short discovery, then a few weeks to live templates, e-sign, and scheduled payouts."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Compliance Checklist",
                    modalTitle: "Get the Compliance Checklist"
                }
            }
        },
        seo: {
            title: "Legal and contract workflow management for agencies | Desilo",
            description: "Author contracts, collect e signatures, schedule payments, and stay compliant. One workspace with templates, approvals, rights checks, and audit-ready records.",
            keywords: "Legal workflow, contract management, e-signature, contract automation, legal compliance, contract templates, legal workflow automation",
            ogTitle: "Legal and contract workflow management for agencies | Desilo",
            ogDescription: "Author contracts, collect e signatures, schedule payments, and stay compliant. One workspace with templates, approvals, rights checks, and audit-ready records.",
            twitterTitle: "Legal and contract workflow management for agencies | Desilo",
            twitterDescription: "Author contracts, collect e signatures, schedule payments, and stay compliant. One workspace with templates, approvals, rights checks, and audit-ready records."
        }
    },
    {
        slug: "scalable-team-collaboration",
        title: "Scalable team collaboration",
        description: "Multi user roles, task assignment, creator rosters, and progress tracking as you scale.",
        hero: {
            label: "USE CASE",
            title: "Scalable team collaboration",
            description: "Multi user roles, task assignment, creator rosters, and progress tracking as you scale.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the 5 Minute Growth Audit",
                    modalTitle: "Get the 5 Minute Growth Audit"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Agencies and creator managers adding people, clients, and campaigns fast."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "The collaboration stack for growing teams.",
            items: [
                {
                    id: 1,
                    title: "Roles and permissions",
                    description: "Granular access for admins, managers, creators, finance, and clients.",
                },
                {
                    id: 2,
                    title: "Creator rosters",
                    description: "Group creators by client, category, region, or squad and assign owners.",
                },
                {
                    id: 3,
                    title: "Task assignment",
                    description: "Create tasks from briefs and stages with owners, dates, and SLAs.",
                },
                {
                    id: 4,
                    title: "Workboards and calendars",
                    description: "Kanban, list, and calendar views by client, campaign, and creator.",
                },
                {
                    id: 5,
                    title: "Checklists and SOPs",
                    description: "Reusable steps for briefs, approvals, disclosures, and payouts.",
                },
                {
                    id: 6,
                    title: "Mentions and comments",
                    description: "Real time collaboration on posts, assets, and contracts with history.",
                },
                {
                    id: 7,
                    title: "Notifications and reminders",
                    description: "Smart alerts for deadlines, approvals, and blocked tasks.",
                },
                {
                    id: 8,
                    title: "Capacity and workload views",
                    description: "See who is overloaded and rebalance before deadlines slip.",
                },
                {
                    id: 9,
                    title: "Templates and automations",
                    description: "Auto create tasks from stage changes and apply playbooks by campaign type.",
                },
                {
                    id: 10,
                    title: "Reporting and exports",
                    description: "Progress by client and creator with CSVs and branded status decks.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when collaboration is structured.",
            items: [
                {
                    id: 1,
                    title: "Less chaos",
                    description: "one source of truth beats spreadsheets and DMs",
                },
                {
                    id: 2,
                    title: "Higher win rate",
                    description: "faster, on brand execution secures renewals and bigger briefs",
                },
                {
                    id: 3,
                    title: "More capacity",
                    description: "one manager coordinates more creators with fewer misses",
                },
                {
                    id: 4,
                    title: "Trusted results",
                    description: "consistent process and visibility build client confidence",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A clear path from scattered work to a coordinated team.",
            steps: [
                {
                    title: "Set roles and rosters",
                    description: "Define permissions and assign creators to owners and squads."
                },
                {
                    title: "Model your workflows",
                    description: "Turn briefs and stages into tasks and SOPs with SLAs."
                },
                {
                    title: "Plan the week",
                    description: "Use boards and calendars to set priorities and deadlines."
                },
                {
                    title: "Collaborate in context",
                    description: "Comment where work happens and resolve with clear owners."
                },
                {
                    title: "Track and adjust",
                    description: "Watch capacity and shift tasks before bottlenecks form."
                },
                {
                    title: "Report and improve",
                    description: "Share branded status and tune SOPs based on cycle time and misses."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show collaboration is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Missed deadlines down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Cycle time to approval down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Creators per manager up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Rework down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Client renewal rate up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Fast UI, dependable workflows, clear visibility.",
            items: [
                {
                    title: "Frontend",
                    description: "Boards, lists, and calendars with bulk actions, filters, and keyboard shortcuts.",
                },
                {
                    title: "Backend",
                    description: "Services for users, roles, tasks, comments, approvals, and notifications.",
                },
                {
                    title: "Integrations",
                    description: "Email and calendar, storage, e-sign, CRM, accounting, and posting tools.",
                },
                {
                    title: "Data",
                    description: "Event map for task changes, approvals, disclosures, payouts, and results.",
                },
                {
                    title: "Ops",
                    description: "Audit logs, SLA tracking, alerts, and environment separation for clients.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that raise speed and quality.",
            items: [
                {
                    title: "Template packs",
                    description: "Playbooks for common campaign types with prefilled tasks and checklists.",
                },
                {
                    title: "Best time recommender",
                    description: "Suggests publish windows by audience and channel.",
                },
                {
                    title: "AI assistant",
                    description: "Drafts briefs, comments, and recaps in your brand voice.",
                },
                {
                    title: "Client portal",
                    description: "Scoped views for status, approvals, and assets without exposing internal tools.",
                },
                {
                    title: "Cross team permissions",
                    description: "Share creators and assets across squads with safe boundaries.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls clients and finance will trust. Rights metadata travels with assets and actions are fully auditable.",
            tags: [
                "Role based access",
                "SSO and SCIM options",
                "encrypted PII",
                "signed webhooks",
                "configurable retention"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Can we mirror our current workflow?",
                    answer: "Yes. Model your stages, tasks, and SOPs, then automate repeats."
                },
                {
                    id: 2,
                    question: "Do creators and clients need full access?",
                    answer: "No. Use scoped roles and client portals for limited collaboration."
                },
                {
                    id: 3,
                    question: "Will this replace our PM tool?",
                    answer: "Run everything here or sync key objects with your PM and CRM."
                },
                {
                    id: 4,
                    question: "How do we prevent overload on managers?",
                    answer: "Capacity views and alerts show workload and suggest rebalancing."
                },
                {
                    id: 5,
                    question: "How fast can we pilot?",
                    answer: "A short discovery, then a few weeks to a live roster, boards, and status reporting."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the 5 Minute Growth Audit",
                    modalTitle: "Get the 5 Minute Growth Audit"
                }
            }
        },
        seo: {
            title: "Scalable team collaboration for agencies | Desilo",
            description: "Multi user roles, task assignment, creator rosters, and progress tracking. Reduce chaos, increase capacity, and give clients clear visibility as you scale.",
            keywords: "Team collaboration, scalable teams, task management, creator management, team workflow, collaboration tools, agency management",
            ogTitle: "Scalable team collaboration for agencies | Desilo",
            ogDescription: "Multi user roles, task assignment, creator rosters, and progress tracking. Reduce chaos, increase capacity, and give clients clear visibility as you scale.",
            twitterTitle: "Scalable team collaboration for agencies | Desilo",
            twitterDescription: "Multi user roles, task assignment, creator rosters, and progress tracking. Reduce chaos, increase capacity, and give clients clear visibility as you scale."
        }
    },
    {
        slug: "brand-safety-compliance-monitoring",
        title: "Brand safety and compliance monitoring",
        description: "FTC disclosures, fraud checks, sentiment alerts, and platform policy monitoring.",
        hero: {
            label: "USE CASE",
            title: "Brand safety and compliance monitoring",
            description: "FTC disclosures, fraud checks, sentiment alerts, and platform policy monitoring.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Compliance Checklist",
                    modalTitle: "Get the Compliance Checklist"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Agencies and creator managers who must protect brands while moving fast."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Continuous monitoring with evidence you can show.",
            items: [
                {
                    id: 1,
                    title: "Disclosure verification",
                    description: "Capture post URLs and screenshots, confirm FTC tags and labels, and store proof.",
                },
                {
                    id: 2,
                    title: "Fraud and authenticity checks",
                    description: "Signals for suspicious engagement, bot patterns, and sudden spikes with review notes.",
                },
                {
                    id: 3,
                    title: "Sentiment and topic alerts",
                    description: "Track brand mentions, risky keywords, and tone shifts with configurable thresholds.",
                },
                {
                    id: 4,
                    title: "Platform policy tracking",
                    description: "Watch for policy changes by platform and flag posts that may violate rules.",
                },
                {
                    id: 5,
                    title: "Risk rules and scoring",
                    description: "Client-specific rules that score risk by creator, campaign, and post.",
                },
                {
                    id: 6,
                    title: "Escalation workflows",
                    description: "Notify the right owner, pause posts, and route to legal or client as needed.",
                },
                {
                    id: 7,
                    title: "Incident log",
                    description: "Track issues, responses, and outcomes with timelines and attachments.",
                },
                {
                    id: 8,
                    title: "Audit reports",
                    description: "Exportable evidence packs for clients and compliance teams.",
                },
                {
                    id: 9,
                    title: "Dashboards",
                    description: "Live risk views by campaign with drill-downs to the post level.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "The business outcomes that improve when safety is proactive.",
            items: [
                {
                    id: 1,
                    title: "Less chaos",
                    description: "one source of truth beats screenshots and DMs",
                },
                {
                    id: 2,
                    title: "Higher win rate",
                    description: "fewer compliance misses protect renewals and bigger briefs",
                },
                {
                    id: 3,
                    title: "More capacity",
                    description: "automated checks cut manual review time",
                },
                {
                    id: 4,
                    title: "Trusted results",
                    description: "evidence packs and clear logs build client confidence",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A simple flow from rules to real-time monitoring.",
            steps: [
                {
                    title: "Define policies",
                    description: "Capture disclosure, safety, and platform rules per client and region."
                },
                {
                    title: "Connect sources",
                    description: "Ingest posts and comments from linked platforms and campaign trackers."
                },
                {
                    title: "Apply checks",
                    description: "Run disclosure, fraud, sentiment, and policy checks with explainable signals."
                },
                {
                    title: "Score and alert",
                    description: "Calculate risk, notify owners, and auto-create tasks with SLAs."
                },
                {
                    title: "Resolve and record",
                    description: "Approve, pause, or edit posts and log outcomes with evidence."
                },
                {
                    title: "Report and improve",
                    description: "Share dashboards and weekly summaries and tune rules from incidents."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show compliance is working.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Disclosure misses down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Risky posts caught before publish up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Incident response time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Client escalations down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Renewal rate and margin up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Explainable checks, secure data, and calm operations.",
            items: [
                {
                    title: "Frontend",
                    description: "Manager-first views with filters, evidence previews, and one-click escalations.",
                },
                {
                    title: "Backend",
                    description: "Services for posts, checks, incidents, rules, and alerts with idempotent APIs.",
                },
                {
                    title: "Models and rules",
                    description: "Keyword lists, classifiers, and sentiment with thresholds you control and human review paths.",
                },
                {
                    title: "Integrations",
                    description: "Social APIs, link shorteners, e-sign, storage, CRM, and messaging.",
                },
                {
                    title: "Data",
                    description: "Immutable audit logs, evidence files, and schema versioning for rules and incidents.",
                },
                {
                    title: "Ops",
                    description: "Monitors for ingestion freshness, alert queues, and SLA dashboards.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extras that reduce risk and review time.",
            items: [
                {
                    title: "Auto disclosure helper",
                    description: "Suggests captions with compliant tags before publish.",
                },
                {
                    title: "Regional policy packs",
                    description: "Preloaded rules for major markets with translation notes.",
                },
                {
                    title: "Crisis playbooks",
                    description: "One-click sequences for takedown, edit, or response with roles and steps.",
                },
                {
                    title: "Creator safety brief",
                    description: "Shareable guidelines per platform that reduce back and forth.",
                },
                {
                    title: "Client portal",
                    description: "Read-only risk dashboards and incident packs for stakeholders.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Controls clients and legal teams will trust. We provide operational tooling, not legal advice. Your counsel sets final policy.",
            tags: [
                "Role-based access",
                "SSO and SCIM options",
                "encryption at rest and in transit",
                "signed webhooks",
                "configurable retention",
                "Evidence files are hashed and time-stamped"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Can we enforce client-specific rules?",
                    answer: "Yes. Create rule sets per client, market, and platform with different thresholds."
                },
                {
                    id: 2,
                    question: "How accurate are sentiment and risk signals?",
                    answer: "Signals are explainable and always paired with evidence for human review."
                },
                {
                    id: 3,
                    question: "Do you check disclosures after posting?",
                    answer: "Yes. We verify live posts and store proof for audits and renewals."
                },
                {
                    id: 4,
                    question: "What happens when a rule changes mid-campaign?",
                    answer: "Rules version, backfill checks, and re-score affected posts."
                },
                {
                    id: 5,
                    question: "Can clients see incidents without asking the team?",
                    answer: "Client portals provide scoped access to dashboards and evidence packs."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Compliance Checklist",
                    modalTitle: "Get the Compliance Checklist"
                }
            }
        },
        seo: {
            title: "Brand safety and compliance monitoring for agencies | Desilo",
            description: "Verify FTC disclosures, run fraud checks, monitor sentiment, and track platform policies. Reduce risk, save review time, and deliver evidence clients trust.",
            keywords: "Brand safety, compliance monitoring, FTC disclosures, fraud detection, sentiment analysis, brand protection, compliance automation",
            ogTitle: "Brand safety and compliance monitoring for agencies | Desilo",
            ogDescription: "Verify FTC disclosures, run fraud checks, monitor sentiment, and track platform policies. Reduce risk, save review time, and deliver evidence clients trust.",
            twitterTitle: "Brand safety and compliance monitoring for agencies | Desilo",
            twitterDescription: "Verify FTC disclosures, run fraud checks, monitor sentiment, and track platform policies. Reduce risk, save review time, and deliver evidence clients trust."
        }
    },
    {
        slug: "campaign-workflow-automation",
        title: "Campaign and brand deal workflow automation",
        description: "Briefs, deliverables, approvals, payouts, and reporting in one operational pipeline built to reduce chaos, margin leaks, and coordination overhead.",
        hero: {
            label: "USE CASE",
            title: "Campaign and brand deal workflow automation",
            description: "Briefs, deliverables, approvals, payouts, and reporting in one operational pipeline built to reduce chaos, margin leaks, and coordination overhead.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Ops Automation Playbook",
                    modalTitle: "Get the Ops Automation Playbook"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Influencer marketing agencies, creator representation firms, UGC studios, and campaign-led creator businesses that manage multiple clients, creators, deliverables, approvals, and reporting cycles across brands and platforms. Ideal if work happens across spreadsheets, email threads, and chat tools, leading to missed approvals, duplicated effort, slow reporting, and shrinking margins as campaign volume increases."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "A structured campaign operations pipeline that mirrors how real agency work happens.",
            items: [
                {
                    id: 1,
                    title: "Brief intake workflows",
                    description: "Standardized briefs, goals, deliverables, usage rules, budgets, and timelines aligned to brand expectations.",
                },
                {
                    id: 2,
                    title: "Creator assignment and deliverable tracking",
                    description: "Match creators to briefs, assign milestones, track versions, and monitor progress across campaigns.",
                },
                {
                    id: 3,
                    title: "Approval and review workflows",
                    description: "Side-by-side visual reviews, comments, version control, and clear sign-off checkpoints for brands and internal teams.",
                },
                {
                    id: 4,
                    title: "Content and asset management",
                    description: "Central workspace for drafts, revisions, scripts, captions, files, and final deliverables tied to each campaign job.",
                },
                {
                    id: 5,
                    title: "Compliance, usage, and disclosure checks",
                    description: "Brand guidelines, content restrictions, disclosure rules, and approval history preserved in workflow context.",
                },
                {
                    id: 6,
                    title: "Campaign messaging and coordination",
                    description: "Structured communication threads attached to tasks, not scattered across inboxes and chats.",
                },
                {
                    id: 7,
                    title: "Performance and reporting automation",
                    description: "Views, conversions, screenshots, links, and outcomes mapped to each deliverable with exportable reports.",
                },
                {
                    id: 8,
                    title: "Finance and payout readiness",
                    description: "Creator compensation tracking, payout status, and proof-of-delivery alignment to campaign workflow.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "Agency profitability improves when workflows reduce chaos instead of creating it.",
            items: [
                {
                    id: 1,
                    title: "Less operational leakage",
                    description: "fewer missed steps, repeated work, or re-alignment cycles",
                },
                {
                    id: 2,
                    title: "Stronger client confidence",
                    description: "cleaner approvals and consistent delivery transparency",
                },
                {
                    id: 3,
                    title: "Higher team productivity",
                    description: "less time coordinating, more time producing outcomes",
                },
                {
                    id: 4,
                    title: "Scalable margins",
                    description: "campaigns increase without burning capacity or adding headcount",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A workflow based product engineering approach designed around real agency behavior.",
            steps: [
                {
                    title: "Current workflow mapping",
                    description: "Identify how briefs, deliverables, creators, and brands move through the lifecycle today and where failure points occur."
                },
                {
                    title: "Operational UX prototyping",
                    description: "Design an interface and flow that matches how coordinators, managers, and creators already work instead of forcing new habits."
                },
                {
                    title: "Role and responsibility modeling",
                    description: "Define who approves what, when handoffs happen, and what actions require confirmation or audit history."
                },
                {
                    title: "Automation layering",
                    description: "Turn repetitive coordination tasks into structured rules instead of manual follow-ups."
                },
                {
                    title: "Behavior analytics and margin signals",
                    description: "Track delays, blockers, bottlenecks, and rework costs to surface operational insights."
                },
                {
                    title: "Progressive rollout",
                    description: "Start with one high-volume campaign type and expand automation once stability is proven."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that show campaign workflow automation is improving margins.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Time to approval down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Missed deliverables down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Coordinator hours per campaign reduced"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Client feedback cycles shorter"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Reporting time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Campaign throughput up"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Design driven product engineering with execution-ready workflow architecture.",
            items: [
                {
                    title: "Frontend",
                    description: "Task-centered UI, campaign timelines, approval views, creator deliverable dashboards, and review surfaces.",
                },
                {
                    title: "Backend",
                    description: "Workflow engine, role-based permissions, status transitions, audit logging, and automation rules.",
                },
                {
                    title: "Data",
                    description: "Campaign funnels, SLA metrics, milestone completion patterns, rework indicators, creator performance context.",
                },
                {
                    title: "Integrations",
                    description: "Drive, project storage, email gateways, publishing tools, analytics dashboards, and payout systems where required.",
                },
                {
                    title: "Experiment layer",
                    description: "Variant testing for workflow steps, approval timing, naming, and coordination models.",
                },
                {
                    title: "Ops",
                    description: "Rollback controls, history logs, communication records, environment separation for client sensitivity.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extensions that strengthen delivery reliability and client trust.",
            items: [
                {
                    title: "Brand approval portals",
                    description: "Client-side review interface with controlled access and structured sign-off.",
                },
                {
                    title: "Talent and roster visibility",
                    description: "Creator availability, past campaign history, and rate context inside the workflow.",
                },
                {
                    title: "Usage rights and expiry tracking",
                    description: "Licensing windows, region permissions, and automated reminders.",
                },
                {
                    title: "Campaign insights digest",
                    description: "Behavior insights for leadership on bottlenecks, costs, and operations performance.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Built for agencies handling client assets, creator relationships, and sensitive approvals. Campaign workflow automation is implemented responsibly, with full transparency into who did what, when decisions happened, and how results were produced.",
            tags: [
                "Role based access control",
                "encrypted asset storage",
                "permission boundaries",
                "audit trails",
                "documented operating workflows"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Is this project management software?",
                    answer: "No. This is workflow automation purpose-built for creator and campaign operations."
                },
                {
                    id: 2,
                    question: "Can this replace spreadsheets and email approvals?",
                    answer: "Yes. Approvals, deliverables, and reporting move into one structured pipeline."
                },
                {
                    id: 3,
                    question: "Will our existing tools still work?",
                    answer: "Yes. Integrations support storage, analytics, and publishing stacks where useful."
                },
                {
                    id: 4,
                    question: "Does this work for multiple campaign types?",
                    answer: "We start with one high-impact workflow, then scale across formats and clients."
                },
                {
                    id: 5,
                    question: "Does automation reduce control for managers?",
                    answer: "No. Automation assists coordination while preserving human oversight."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Ops Automation Playbook",
                    modalTitle: "Get the Ops Automation Playbook"
                }
            }
        },
        seo: {
            title: "Campaign workflow automation for creator agencies | Desilo",
            description: "Automate campaign and brand deal workflows for creator agencies. Centralize briefs, deliverables, approvals, payouts, and reporting with workflow based UX and design driven product engineering to reduce chaos and margin loss.",
            keywords: "Campaign automation, creator agency workflow, brand deal management, influencer marketing operations, agency margins",
            ogTitle: "Campaign workflow automation for creator agencies | Desilo",
            ogDescription: "Automate campaign and brand deal workflows for creator agencies. Centralize briefs, deliverables, approvals, payouts, and reporting with workflow based UX and design driven product engineering to reduce chaos and margin loss.",
            twitterTitle: "Campaign workflow automation for creator agencies | Desilo",
            twitterDescription: "Automate campaign and brand deal workflows for creator agencies. Centralize briefs, deliverables, approvals, payouts, and reporting with workflow based UX and design driven product engineering to reduce chaos and margin loss."
        }
    },
    {
        slug: "outbound-sales-pitch-intelligence",
        title: "Outbound sales and pitch intelligence for brand partnerships",
        description: "Proposal generators, case study builders, performance snapshots, and deal insights that help agencies win brand partnerships faster with less manual effort.",
        hero: {
            label: "USE CASE",
            title: "Outbound sales and pitch intelligence for brand partnerships",
            description: "Proposal generators, case study builders, performance snapshots, and deal insights that help agencies win brand partnerships faster with less manual effort.",
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Sales Intelligence Playbook",
                    modalTitle: "Get the Sales Intelligence Playbook"
                }
            }
        },
        whoThisIsFor: {
            title: "Who this is for",
            description: "Influencer marketing agencies, creator representation teams, UGC studios, and talent networks that pitch campaigns, partnerships, and creator opportunities to brands and need faster, more credible, and more data-backed outbound efforts. Ideal if proposals are built manually, case studies are recreated for every outreach, performance proof is scattered across files, and sales workflows depend on individual team memory instead of structured intelligence."
        },
        whatYouCanLaunch: {
            title: "What you can launch",
            description: "Sales enablement tools shaped around real agency outreach workflows.",
            items: [
                {
                    id: 1,
                    title: "Proposal and deck generators",
                    description: "Auto-assemble brand-ready proposals using creator data, past outcomes, formats, rates, and creative angles.",
                },
                {
                    id: 2,
                    title: "Case study and proof-of-results builder",
                    description: "Generate campaign summaries, metrics, screenshots, and narrative framing from campaign history.",
                },
                {
                    id: 3,
                    title: "Performance and credibility snapshots",
                    description: "Quick exports showing audience fit, performance benchmarks, conversion potential, and creator reliability indicators.",
                },
                {
                    id: 4,
                    title: "Opportunity tracking and outreach flows",
                    description: "Structured pipelines for leads, follow-ups, conversations, and deal status across brands and categories.",
                },
                {
                    id: 5,
                    title: "Creator and brand fit intelligence",
                    description: "Signals that highlight alignment between brand goals, creator strengths, content style, and audience behavior.",
                },
                {
                    id: 6,
                    title: "Message and pitch frameworks",
                    description: "Reusable outreach templates based on what typically converts in your market and niche.",
                },
                {
                    id: 7,
                    title: "Pricing and scope guidance",
                    description: "Reference past deals, acceptance ranges, and risk signals to support confident negotiations.",
                },
                {
                    id: 8,
                    title: "Learning loop from wins and losses",
                    description: "Close-rate insights, feedback clustering, and positioning improvement over time.",
                }
            ]
        },
        whyItMatters: {
            title: "Why it matters",
            description: "Sales performance improves when credibility, proof, and context are built into the workflow.",
            items: [
                {
                    id: 1,
                    title: "Faster proposal turnaround",
                    description: "outreach moves at opportunity speed, not document-building speed",
                },
                {
                    id: 2,
                    title: "Higher win rates",
                    description: "pitches backed with data, relevance, and outcomes instead of generic claims",
                },
                {
                    id: 3,
                    title: "More scalable sales operations",
                    description: "less dependence on individual memory and manual assembly",
                },
                {
                    id: 4,
                    title: "Clearer positioning",
                    description: "agencies learn which narratives and creators convert best",
                }
            ]
        },
        howItWorks: {
            title: "How it works",
            description: "A workflow-based sales enablement approach grounded in behavior and product strategy.",
            steps: [
                {
                    title: "Sales and outreach mapping",
                    description: "Document how your team currently identifies leads, builds proposals, follows up, and closes deals."
                },
                {
                    title: "Context-aware UX design",
                    description: "Design tools that fit the natural motion of pitching instead of forcing complex CRM habits."
                },
                {
                    title: "Proof and narrative modeling",
                    description: "Define how results, performance, and brand fit should be communicated in repeatable ways."
                },
                {
                    title: "Automation stitching",
                    description: "Turn repetitive proposal assembly and case study formatting into structured generation workflows."
                },
                {
                    title: "Behavior and signal analytics",
                    description: "Track which pitches convert, which narratives resonate, and where deals stall."
                },
                {
                    title: "Iterative refinement",
                    description: "Improve messaging, positioning, creator selection logic, and deal framing over time."
                }
            ]
        },
        outcomes: {
            title: "Outcomes you can measure",
            description: "Signals that outbound sales intelligence is creating real revenue impact.",
            items: [
                {
                    icon: "fa-solid fa-check",
                    text: "Proposal turnaround time down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Win rate up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Sales cycle length reduced"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Custom work per pitch down"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Positioning clarity up"
                },
                {
                    icon: "fa-solid fa-check",
                    text: "Brand reply rate improving"
                }
            ]
        },
        technicalApproach: {
            title: "Technical approach",
            description: "Design driven product engineering for scalable sales intelligence systems.",
            items: [
                {
                    title: "Frontend",
                    description: "Proposal builders, case study composer, snapshot cards, opportunity dashboards, outreach workflow UI.",
                },
                {
                    title: "Backend",
                    description: "Sales intelligence services, content assembly logic, historical campaign retrieval, role based permissions.",
                },
                {
                    title: "Data",
                    description: "Campaign outcomes, platform metrics, creator cohorts, pricing history, and win-loss analytics.",
                },
                {
                    title: "Integrations",
                    description: "Email, CRM, storage, roster systems, analytics exports, and presentation tools where useful.",
                },
                {
                    title: "Experimentation",
                    description: "Variant testing for pitch narrative styles, proposal layouts, and value framing.",
                },
                {
                    title: "Ops",
                    description: "Audit trails, activity logs, template governance, safe editing and publishing controls.",
                }
            ]
        },
        commonAddOns: {
            title: "Common add-ons",
            description: "Extensions that increase sales leverage and brand trust.",
            items: [
                {
                    title: "Brand-specific pitch packs",
                    description: "Auto-generated proposal variants tailored to brand category, goals, and tone.",
                },
                {
                    title: "Creator-first media kits",
                    description: "Living profiles with metrics, history, strengths, and example work tied to roster data.",
                },
                {
                    title: "Strategic opportunity insights",
                    description: "Signals on ideal timing, campaign readiness, and repeat collaboration potential.",
                },
                {
                    title: "Leadership reporting",
                    description: "Sales funnel insights, messaging patterns, and performance summaries for decision makers.",
                }
            ]
        },
        securityAndGovernance: {
            title: "Security and governance",
            description: "Built for agencies handling sensitive brand, pricing, and creator performance data. Sales intelligence becomes structured, accountable, and trustworthy instead of fragmented across personal files.",
            tags: [
                "Role based access",
                "internal and external visibility controls",
                "encrypted storage",
                "permission boundaries",
                "documented sales workflows"
            ]
        },
        faqs: {
            title: "FAQs",
            items: [
                {
                    id: 1,
                    question: "Is this a CRM replacement?",
                    answer: "No. It works as a sales intelligence and proposal automation layer that can integrate with your CRM."
                },
                {
                    id: 2,
                    question: "Can this reduce manual proposal work?",
                    answer: "Yes. Most assembly, formatting, and proof packaging becomes automated."
                },
                {
                    id: 3,
                    question: "Does this work across multiple creator niches?",
                    answer: "Yes. Data structures and messaging frameworks adapt to different verticals."
                },
                {
                    id: 4,
                    question: "Can this scale as our pipeline grows?",
                    answer: "Yes. The system is designed for multi-brand, multi-campaign outbound environments."
                },
                {
                    id: 5,
                    question: "Do we lose flexibility in how we pitch?",
                    answer: "No. Frameworks support creativity while removing repetitive work."
                }
            ]
        },
        preFooter: {
            cta: {
                primary: {
                    text: "Book a Strategy Call",
                    href: "https://calendly.com/parthik/30min"
                },
                secondary: {
                    text: "Get the Sales Intelligence Playbook",
                    modalTitle: "Get the Sales Intelligence Playbook"
                }
            }
        },
        seo: {
            title: "Outbound sales intelligence for creator and influencer agencies | Desilo",
            description: "Build a sales intelligence and pitch automation platform for creator agencies. Generate proposals, case studies, and performance snapshots with workflow based UX and design driven product engineering to win brand partnerships faster.",
            keywords: "Outbound sales, creator agency sales, pitch automation, influencer marketing proposals, sales intelligence, brand partnerships",
            ogTitle: "Outbound sales intelligence for creator and influencer agencies | Desilo",
            ogDescription: "Build a sales intelligence and pitch automation platform for creator agencies. Generate proposals, case studies, and performance snapshots with workflow based UX and design driven product engineering to win brand partnerships faster.",
            twitterTitle: "Outbound sales intelligence for creator and influencer agencies | Desilo",
            twitterDescription: "Build a sales intelligence and pitch automation platform for creator agencies. Generate proposals, case studies, and performance snapshots with workflow based UX and design driven product engineering to win brand partnerships faster."
        }
    }
];

// ============================================================================
// COMBINED EXPORTS - All use cases for backward compatibility
// ============================================================================
export const useCases: UseCaseData[] = [
    ...startupUseCases,
    ...creatorUseCases,
    ...establishedGrowingUseCases,
    ...agencyManagerUseCases
];

// ============================================================================
// HELPER FUNCTIONS - Get use cases by ICP
// ============================================================================
export const getStartupUseCases = (): UseCaseData[] => startupUseCases;
export const getCreatorUseCases = (): UseCaseData[] => creatorUseCases;
export const getEstablishedGrowingUseCases = (): UseCaseData[] => establishedGrowingUseCases;
export const getAgencyManagerUseCases = (): UseCaseData[] => agencyManagerUseCases;

export const getUseCaseBySlug = (slug: string): UseCaseData | undefined => {
    return useCases.find(useCase => useCase.slug === slug);
};

export const getAllUseCaseSlugs = (): string[] => {
    return useCases.map(useCase => useCase.slug);
};
